import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dayBeforeIso, normalizeLiveSince } from '../../utils/journeyView';
import JourneyTabs from '../JourneyTabs';
import { themeColor } from '../../utils/themeColor';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders, getVectorApiHeaders } from '../../utils/authFetchHeaders';
import { useAuth } from '../../contexts/AuthContext';
import { chartTheme, useColorMode } from '../../hooks/useColorMode';
import { subscribeAppAutoRefresh } from '../../hooks/useAppAutoRefresh';
import { sortStrategies } from '../../utils/strategySort';
import { FYNTRAK_OG_EQUITY_1X_CURVE } from '../../data/fyntrakOgEquityCurve';
import {
  buildEquityPortfolioCalendarStats,
  buildEquityPortfolioSeries,
  applyLinearLeverageToSeries,
  applyCompoundLeverageToSeries,
  buildDailyStatsFromSeries,
  buildDailyStatsFromTrades,
  buildEquityPointsFromTrades,
  buildEquityCurveWindowSeries,
  buildOgEquityPortfolioBlendedSeries,
  rebaseSeriesToPeriodStart,
  collectEquityPortfolioLegTrades,
  buildPortfolioDailySeries,
  combineScenarioSleeveRows,
  computeMonthlyReturnBreakdown,
  computePeriodReturns,
  computeYearlyReturnBreakdown,
  computeYearlyReturnsFromSeries,
  formatDateLabel,
  getDateRange,
  parseDateFromRowLabel,
  summarizePerformance,
  filterSeriesByDateRange,
} from '../../utils/vectorCapitalScenario';
import { buildMultiBucketDailySeries } from '../../utils/vectorCapitalPlusScenario';
import {
  applyControlsPillOrder,
  dynamicEquityScenarioId,
  dynamicFuturesScenarioId,
  equityAlgoNameFromKey,
  filterEquityAlgoItems,
  filterDynamicFuturesForRegistryPresets,
  filterRegistryScenariosForViewer,
  filterNamesByInternalRegistryClaims,
  internalRegistryClaimKeys,
  buildInternalScenarioIdSet,
  filterFuturesBasketItems,
  filterStockScenarioIds,
  filterTestScenarioIds,
  findEquityStrategyForRegistry,
  findRegistryRowForFuturesDataName,
  isIndividualEquityAlgo,
  isVectorCapitalStockAlgo,
  parseDynamicScenarioId,
  registryScenarioUsesCompoundEquity,
  resolveFuturesDataNameDisplayLabel,
  resolveScenarioDisplayLabel,
  resolveScenarioDisplayColor,
} from '../../utils/vectorBasketDisplay';
import {
  normalizeScenarioId,
  parseRegistryScenarioId,
  legacyPresetIdFromRegistrySlug,
  registryScenarioColor,
  registryScenarioId,
  registryScenarioLabel,
  LEGACY_SCENARIO_SLUGS,
} from '../../utils/vectorScenarioRegistry';
import { resolveCalendarScenarioSymbol, resolveInstrumentStrategyName, applyInstrumentSymbolsToTrades } from '../../utils/strategyInstrument';
import {
  buildPresetPrograms,
  ALL_EQUITY_PORTFOLIO_PRESET_ORDER,
  EQUITY_PORTFOLIO_PRESETS,
  getExecutionRatio,
  getStrategyScenarioDefaults,
  getVectorPresetRecipe,
  isEquityPortfolioPreset,
  resolveEquityPortfolioLegs,
  getEquityPortfolioEffectiveLeverage,
  resolveEquityPortfolioMethod,
  resolveEquityPortfolioLeverageMode,
  scenarioUsesCompoundMonthly,
  PORTFOLIO_KEY_BY_PRESET,
  VC_DAY_LABELS,
  VC_DAY_SEQUENCE,
  VECTOR_PRESET_META,
  VECTOR_PRESET_ORDER,
  CUSTOM_BLEND_START_BASES,
  DEFAULT_CUSTOM_BLEND_START_BASIS,
} from '../../utils/vectorCapitalProgramConfig';
import MetricInfo from '../MetricInfo';
import { computeSeriesRiskStats } from '../../utils/seriesRiskStats';
import {
  monthlyFixedBaseCapital,
  normalizeMonthlyReturnMethods,
} from '../../utils/vectorMonthlyReturnMethod';
import {
  BENCHMARK_IDS,
  getBenchmarkLineColor,
  clipDailyStatsToHistoryStart,
  clipSeriesToHistoryStart,
  clipTradesToHistoryStart,
  isYearOnOrAfterHistoryStart,
  normalizeBenchmarkSettings,
  resolveMarketOptions,
  resolveOverviewStartIso,
} from '../../utils/vectorBenchmarkSettings';
import {
  IconAlert,
  IconCheck,
  IconChevronDown,
  IconFilter,
  IconInfo,
  IconPencil,
  IconSearch,
  IconX,
} from '../Icons';
import VectorCapitalCalendar, {
  VectorCapitalMonthlyReturns,
  VectorCapitalTradesList,
} from '../VectorCapitalCalendar';
import {
  VectorCapitalGrowthCalculator,
  VectorCapitalKeyInsights,
} from '../VectorCapitalInsights';
import {
  buildMonthlyReturnsInfoParagraphs,
  MONTHLY_RETURNS_INFO_TITLE,
} from '../MonthlyReturnsInfoModal';
import { PnlScalingInfoModal } from '../PnlScalingInfoModal';
import VectorEquityPortfolioSettings from '../VectorEquityPortfolioSettings';
import VectorCapitalChartLoader from './VectorCapitalChartLoader';
import usePersistentTab from '../../hooks/usePersistentTab';
import { isVectorEntity, getEntityDisplayName, getResultsNavLabel } from '../../utils/activeEntity';
import { useBrandIdentity } from '../../utils/brandIdentity';
import '../VectorCapitalPlusSimulator.css';

const NON_STRATEGY_SHEETS = new Set([
  'current results',
  'current results (hidden)',
  'past results',
  'past results (hidden)',
  'sheet2',
  'results',
  'results updated',
  'summary',
  'sample strategy (demo)',
]);

const DATE_SHEET_PATTERN = /^\d{2}\.\d{4}$/;

const DATE_RANGE_OPTIONS = [
  { value: 'last-30-days', label: '1M' },
  { value: 'last-3-months', label: '3M' },
  { value: 'last-6-months', label: '6M' },
  { value: 'ytd', label: 'YTD' },
  { value: 'last-12-months', label: '1Y' },
  { value: 'all-time', label: 'All' },
  { value: 'custom', label: 'Custom' },
];

const DATE_RANGE_VALUES = new Set(DATE_RANGE_OPTIONS.map((o) => o.value));

function vectorCapitalStoragePrefix(showEquityPortfolios) {
  return showEquityPortfolios ? 'vectorCapital' : 'vectorCapitalPlus';
}

function readStoredScenarioIds(prefix, defaultScenarioId) {
  try {
    const raw = localStorage.getItem(`${prefix}.selectedScenarioIds`);
    if (!raw) return new Set([defaultScenarioId]);
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length < 1) return new Set([defaultScenarioId]);
    const ids = parsed.filter((id) => typeof id === 'string' && id.trim());
    return ids.length ? new Set(ids) : new Set([defaultScenarioId]);
  } catch (_err) {
    return new Set([defaultScenarioId]);
  }
}

/** Leftmost strategy pill — default when nothing is selected. */
function primaryPillScenarioId(scenarioPillOrder, fallback = 'custom') {
  const first = Array.isArray(scenarioPillOrder) ? scenarioPillOrder[0] : null;
  return typeof first === 'string' && first.trim() ? first : fallback;
}

/** Drop stale ids; if nothing remains selected, fall back to the first pill. */
function normalizeScenarioSelection(prev, scenarioPillOrder, fallback = 'custom') {
  const allowed = new Set(scenarioPillOrder || []);
  const next = new Set([...prev].filter((id) => allowed.has(id)));
  if (next.size === 0) {
    next.add(primaryPillScenarioId(scenarioPillOrder, fallback));
  }
  return next;
}

function readStoredDateRange(prefix) {
  try {
    const stored = localStorage.getItem(`${prefix}.dateRangeFilter`);
    if (stored && DATE_RANGE_VALUES.has(stored)) return stored;
  } catch (_err) { /* ignore */ }
  return 'all-time';
}

function readStoredCustomBlendStartBasis(prefix) {
  try {
    const stored = localStorage.getItem(`${prefix}.customBlendStartBasis`);
    if (stored && CUSTOM_BLEND_START_BASES[stored]) return stored;
  } catch (_err) { /* ignore */ }
  return DEFAULT_CUSTOM_BLEND_START_BASIS;
}

function readStoredDateField(prefix, field) {
  try {
    return localStorage.getItem(`${prefix}.${field}`) || '';
  } catch (_err) {
    return '';
  }
}

// Validate a stored/admin color, falling back to the yellow default.
function normalizeCustomLineColor(raw) {
  const v = String(raw || '').trim().toLowerCase();
  return /^#[0-9a-f]{6}$/.test(v) ? v : DEFAULT_CUSTOM_LINE_COLOR;
}

const CAPITAL_BASE_OPTIONS = [100000, 250000, 500000, 1000000];
/** Benchmark daily % — not live ticks; matches Client Reporting (5 min). */
const QUOTE_POLL_MS = Number(process.env.REACT_APP_QUOTE_POLL_MS) || 300_000;
/** Live NT8 overlay curves refresh on the worker every ~10 min. */
const LIVE_EQUITY_POLL_MS = Number(process.env.REACT_APP_LIVE_EQUITY_POLL_MS) || 600_000;
function readStoredCapitalBasePreset(prefix) {
  try {
    const stored = Number(localStorage.getItem(`${prefix}.capitalBasePreset`));
    if (CAPITAL_BASE_OPTIONS.includes(stored)) return stored;
  } catch (_err) { /* ignore */ }
  return 100000;
}

function readStoredBenchmarkIds(prefix) {
  try {
    const raw = localStorage.getItem(`${prefix}.selectedBenchmarkIds`);
    if (!raw) return new Set(['SPY']);
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set(['SPY']);
    const ids = parsed.filter((id) => typeof id === 'string' && BENCHMARK_IDS.has(id));
    return ids.length ? new Set(ids) : new Set(['SPY']);
  } catch (_err) {
    return new Set(['SPY']);
  }
}

// Default color for the "Custom" line. Yellow by default so it reads clearly
// apart from Master Multi Account (purple). The live value is admin/vector-admin
// managed (basketDisplayEnabled.customColor) and applied for everyone.
const DEFAULT_CUSTOM_LINE_COLOR = '#eab308';

// Distinct colors for each selectable strategy line/column.
const SCENARIO_LINE_COLORS = {
  master_multi_account: '#a78bfa',
  mma_test: '#f472b6',
  btc_test: '#f7931a',
  jean_test: '#a78bfa',
  vector_capital: '#34d399',
  vector_capital_journal: '#22d3ee',
  vc_core_standard: '#34d399',
  vc_core_aggressive: '#f59e0b',
  custom: DEFAULT_CUSTOM_LINE_COLOR,
  conservative: '#22c55e',
  balanced: '#eab308',
  aggressive: '#ef4444',
  equity_portfolio_1x: '#06b6d4',
  equity_portfolio_225x: '#ec4899',
  og_equity_portfolio_1x: '#14b8a6',
  og_equity_portfolio_225x: '#e879f9',
};

function resolveScenarioColor(scenarioId, colors) {
  // RiskLo palette colors follow the brand theme; custom colors a brand picked stay as they are.
  return themeColor(resolveScenarioDisplayColor(
    scenarioId,
    colors,
    (id) => SCENARIO_LINE_COLORS[id] || '#8b5cf6',
  ));
}

const MASTER_ACCOUNT_STRATEGY_NAME = 'Master Multi Account';

/** Scenarios whose growth curve comes from equity strategies, not sheet analytics. */
const EQUITY_CURVE_SCENARIO_IDS = new Set([
  'master_multi_account',
  'vector_capital_journal',
  'mma_test',
  'btc_test',
  'jean_test',
]);

/** Webhook test scenarios polled for near-real-time NT8 alerts. */
const LIVE_EQUITY_POLL_SCENARIO_IDS = new Set(['mma_test', 'btc_test', 'jean_test']);

function scenarioDataHasLiveOverlay(config) {
  const cfg = config && typeof config === 'object' ? config : {};
  if (cfg.mirrorLiveFrom && cfg.mirrorLiveFrom.entitySlug && cfg.mirrorLiveFrom.strategyName) {
    return true;
  }
  if (Array.isArray(cfg.nt8OverlaySleeves) && cfg.nt8OverlaySleeves.length) return true;
  if (String(cfg.nt8StrategyName || '').trim()) return true;
  const tvId = Number(cfg.tvOrderStrategyId);
  return Number.isFinite(tvId) && tvId > 0;
}

function scenarioNeedsEquityOverlayFetch(row) {
  if (!row) return false;
  const cfg = row.dataSourceConfig || {};
  if (scenarioDataHasLiveOverlay(cfg)) return true;
  const parser = String(cfg.parser || '').trim();
  return String(row.dataSourceType || '').toLowerCase() === 'webhook' && parser === 'kairos_daily';
}

function buildEquityOverlaySlugs(orderedIds, registryScenarioBySlug) {
  const slugs = new Set();
  orderedIds.forEach((id) => {
    const normalized = normalizeScenarioId(id);
    if (LIVE_EQUITY_POLL_SCENARIO_IDS.has(normalized)) {
      slugs.add(normalized);
      return;
    }
    const registrySlug = parseRegistryScenarioId(normalized);
    if (!registrySlug) return;
    const row = registryScenarioBySlug.get(registrySlug);
    if (row && scenarioNeedsEquityOverlayFetch(row)) {
      slugs.add(registrySlug);
    }
  });
  return [...slugs].sort().join(',');
}

/** History-windowed overview series rebased to capital base at the floor date. */
function calendarWindowedSeries(scenarioSeriesById, id, capitalBase) {
  const raw = scenarioSeriesById?.[id] || [];
  if (raw.length < 2) return raw;
  return rebaseSeriesToPeriodStart(raw, capitalBase);
}

function needsLiveEquityStrategiesPoll(orderedIds, registryScenarioBySlug) {
  return orderedIds.some((id) => {
    const normalized = normalizeScenarioId(id);
    if (LIVE_EQUITY_POLL_SCENARIO_IDS.has(normalized)) return true;
    const registrySlug = parseRegistryScenarioId(normalized);
    if (!registrySlug) return false;
    const row = registryScenarioBySlug.get(registrySlug);
    return !!(row && scenarioDataHasLiveOverlay(row.dataSourceConfig));
  });
}

const API_FETCH_TIMEOUT_MS = 45000;

async function fetchJsonWithTimeout(url, options = {}, timeoutMs = API_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('Request timed out. The development server may be under load — try refreshing.');
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

function scenarioNeedsSheetAnalytics(id, registryScenarioBySlug) {
  const normalized = normalizeScenarioId(id);
  if (normalized === 'custom') return false;
  if (EQUITY_CURVE_SCENARIO_IDS.has(normalized)) return false;
  if (normalized.startsWith('equity_portfolio') || normalized.startsWith('og_equity')) return false;
  const dynamic = parseDynamicScenarioId(normalized);
  if (dynamic?.kind === 'equity_algo') return false;
  const slug = parseRegistryScenarioId(normalized);
  if (slug) {
    const row = registryScenarioBySlug.get(slug);
    const ds = row?.dataSourceConfig || {};
    if (ds.strategyName && !ds.futuresSleeves?.length && !ds.sheetName) return false;
  }
  return true;
}

// Vector Capital preset blends use futures sleeves from the Google-sheet sync.
const FUTURES_SCENARIO_SLEEVES = {
  master_multi_account: [
    { strategyName: 'Grass Fed Prime Beef Pro', activeDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] },
  ],
  vector_capital: [
    { strategyName: 'Grass Fed Prime Beef (Current version 2.1C)', activeDays: ['monday', 'wednesday', 'friday'] },
    { strategyName: 'Tuesday Special (Previous 2.2A)', activeDays: ['tuesday'] },
    { strategyName: 'Thursday Special', activeDays: ['thursday'] },
  ],
};

const CHART_GROWTH_CURVE_INFO = (
  <>
    <p>
      The strategy line starts from your selected capital base and combines each strategy sleeve using its active
      weekdays, native instrument family, and selected execution product (for example NQ vs MNQ). Each sheet is
      treated as one full-size contract of its native market, then scaled by each sleeve&apos;s size multiplier.
    </p>
    <p>
      Each benchmark line is the benchmark&apos;s price history scaled so the first day in range equals your capital
      base. Its dollar gains and losses therefore scale up or down when you change capital.
    </p>
    <p>
      The strategy account compounds over time, but its path still depends on which sleeves are active on which days
      and how each strategy is sized relative to its full-contract source sheet.
    </p>
  </>
);

const METRIC_TOOLTIPS = {
  totalReturn: (
    <>
      Return from the first day in the selected range to the last day in the selected range.
      <br />
      <br />
      Math: `((ending value / starting value) - 1) x 100`
    </>
  ),
  monthlyReturn: (
    <>
      Trailing return over roughly the last 30 calendar days ending on the final date shown. This is not the same as
      a calendar-month return.
      <br />
      <br />
      Math: `((value at end / value about 30 days earlier) - 1) x 100`
    </>
  ),
  yearlyReturn: (
    <>
      Trailing return over roughly the last 365 calendar days ending on the final date shown. This is not the same as
      annualized return.
      <br />
      <br />
      Math: `((value at end / value about 365 days earlier) - 1) x 100`
    </>
  ),
  annualizedReturn: (
    <>
      The annualized pace of return over the full selected history, also called CAGR. It is not the actual return so
      far, and it can look very large if the selected range is short and performance was strong.
      <br />
      <br />
      Math: `((ending value / starting value)^(1 / years) - 1) x 100`
    </>
  ),
  maxDrawdown: (
    <>
      The worst peak-to-trough drop during the selected range. In percent mode, it is measured from the highest value
      reached before the drop. In dollars mode, it is the biggest dollar drop from a prior peak.
      <br />
      <br />
      Math (percent): `((current value - prior peak) / prior peak) x 100`
    </>
  ),
  winRate: (
    <>
      Percent of non-zero days that finished positive. For benchmarks like SPY, this is based on positive daily
      changes in the benchmark line, not "winning trades" from buy-and-hold.
      <br />
      <br />
      Math: `(positive days / non-zero days) x 100`
    </>
  ),
  profitFactor: (
    <>
      Gross profit divided by gross loss over the selected range. For benchmarks like SPY, this uses summed positive
      daily moves versus summed negative daily moves, not trade outcomes.
      <br />
      <br />
      Math: `gross profit / gross loss`
    </>
  ),
  stdDev: (
    <>
      Annualized standard deviation of daily returns in the selected range — a volatility measure. Uses the same daily
      return series as Sharpe ratio (population std over all consecutive days, then × √252).
      <br />
      <br />
      Math: `stdev(daily returns) × √252 × 100`
    </>
  ),
};

/** Remove "(FRED)" / source-style parentheticals from API labels for cleaner UI. */
function stripDisplayLabel(text) {
  if (text == null || text === '') return '';
  let s = String(text);
  s = s.replace(/\s*\([^)]*\bFRED\b[^)]*\)/gi, '').trim();
  s = s.replace(/\s*\([^)]*\bfred\b[^)]*\)/gi, '').trim();
  return s.replace(/\s{2,}/g, ' ');
}

function benchmarkTicker(id, marketOptions = []) {
  const list = Array.isArray(marketOptions) ? marketOptions : [];
  return list.find((m) => m.id === id)?.shortLabel || id;
}

/** "SPY" → "S&P 500 (SPY)" for table column headers. */
function benchmarkFullName(id, marketOptions = []) {
  const list = Array.isArray(marketOptions) ? marketOptions : [];
  const opt = list.find((m) => m.id === id);
  return opt ? `${opt.label} (${opt.shortLabel})` : String(id);
}

function formatUnitMultiplier(value) {
  const numeric = safeNumber(value);
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(2).replace(/\.?0+$/, '');
}

/** Per-sleeve program size multiplier (string from input, same rules as former global multiplier). */
function parseProgramSizeMultiplier(raw) {
  const n = parseFloat(String(raw ?? '').trim());
  if (String(raw ?? '').trim() === '' || !Number.isFinite(n)) return 1;
  return Math.max(0.1, Math.min(100, n));
}

/** Strip heavy fields from benchmark API payloads to avoid huge React/Recharts trees (OOM in dev). */
function slimBenchmarkApiResponse(data) {
  if (!data || !data.success) return data;
  const series = Array.isArray(data.series)
    ? data.series.map((p) => ({
      date: p.date,
      normalized: p.normalized,
    }))
    : [];
  const benchmark = data.benchmark && typeof data.benchmark === 'object'
    ? {
      ...data.benchmark,
      label:
        stripDisplayLabel(data.benchmark.label)
        || benchmarkTicker(data.benchmark.id)
        || data.benchmark.label,
    }
    : data.benchmark;
  return { ...data, series, benchmark };
}

const MAX_MERGED_CHART_ROWS = 4000;
const MAX_CHART_RENDER_POINTS = 550;

function safeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** A local Date (from getDateRange) → 'YYYY-MM-DD' for string-keyed comparison
 *  against equity-curve point dates. Returns null for null/invalid input. */
function dateToIsoKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Notional base used only to turn a regular strategy's P&L curve into a daily
 *  return profile for bucketing. Returns are scale-invariant, so the exact
 *  value doesn't affect results. */
const STRATEGY_AREA_NOTIONAL = 100000;

// Buckets compound independently from their initial weighted slice (drift /
// buy-and-hold). Periodic rebalancing is supported by the engine but not
// exposed in the UI.
const REBALANCE_MODE = 'none';

/** Clamp every blend bucket to the LATEST first-data date across buckets, so
 *  the Custom strategy starts when all selected strategies are live. Without
 *  this, a young strategy's compartment sits flat as cash for the years before
 *  its data begins, dragging down the blend's total/annualized returns. */
function clampBlendToCommonStart(blend) {
  const starts = blend
    .map((b) => (Array.isArray(b.returns) ? b.returns : [])
      .reduce((min, p) => (p?.date && (!min || p.date < min) ? p.date : min), null))
    .filter(Boolean);
  if (starts.length < 2) return blend;
  const commonStart = starts.reduce((max, d) => (d > max ? d : max));
  return blend.map((b) => ({
    ...b,
    returns: (Array.isArray(b.returns) ? b.returns : []).filter((p) => p?.date >= commonStart),
  }));
}

/** Parse a weight input (string or number) → a clamped non-negative number, 0 if blank/invalid. */
function parseWeightPct(raw) {
  if (raw == null || String(raw).trim() === '') return 0;
  const n = parseFloat(String(raw).trim());
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Daily fractional returns on the curve's FIXED starting base
 *  (ret = (v[i] − v[i−1]) ÷ v[0]) — for ADDITIVE fixed-notional curves (trade
 *  journals, additive portfolio blends, futures sheet strategies). Day-over-day
 *  ratios of a grown fixed-notional curve understate the edge by the curve's
 *  growth factor, so windowed Custom blends read far below the strategy columns
 *  (which are Σ P&L ÷ base). Compounding curves (Kairos-style) keep the
 *  server-derived ratio returns instead. */
function lastTradeIso(trades) {
  return (Array.isArray(trades) ? trades : []).reduce((max, t) => {
    const d = String(t?.trade_date || '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(d) && d > max ? d : max;
  }, '');
}

/** Resolve trade-journal rows for a registry pill or futures strategy name. */
function resolveJournalTradesForScenario({ registryRow, strategyName, equityStrategies }) {
  const list = Array.isArray(equityStrategies) ? equityStrategies : [];
  const nameKey = (n) => String(n || '').trim().toLowerCase();

  if (registryRow) {
    const strat = findEquityStrategyForRegistry(registryRow, list);
    if (Array.isArray(strat?.trades) && strat.trades.length) return strat.trades;
    const slug = String(registryRow.slug || '').trim();
    const candidates = new Set([
      registryRow.dataSourceConfig?.strategyName,
      registryRow.displayName,
    ].map(nameKey).filter(Boolean));
    const bySlug = list.find((s) => s?.scenarioSlug === slug);
    if (Array.isArray(bySlug?.trades) && bySlug.trades.length) return bySlug.trades;
    const byName = list.find((s) => candidates.has(nameKey(s?.name)));
    if (Array.isArray(byName?.trades) && byName.trades.length) return byName.trades;
  }

  const want = nameKey(strategyName);
  if (!want) return null;
  const match = list.find((s) => nameKey(s?.name) === want);
  return Array.isArray(match?.trades) && match.trades.length ? match.trades : null;
}

/**
 * Prefer live NT8 / mirror overlay trades over stale journal rows when both
 * exist — Strategy Data and Results already merge webhooks; Overview must not
 * stay pinned to an older journal tail.
 */
function resolveBestTradesForScenario({ registryRow, strategyName, equityStrategies }) {
  const list = Array.isArray(equityStrategies) ? equityStrategies : [];
  const journalTrades = resolveJournalTradesForScenario({ registryRow, strategyName, equityStrategies });
  const overlayStrat = registryRow ? findEquityStrategyForRegistry(registryRow, list) : null;
  const overlayTrades = overlayStrat?.isNt8LiveOverlay && Array.isArray(overlayStrat.trades) && overlayStrat.trades.length
    ? overlayStrat.trades
    : null;

  if (overlayTrades && journalTrades) {
    return lastTradeIso(overlayTrades) >= lastTradeIso(journalTrades) ? overlayTrades : journalTrades;
  }
  return overlayTrades || journalTrades || null;
}

function journalCoversGridHistory(journalTrades, rawStrategyData, strategyName) {
  const gridForCoverage = rawStrategyData?.[strategyName];
  const gridFirstLabel = Array.isArray(gridForCoverage)
    ? gridForCoverage.slice(2).map((r) => r?.[0])
      .find((v) => /(\d{1,2})\.(\d{1,2})\.(\d{4})/.test(String(v || '')))
    : null;
  const gridFirstDate = gridFirstLabel ? parseDateFromRowLabel(gridFirstLabel) : null;
  const journalFirstIso = (journalTrades || []).reduce((min, t) => {
    const d = String(t?.trade_date || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return min;
    return !min || d < min ? d : min;
  }, null);
  return Boolean(journalTrades && journalFirstIso) && (
    !gridFirstDate
    || (() => {
      const weekEnd = new Date(gridFirstDate);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return new Date(`${journalFirstIso}T12:00:00Z`) <= weekEnd;
    })()
  );
}

/** Build fixed-notional daily returns for a registry futures row (Custom blend path). */
function buildRegistryFuturesBlendEquityReturns({
  registryRow, equityStrategies, rawStrategyData, capitalBase,
}) {
  if (!registryRow) return null;
  const config = registryRow.dataSourceConfig || {};
  const strategyName = String(config.strategyName || registryRow.displayName || '').trim();
  const type = String(registryRow.dataSourceType || '').toLowerCase();

  const toBlendReturns = (series) => {
    const derived = deriveFixedNotionalReturns(series);
    return Array.isArray(derived) && derived.length ? derived : null;
  };

  if (type === 'futures_snapshot') {
    const journalTrades = resolveJournalTradesForScenario({
      registryRow,
      strategyName,
      equityStrategies,
    });
    if (journalCoversGridHistory(journalTrades, rawStrategyData, strategyName)) {
      const series = buildTradeJournalScenarioSeries({ trades: journalTrades }, {}, capitalBase);
      return toBlendReturns(series);
    }
    return null;
  }

  if (type === 'trade_journal' || type === 'equity_curve') {
    const strat = findEquityStrategyForRegistry(registryRow, equityStrategies);
    if (!strat) return null;
    if (type === 'trade_journal') {
      const series = buildTradeJournalScenarioSeries(strat, {}, capitalBase);
      return toBlendReturns(series);
    }
    if (Array.isArray(strat?.points) && strat.points.length >= 2) {
      if (registryScenarioUsesCompoundEquity(registryRow)) {
        return Array.isArray(strat.returns) && strat.returns.length ? strat.returns : null;
      }
      return toBlendReturns(
        buildEquityCurveWindowSeries(strat.points, {}, capitalBase, false),
      );
    }
    if (Array.isArray(strat?.returns) && strat.returns.length) return strat.returns;
    return null;
  }

  if (type === 'webhook') {
    const strat = findEquityStrategyForRegistry(registryRow, equityStrategies);
    if (!strat) return null;
    const series = buildTradeJournalScenarioSeries(strat, {}, capitalBase);
    return toBlendReturns(series);
  }

  return null;
}

/** Build overview series for a trade-journal equity curve (fixed $100k notional). */
function buildTradeJournalScenarioSeries(strat, range, capitalBase) {
  // Prefer live trade rows over stored equity points — production can serve stale
  // compounded points from the equity-strategies cache while the calendar reads
  // fresh vector_trade_journal rows (the mismatch the user saw on MW2).
  if (Array.isArray(strat?.trades) && strat.trades.length) {
    const tradePoints = buildEquityPointsFromTrades(strat.trades, capitalBase);
    if (tradePoints.length >= 2) {
      return buildEquityCurveWindowSeries(tradePoints, range, capitalBase, false);
    }
  }
  if (Array.isArray(strat?.points) && strat.points.length >= 2) {
    return buildEquityCurveWindowSeries(
      strat.points,
      range,
      capitalBase,
      false,
    );
  }
  if (Array.isArray(strat?.returns) && strat.returns.length) {
    const fixedReturns = Array.isArray(strat?.points) && strat.points.length >= 2
      ? deriveFixedNotionalReturns(
        buildEquityCurveWindowSeries(strat.points, {}, capitalBase, false),
      )
      : strat.returns;
    const inRange = (returns) => {
      const arr = Array.isArray(returns) ? returns : [];
      const { startDate, endDate } = range || {};
      const startIso = startDate || null;
      const endIso = endDate || null;
      if (!startIso && !endIso) return arr;
      return arr.filter((p) => {
        if (startIso && p.date < startIso) return false;
        if (endIso && p.date > endIso) return false;
        return true;
      });
    };
    return buildMultiBucketDailySeries(
      [{ returns: inRange(fixedReturns), weightPct: 100, exposure: 1, compound: false }],
      capitalBase,
      { rebalance: 'none' },
    );
  }
  return [];
}

function findEquityStrategyByName(equityStrategies, name) {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return null;
  return equityStrategies.find((s) => String(s?.name || '').trim().toLowerCase() === key) || null;
}

function deriveFixedNotionalReturns(series, valueOf = (p) => p?.normalized) {
  if (!Array.isArray(series) || series.length < 2) return [];
  const out = [];
  let base = null;
  let prev = null;
  for (let i = 0; i < series.length; i += 1) {
    const v = Number(valueOf(series[i]));
    if (!Number.isFinite(v)) continue;
    if (base == null && v > 0) base = v;
    if (prev != null && base) out.push({ date: series[i].date, ret: (v - prev) / base });
    prev = v;
  }
  return out;
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(safeNumber(value));
}

function formatPercent(value) {
  const parsed = safeNumber(value);
  const sign = parsed > 0 ? '+' : '';
  return `${sign}${parsed.toFixed(2)}%`;
}

function formatFactor(value) {
  if (value === Infinity || !Number.isFinite(value)) return 'No losses';
  return safeNumber(value).toFixed(2);
}

function buildTooltipCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(safeNumber(value));
}


// Metric explanations for the Full Comparison table (mirrors Fyntrak's
// Performance & Risk Statistics info icons).
const COMPARISON_METRIC_INFO = {
  'Total Return': 'The total percentage gain or loss over the selected period. It reflects the cumulative performance from the start to the end date.',
  'Annualized Return': 'The geometric average return per year. It standardizes the return over different time periods for easier comparison.',
  'Max Drawdown': 'The largest single drop from a peak to a trough in portfolio value. This is a key indicator of downside risk.',
  'Sharpe Ratio': 'Measures risk-adjusted return. A higher value indicates better performance for the amount of risk taken. (Annualized)',
  'Standard Deviation': 'Annualized standard deviation of daily returns (volatility). Lower values indicate less day-to-day fluctuation in the selected range.',
  'Profit Factor': 'The ratio of gross profits to gross losses. A value greater than 1 indicates a profitable system.',
  'Win Rate': 'The share of active trading days (days with a non-zero move) that closed positive. Flat no-trade days are excluded, so a strategy that only trades a few days a week isn’t penalized.',
  'Avg. Trade Return': 'The average percentage gain or loss per day the strategy was active.',
  'Year by Year': 'Calendar-year return for each strategy and benchmark: first trading day to last trading day within each year, using the same date range filter as the chart above.',
};


/** Fyntrak-style value coloring for the Full Comparison table. */
function comparisonTone(kind, value) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  if (kind === 'dd') return value > 0 ? '#f87171' : '#34d399'; // stored positive = a real drawdown
  if (kind === 'pf') return value > 1 ? '#34d399' : '#f87171';
  return value >= 0 ? '#34d399' : '#f87171';
}

function formatComparisonValue(kind, value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (kind === 'dd') return `-${safeNumber(value).toFixed(1)}%`;
  if (kind === 'num') return Number(value).toFixed(2);
  if (kind === 'vol') return `${safeNumber(value).toFixed(2)}%`;
  if (kind === 'pf') return value === Infinity ? '∞' : `${Number(value || 0).toFixed(2)}x`;
  return formatPercent(value); // 'pct'
}

function isEligibleStrategy(strategy) {
  const name = String(strategy?.strategy || '').trim();
  if (!name) return false;
  if (NON_STRATEGY_SHEETS.has(name.toLowerCase())) return false;
  if (DATE_SHEET_PATTERN.test(name)) return false;
  return Boolean(strategy?.success && strategy?.rawData?.length >= 3);
}

function strategyOverall(strategy) {
  return strategy?.statsByDay?.Overall || {};
}

function chartTickFormatter(value) {
  const numeric = safeNumber(value);
  if (Math.abs(numeric) >= 1000000) return `$${(numeric / 1000000).toFixed(1)}M`;
  if (Math.abs(numeric) >= 1000) return `$${(numeric / 1000).toFixed(0)}k`;
  return `$${numeric.toFixed(0)}`;
}

function buildRangeLabel(startDate, endDate) {
  if (!startDate || !endDate) return 'Selected history';
  return `${formatDateLabel(startDate)} – ${formatDateLabel(endDate)}`;
}

function getPresetMeta(presetId) {
  return VECTOR_PRESET_META[presetId] || VECTOR_PRESET_META.custom;
}

function getPresetLabel(presetId) {
  return getPresetMeta(presetId).label;
}

function formatQuoteNumber(value) {
  const parsed = safeNumber(value);
  if (Math.abs(parsed) >= 1000) return parsed.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (Math.abs(parsed) >= 100) return parsed.toFixed(2);
  return parsed.toFixed(3);
}

function ComparisonTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const scenarioEntries = payload.filter((p) => p.dataKey && String(p.dataKey).startsWith('scn_'));
  const benchmarkEntries = payload.filter((p) => p.dataKey && String(p.dataKey).startsWith('bm_'));
  return (
    <div className="vector-capital-tooltip">
      <div className="vector-capital-tooltip-date">{label || row?.label}</div>
      {scenarioEntries.map((p) => (
        <div key={String(p.dataKey)} className="vector-capital-tooltip-line portfolio">
          <span className="swatch" style={{ background: p.color }} />
          <span>{p.name}</span>
          <strong>{buildTooltipCurrency(p.value)}</strong>
        </div>
      ))}
      {benchmarkEntries.map((p) => (
        <div key={String(p.dataKey)} className="vector-capital-tooltip-line benchmark">
          <span className="swatch" style={{ background: p.color }} />
          <span>{p.name}</span>
          <strong>{buildTooltipCurrency(p.value)}</strong>
        </div>
      ))}
      <div className="vector-capital-tooltip-footnote">
        Hypothetical strategy values shown from the selected capital base.
      </div>
    </div>
  );
}

/**
 * rowKind: return | drawdown | factor | winrate — factor/winrate ignore money mode.
 */
function ComparisonMetricRow({
  label,
  metricInfo,
  metricInfoModal = null,
  labelTone = 'default',
  displayMode,
  rowKind = 'return',
  portfolioPct,
  benchmarkPct,
  portfolioMoney,
  benchmarkMoney,
  invert = false,
  diffSuffix = null,
}) {
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const labelClass =
    labelTone === 'risk'
      ? 'vector-capital-metric-label vector-capital-metric-label--risk'
      : labelTone === 'positive'
        ? 'vector-capital-metric-label vector-capital-metric-label--positive'
        : 'vector-capital-metric-label';
  const useMoney = displayMode === 'money' && rowKind === 'return';
  const useDdMoney = displayMode === 'money' && rowKind === 'drawdown';

  const pv = useMoney ? portfolioMoney : (useDdMoney ? portfolioMoney : portfolioPct);
  const bv = useMoney ? benchmarkMoney : (useDdMoney ? benchmarkMoney : benchmarkPct);

  const portfolioNumeric = typeof pv === 'number' && Number.isFinite(pv) ? pv : null;
  const benchmarkNumeric = typeof bv === 'number' && Number.isFinite(bv) ? bv : null;

  const formatCell = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    if (rowKind === 'factor') return formatFactor(value);
    if (rowKind === 'winrate') return formatPercent(value);
    if (rowKind === 'drawdown' && useDdMoney) return formatCurrency(value);
    if (rowKind === 'drawdown' && !useDdMoney) return `${safeNumber(value).toFixed(2)}%`;
    if (rowKind === 'return' && useMoney) return formatCurrency(value);
    return formatPercent(value);
  };

  let diffText = '—';
  let better = true;
  if (portfolioNumeric !== null && benchmarkNumeric !== null) {
    if (!Number.isFinite(portfolioNumeric) || !Number.isFinite(benchmarkNumeric)) {
      diffText = '—';
      better = true;
    } else {
    let difference;
    if (rowKind === 'drawdown' && useDdMoney) {
      difference = portfolioNumeric - benchmarkNumeric;
    } else if (rowKind === 'drawdown' && !useDdMoney) {
      difference = benchmarkNumeric - portfolioNumeric;
    } else {
      difference = invert ? (benchmarkNumeric - portfolioNumeric) : (portfolioNumeric - benchmarkNumeric);
    }
    better = difference >= 0;
    if (rowKind === 'factor') {
      diffText = difference > 0 ? `+${formatFactor(difference)}` : formatFactor(difference);
    } else if ((rowKind === 'return' && useMoney) || (rowKind === 'drawdown' && useDdMoney)) {
      diffText = `${difference > 0 ? '+' : ''}${formatCurrency(difference)}`;
    } else {
      diffText = `${difference > 0 ? '+' : ''}${difference.toFixed(2)}%`;
    }
    }
  }

  return (
    <div className="vector-capital-metric-row">
      <div className={labelClass}>
        <span className="vector-capital-metric-label-text">{label}</span>
        {metricInfo || metricInfoModal ? (
          <span className="vector-capital-metric-info-wrap">
            <button
              type="button"
              className="vector-capital-metric-info-btn"
              aria-label={`${label}: definition`}
              aria-haspopup={metricInfoModal ? 'dialog' : undefined}
              aria-expanded={metricInfoModal ? infoModalOpen : undefined}
              onClick={metricInfoModal ? (e) => {
                e.stopPropagation();
                setInfoModalOpen(true);
              } : undefined}
            >
              <IconInfo size={14} color="rgba(148, 163, 184, 0.95)" />
            </button>
            {metricInfo && !metricInfoModal ? (
              <span className="vector-capital-metric-info-tooltip" role="tooltip">
                {metricInfo}
              </span>
            ) : null}
            {metricInfoModal ? (
              <PnlScalingInfoModal
                open={infoModalOpen}
                onClose={() => setInfoModalOpen(false)}
                title={metricInfoModal.title}
                paragraphs={metricInfoModal.paragraphs}
              />
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="vector-capital-metric-value">{formatCell(pv)}</div>
      <div className="vector-capital-metric-value">{formatCell(bv)}</div>
      <div
        className={`vector-capital-metric-diff ${better ? 'positive' : 'negative'}${
          diffSuffix ? ' vector-capital-metric-diff--with-suffix' : ''
        }`}
      >
        <span className="vector-capital-metric-diff-main">{diffText}</span>
        {diffSuffix}
      </div>
    </div>
  );
}

function monthlyBreakdownTone(value, isMoney) {
  if (value === null || value === undefined || Number.isNaN(value)) return 'muted';
  if (isMoney) {
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'neutral';
  }
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
}

function PeriodReturnBreakdownTable({
  rows,
  displayMode,
  benchmarkShortLabel,
  firstColumnHeader,
  rowKey,
  emptyMessage,
}) {
  const isMoney = displayMode === 'money';

  const cellFmt = (row, pctKey, dollarKey) => {
    if (isMoney) {
      const v = row[dollarKey];
      if (v === null || v === undefined || Number.isNaN(v)) return '—';
      return `${v > 0 ? '+' : ''}${formatCurrency(v)}`;
    }
    const v = row[pctKey];
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    return formatPercent(v);
  };

  if (!rows?.length) {
    return (
      <div className="vector-capital-monthly-breakdown-empty">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="vector-capital-monthly-breakdown-scroll">
      <table className="vector-capital-monthly-breakdown-table">
        <thead>
          <tr>
            <th>{firstColumnHeader}</th>
            <th>Strategy</th>
            <th>{benchmarkShortLabel}</th>
            <th>Difference</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const portTone = monthlyBreakdownTone(isMoney ? row.portDollar : row.portPct, isMoney);
            const benchTone = monthlyBreakdownTone(isMoney ? row.benchDollar : row.benchPct, isMoney);
            const diffTone = monthlyBreakdownTone(isMoney ? row.diffDollar : row.diffPct, isMoney);
            return (
              <tr key={row[rowKey]}>
                <td>{row.label}</td>
                <td className={`vector-capital-mb-num ${portTone}`}>{cellFmt(row, 'portPct', 'portDollar')}</td>
                <td className={`vector-capital-mb-num ${benchTone}`}>{cellFmt(row, 'benchPct', 'benchDollar')}</td>
                <td className={`vector-capital-mb-num ${diffTone}`}>
                  {isMoney
                    ? (row.diffDollar === null || row.diffDollar === undefined || Number.isNaN(row.diffDollar)
                      ? '—'
                      : `${row.diffDollar > 0 ? '+' : ''}${formatCurrency(row.diffDollar)}`)
                    : (row.diffPct === null || row.diffPct === undefined || Number.isNaN(row.diffPct)
                      ? '—'
                      : formatPercent(row.diffPct))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function useVectorCapitalSimulatorState({
  user,
  showEquityPortfolios = false,
  isAdmin = false,
  isFullAdmin = false,
}) {
  const { isAdminActual, adminViewAs } = useAuth();
  const { appName } = useBrandIdentity();
  const chartColors = chartTheme(useColorMode());
  const canSeeInternalStrategies = Boolean(isAdmin);
  const vectorApiHeaders = useMemo(
    () => getVectorApiHeaders({ isAdminActual, adminViewAs }),
    [isAdminActual, adminViewAs],
  );
  const storagePrefix = vectorCapitalStoragePrefix(showEquityPortfolios);
  const defaultScenarioId = showEquityPortfolios ? 'master_multi_account' : 'custom';
  const [strategies, setStrategies] = useState([]);
  const [rawStrategyData, setRawStrategyData] = useState({});
  const [loading, setLoading] = useState(false);
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);
  const [equityStrategiesLoaded, setEquityStrategiesLoaded] = useState(false);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [error, setError] = useState(null);
  const [benchmarkError, setBenchmarkError] = useState(null);
  const [dataSoftRefreshKey, setDataSoftRefreshKey] = useState(0);
  const [marketQuotes, setMarketQuotes] = useState([]);
  const [strategyInstrumentMap, setStrategyInstrumentMap] = useState({});
  // Multi-select scenarios: each selected strategy draws its own curve AND gets
  // its own comparison column. The "Custom Composition" grid is always the
  // Custom scenario; the presets are fixed recipes computed independently. The
  // first selected scenario (in pill order) is the "primary" that drives the
  // editable grid and the single-value panels (assumptions, monthly breakdown).
  // The Vector Capital page starts on Master Multi Account so a growth curve
  // is visible immediately; the + page keeps Custom (build-your-own) default.
  const [selectedScenarioIds, setSelectedScenarioIds] = useState(
    () => readStoredScenarioIds(storagePrefix, defaultScenarioId),
  );
  const [basketDisplayEnabled, setBasketDisplayEnabled] = useState(null);
  const [enabledRegistryScenarios, setEnabledRegistryScenarios] = useState(null);
  const visibleRegistryScenarios = useMemo(
    () => filterRegistryScenariosForViewer(enabledRegistryScenarios, canSeeInternalStrategies),
    [enabledRegistryScenarios, canSeeInternalStrategies],
  );
  const internalRegistryClaims = useMemo(
    () => internalRegistryClaimKeys(enabledRegistryScenarios, canSeeInternalStrategies),
    [enabledRegistryScenarios, canSeeInternalStrategies],
  );
  // Global Custom line color — admin/vector-admin managed, applied for everyone.
  const customLineColor = normalizeCustomLineColor(basketDisplayEnabled?.customColor);

  const registryScenarioBySlug = useMemo(() => {
    const map = new Map();
    const source = canSeeInternalStrategies
      ? (enabledRegistryScenarios || [])
      : (visibleRegistryScenarios || []);
    source.forEach((row) => {
      if (row?.slug) map.set(row.slug, row);
    });
    return map;
  }, [enabledRegistryScenarios, visibleRegistryScenarios, canSeeInternalStrategies]);

  // Live / Backtested / Full Journey (strategies with a live date, set on
  // Strategies → Live data). Remembered per page for this browser tab.
  const [journeyView, setJourneyView] = usePersistentTab(`${storagePrefix}.journey`, 'live', ['live', 'backtested', 'full']);

  // Changes when a strategy is added to (or removed from) this brand, so the
  // equity-curve list below is fetched again instead of missing the new one.
  const registryScenarioSignature = useMemo(
    () => Array.from(registryScenarioBySlug.keys()).sort().join(','),
    [registryScenarioBySlug],
  );

  const monthlyReturnMethods = useMemo(
    () => normalizeMonthlyReturnMethods(basketDisplayEnabled?.monthlyReturnMethods),
    [basketDisplayEnabled?.monthlyReturnMethods],
  );

  const benchmarkSettings = useMemo(
    () => normalizeBenchmarkSettings(basketDisplayEnabled?.benchmarkSettings),
    [basketDisplayEnabled?.benchmarkSettings],
  );

  const marketOptions = useMemo(
    () => resolveMarketOptions(benchmarkSettings),
    [benchmarkSettings],
  );

  const resolveBenchmarkLineColor = useCallback(
    (benchmarkId) => getBenchmarkLineColor(benchmarkSettings, benchmarkId),
    [benchmarkSettings],
  );

  const registryRowForScenarioId = useCallback((scenarioId) => {
    const slug = parseRegistryScenarioId(normalizeScenarioId(scenarioId))
      || parseRegistryScenarioId(scenarioId)
      || String(scenarioId || '').replace(/^scenario:/, '');
    return registryScenarioBySlug.get(slug) || null;
  }, [registryScenarioBySlug]);

  const internalScenarioIds = useMemo(
    () => buildInternalScenarioIdSet(enabledRegistryScenarios, { canSeeInternal: canSeeInternalStrategies }),
    [enabledRegistryScenarios, canSeeInternalStrategies],
  );
  const basketInternalMarkers = useMemo(() => {
    if (!canSeeInternalStrategies) return new Set();
    return new Set(
      (basketDisplayEnabled?.internalPillMarkers || [])
        .map((key) => String(key || '').trim().toLowerCase())
        .filter(Boolean),
    );
  }, [basketDisplayEnabled?.internalPillMarkers, canSeeInternalStrategies]);

  /** Internal-visibility registry scenarios — only admins receive them from the
   *  enabled endpoint, so a marker on the pill shows "clients can't see this". */
  const isInternalScenario = useCallback((id) => {
    if (!canSeeInternalStrategies) return false;
    const normalized = normalizeScenarioId(id);
    if (internalScenarioIds.has(normalized) || internalScenarioIds.has(String(id || ''))) {
      return true;
    }
    const slug = parseRegistryScenarioId(normalized);
    if (slug) {
      if (registryScenarioBySlug.get(slug)?.visibility === 'internal') return true;
      if (basketInternalMarkers.has(slug.toLowerCase())) return true;
    }
    const dynamic = parseDynamicScenarioId(id);
    if (dynamic?.kind === 'futures') {
      const nameKey = String(dynamic.name || '').trim().toLowerCase();
      if (basketInternalMarkers.has(nameKey)) return true;
      return (enabledRegistryScenarios || []).some((row) => {
        if (String(row?.visibility || '').toLowerCase() !== 'internal') return false;
        const cfg = row.dataSourceConfig || {};
        const candidates = [cfg.strategyName, row.displayName, row.slug]
          .map((v) => String(v || '').trim().toLowerCase());
        return candidates.includes(nameKey);
      });
    }
    if (dynamic?.kind === 'equity_algo' && basketInternalMarkers.has(String(dynamic.key || '').toLowerCase())) {
      return true;
    }
    return false;
  }, [
    canSeeInternalStrategies,
    internalScenarioIds,
    registryScenarioBySlug,
    enabledRegistryScenarios,
    basketInternalMarkers,
  ]);

  /** Strategy names whose curves genuinely compound (Kairos-style) — their
   *  blend sleeves keep ratio returns; everything else is fixed-notional. */
  const compoundEquityStrategyNames = useMemo(() => {
    const set = new Set();
    registryScenarioBySlug.forEach((row) => {
      if (!registryScenarioUsesCompoundEquity(row)) return;
      const cfg = row.dataSourceConfig || {};
      [cfg.strategyName, row.displayName].forEach((n) => {
        const key = String(n || '').trim().toLowerCase();
        if (key) set.add(key);
      });
    });
    return set;
  }, [registryScenarioBySlug]);

  /** Whether an equity strategy's curve genuinely compounds (Kairos-style). */
  const isCompoundEquityStrategy = useCallback((s) => {
    const name = String(s?.name || '').trim().toLowerCase();
    const slugRow = s?.scenarioSlug ? registryScenarioBySlug.get(s.scenarioSlug) : null;
    return s?.isKairosWebhook === true
      || compoundEquityStrategyNames.has(name)
      || (slugRow ? registryScenarioUsesCompoundEquity(slugRow) : false);
  }, [registryScenarioBySlug, compoundEquityStrategyNames]);

  /** Returns stream for an equity-strategy blend sleeve: ratio returns for
   *  compounding curves, fixed-base-notional returns for additive ones. */
  const blendReturnsForEquityStrategy = useCallback((s) => {
    if (isCompoundEquityStrategy(s)) return Array.isArray(s?.returns) ? s.returns : [];
    if (Array.isArray(s?.points) && s.points.length >= 2) {
      return deriveFixedNotionalReturns(s.points, (p) => p?.equity);
    }
    return Array.isArray(s?.returns) ? s.returns : [];
  }, [isCompoundEquityStrategy]);

  /** Whether a scenario's equity curve compounds (Kairos-style) — used for
   *  series construction and calendar daily $ display, not calendar-month % tables. */
  const scenarioCompoundsMonthly = useCallback((id) => {
    if (scenarioUsesCompoundMonthly(id)) return true;
    const registryRow = registryScenarioBySlug.get(
      parseRegistryScenarioId(normalizeScenarioId(id)) || '',
    );
    return registryRow ? registryScenarioUsesCompoundEquity(registryRow) : false;
  }, [registryScenarioBySlug]);

  const scenarioPillOrder = useMemo(
    () => {
      // The legacy pill IDs (Master Multi Account, the Equity Portfolios, the
      // stock/test presets) name Vector Capital's own products. Another brand
      // has no claim on them, so it starts from "Custom" alone and fills up from
      // its own registry — otherwise a brand with an empty registry falls back
      // to Vector's line-up, which is the leak this whole change exists to fix.
      const vectorBrand = isVectorEntity();
      const legacyStockAndTestIds = vectorBrand ? Object.keys(LEGACY_SCENARIO_SLUGS) : [];
      const base = showEquityPortfolios
        ? (vectorBrand
          ? [
            'master_multi_account',
            ...legacyStockAndTestIds.filter((id) => id !== 'master_multi_account'),
          ]
          : ['custom'])
        : ['custom', ...(vectorBrand ? VECTOR_PRESET_ORDER : [])];
      let pills = base;

      if (visibleRegistryScenarios?.length) {
        const registryIds = visibleRegistryScenarios
          .filter((row) => row.slug !== 'master_multi_account')
          .slice()
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.displayName).localeCompare(String(b.displayName)))
          .map((row) => row.dashboardId || registryScenarioId(row.slug));
        if (showEquityPortfolios) {
          pills = vectorBrand
            ? [
              'master_multi_account',
              ...registryIds.filter((id) => id !== registryScenarioId('master_multi_account')),
            ]
            : registryIds;
        } else {
          pills = ['custom', ...(vectorBrand ? VECTOR_PRESET_ORDER : []), ...registryIds];
        }
      }

      if (basketDisplayEnabled) {
        const dynamicEquity = filterNamesByInternalRegistryClaims(
          basketDisplayEnabled.equityAlgos,
          internalRegistryClaims,
        ).map(dynamicEquityScenarioId);
        const dynamicFuturesNames = filterDynamicFuturesForRegistryPresets(
          filterNamesByInternalRegistryClaims(
            basketDisplayEnabled.futures,
            internalRegistryClaims,
          ),
          visibleRegistryScenarios,
          basketDisplayEnabled.registryFuturesClaims,
        );
        const dynamicFutures = dynamicFuturesNames.map(dynamicFuturesScenarioId);
        pills = [...pills, ...dynamicEquity, ...dynamicFutures];
        if (!visibleRegistryScenarios?.length) {
          pills = filterStockScenarioIds(pills, basketDisplayEnabled.stocks);
          pills = filterTestScenarioIds(pills, basketDisplayEnabled.tests);
        }
      } else if (!visibleRegistryScenarios?.length) {
        pills = filterTestScenarioIds(pills, []);
      }

      if (showEquityPortfolios) {
        pills = [...new Set([...pills.filter((id) => id !== 'custom'), 'custom'])];
      }
      return applyControlsPillOrder(pills, basketDisplayEnabled?.controlsPillOrder);
    },
    [showEquityPortfolios, basketDisplayEnabled, visibleRegistryScenarios, internalRegistryClaims],
  );
  useEffect(() => {
    if (!visibleRegistryScenarios?.length) return;
    setSelectedScenarioIds((prev) => {
      const next = new Set(Array.from(prev).map((id) => normalizeScenarioId(id)));
      return next.size ? next : prev;
    });
  }, [visibleRegistryScenarios]);

  const orderedSelectedIds = useMemo(
    () => scenarioPillOrder.filter((id) => selectedScenarioIds.has(id)),
    [scenarioPillOrder, selectedScenarioIds],
  );
  // The live date of the first selected strategy that has one: the Live /
  // Backtested / Full Journey tabs only show when there is one.
  const journeyLiveSince = useMemo(() => {
    for (const id of orderedSelectedIds) {
      const slug = parseRegistryScenarioId(normalizeScenarioId(id));
      const live = slug ? normalizeLiveSince(registryScenarioBySlug.get(slug)?.dataSourceConfig?.liveSince) : null;
      if (live) return live;
    }
    return null;
  }, [orderedSelectedIds, registryScenarioBySlug]);
  const equityOverlaySlugs = useMemo(
    () => buildEquityOverlaySlugs(orderedSelectedIds, registryScenarioBySlug),
    [orderedSelectedIds, registryScenarioBySlug],
  );
  const liveEquityPollActive = useMemo(
    () => needsLiveEquityStrategiesPoll(orderedSelectedIds, registryScenarioBySlug),
    [orderedSelectedIds, registryScenarioBySlug],
  );
  const primaryScenarioId = orderedSelectedIds[0]
    || primaryPillScenarioId(scenarioPillOrder, defaultScenarioId);
  const selectedPreset = primaryScenarioId; // primary scenario drives the grid + headline panels
  const [dateRangeFilter, setDateRangeFilter] = useState(() => readStoredDateRange(storagePrefix));
  const [customBlendStartBasis, setCustomBlendStartBasis] = useState(() => readStoredCustomBlendStartBasis(storagePrefix));
  const [customStartDate, setCustomStartDate] = useState(() => readStoredDateField(storagePrefix, 'customStartDate'));
  const [customEndDate, setCustomEndDate] = useState(() => readStoredDateField(storagePrefix, 'customEndDate'));
  const [presetInfoExpanded, setPresetInfoExpanded] = useState(false);
  // Pencil next to the Custom pill opens the composition editor in a modal;
  // closing it keeps the allocations (they live in normal state, not the modal).
  const [basketModalOpen, setBasketModalOpen] = useState(false);
  const [comparisonTab, setComparisonTab] = usePersistentTab(`${storagePrefix}.comparisonTab`, 'all', ['all', 'benchmark']);
  const [capitalBasePreset, setCapitalBasePreset] = useState(() => readStoredCapitalBasePreset(storagePrefix));
  const [customCapitalRaw, setCustomCapitalRaw] = useState(() => readStoredDateField(storagePrefix, 'customCapitalRaw'));
  const [strategyScenarioConfigByName, setStrategyScenarioConfigByName] = useState({});
  const [selectedBenchmarkIds, setSelectedBenchmarkIds] = useState(() => readStoredBenchmarkIds(storagePrefix));
  const [benchmarkDataById, setBenchmarkDataById] = useState({});

  useEffect(() => {
    const enabled = new Set(benchmarkSettings.enabled);
    setSelectedBenchmarkIds((prev) => {
      let next = new Set([...prev].filter((id) => enabled.has(id)));
      if (next.size === 0) {
        benchmarkSettings.defaultSelected
          .filter((id) => enabled.has(id))
          .forEach((id) => next.add(id));
      }
      if (next.size === 0 && benchmarkSettings.enabled.length) {
        next = new Set([benchmarkSettings.enabled[0]]);
      }
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [benchmarkSettings]);
  // Scenario shown in the "Strategy vs Benchmark" panel (header dropdown);
  // falls back to the primary selected strategy.
  const [benchmarkCompareScenarioId, setBenchmarkCompareScenarioId] = useState(null);
  const [monthlyBreakdownExpanded, setMonthlyBreakdownExpanded] = useState(false);
  const [yearlyBreakdownExpanded, setYearlyBreakdownExpanded] = useState(false);
  const [fullComparisonYearlyExpanded, setFullComparisonYearlyExpanded] = useState(false);
  const [strategyBasketQuery, setStrategyBasketQuery] = useState('');
  const [strategyBasketFilter, setStrategyBasketFilter] = useState('all');
  // Futures (basket strategies) and Stocks (equity strategies) groups start
  // collapsed so the long lists don't dominate the page — expand to allocate.
  // Per-futures-card expand (clicking a card reveals its config). Independent
  // of allocation — the checkmark still only reflects weight > 0.
  const [expandedStrategies, setExpandedStrategies] = useState(() => new Set());
  const [comparisonBenchmarkId, setComparisonBenchmarkId] = useState(null);
  const [strategyBasketScrollCueVisible, setStrategyBasketScrollCueVisible] = useState(true);
  const strategyBasketScrollCueTimeoutRef = useRef(null);
  // Vector Capital + allocates a PERCENTAGE of capital across the strategies in
  // the basket. Each selected strategy carries a weight (% of the total
  // portfolio), stored in strategyScenarioConfigByName[name].weightPct. Equity-
  // curve strategies (e.g. "Vector Capital Long Equity Strategy") are also
  // allocatable; their weights live in equityWeightByName. When the total
  // allocated weight is > 0, the weighted blend replaces the contract-sized
  // basket combine.
  const [equityStrategies, setEquityStrategies] = useState([]);
  const [equityWeightByName, setEquityWeightByName] = useState({});
  // Admin-configurable Equity Portfolio settings (equity %, leverage, per-leg
  // allocation/enabled), keyed by portfolio_key. Null until loaded → defaults.
  const [equityPortfolioConfig, setEquityPortfolioConfig] = useState(null);
  const [ntAccounts, setNtAccounts] = useState([]);
  const [nt8ConnectionId, setNt8ConnectionId] = useState(null);

  const sortedBenchmarkIds = useMemo(
    () => Array.from(selectedBenchmarkIds).sort((a, b) => a.localeCompare(b)),
    [selectedBenchmarkIds],
  );

  useEffect(() => {
    const list = sortedBenchmarkIds;
    if (!list.length) return;
    setComparisonBenchmarkId((prev) => (prev && list.includes(prev) ? prev : list[0]));
  }, [sortedBenchmarkIds]);

  useEffect(() => () => {
    if (strategyBasketScrollCueTimeoutRef.current) {
      clearTimeout(strategyBasketScrollCueTimeoutRef.current);
    }
  }, []);

  // Persist dashboard controls across refresh (data reloads independently).
  useEffect(() => {
    try {
      localStorage.setItem(`${storagePrefix}.selectedScenarioIds`, JSON.stringify([...selectedScenarioIds]));
    } catch (_err) { /* ignore */ }
  }, [storagePrefix, selectedScenarioIds]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storagePrefix}.dateRangeFilter`, dateRangeFilter);
    } catch (_err) { /* ignore */ }
  }, [storagePrefix, dateRangeFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storagePrefix}.customBlendStartBasis`, customBlendStartBasis);
    } catch (_err) { /* ignore */ }
  }, [storagePrefix, customBlendStartBasis]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storagePrefix}.customStartDate`, customStartDate);
    } catch (_err) { /* ignore */ }
  }, [storagePrefix, customStartDate]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storagePrefix}.customEndDate`, customEndDate);
    } catch (_err) { /* ignore */ }
  }, [storagePrefix, customEndDate]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storagePrefix}.capitalBasePreset`, String(capitalBasePreset));
    } catch (_err) { /* ignore */ }
  }, [storagePrefix, capitalBasePreset]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storagePrefix}.customCapitalRaw`, customCapitalRaw);
    } catch (_err) { /* ignore */ }
  }, [storagePrefix, customCapitalRaw]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storagePrefix}.selectedBenchmarkIds`, JSON.stringify([...selectedBenchmarkIds]));
    } catch (_err) { /* ignore */ }
  }, [storagePrefix, selectedBenchmarkIds]);

  // Close the composition modal with Escape.
  useEffect(() => {
    if (!basketModalOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setBasketModalOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [basketModalOpen]);

  const toggleBenchmarkId = useCallback((id) => {
    if (!benchmarkSettings.enabled.includes(id)) return;
    setSelectedBenchmarkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id); // allow removing all benchmarks (zero is valid)
      } else {
        next.add(id);
      }
      return next;
    });
  }, [benchmarkSettings.enabled]);

  const handleStrategyBasketScroll = useCallback(() => {
    setStrategyBasketScrollCueVisible(false);
    if (strategyBasketScrollCueTimeoutRef.current) {
      clearTimeout(strategyBasketScrollCueTimeoutRef.current);
    }
    strategyBasketScrollCueTimeoutRef.current = setTimeout(() => {
      setStrategyBasketScrollCueVisible(true);
    }, 700);
  }, []);

  const fetchAnalytics = useCallback(async (options = {}) => {
    const silent = Boolean(options?.silent);
    if (!user?.email) {
      if (!silent) setError('Please sign in to view strategies.');
      return;
    }

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const { response, data } = await fetchJsonWithTimeout(
        `${API_ENDPOINTS.analyze.replace('/api/analyze', '/api/portfolio-analytics')}?email=${encodeURIComponent(user.email)}`,
        { headers: getBearerAuthHeaders() },
      );
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load strategy analytics.');
      }

      const eligible = (data.strategies || []).filter(isEligibleStrategy);
      const nextRawData = {};
      eligible.forEach((strategy) => {
        nextRawData[strategy.strategy] = strategy.rawData;
      });
      setRawStrategyData(nextRawData);
      setStrategies(eligible);
    } catch (nextError) {
      if (!silent) setError(nextError.message || 'Failed to load strategy analytics.');
    } finally {
      if (!silent) setLoading(false);
      setAnalyticsLoaded(true);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    if (!user?.email) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { response, data } = await fetchJsonWithTimeout(
          API_ENDPOINTS.strategyInstruments,
          { headers: getBearerAuthHeaders() },
        );
        if (!cancelled && response.ok && data.success) {
          setStrategyInstrumentMap(data.instruments || {});
        }
      } catch (_e) {
        if (!cancelled) setStrategyInstrumentMap({});
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email, dataSoftRefreshKey]);

  useEffect(() => {
    return subscribeAppAutoRefresh(() => {
      if (!user?.email) return;
      fetchAnalytics({ silent: true });
      setDataSoftRefreshKey((key) => key + 1);
    });
  }, [user?.email, fetchAnalytics]);

  // Load equity-curve strategies (admin-uploaded). Best-effort: failure just
  // means none are offered; the weekday-sleeve simulator works unchanged.
  // Re-polled on worker schedule when a live webhook/NT8 overlay scenario is selected.
  useEffect(() => {
    let cancelled = false;
    const equityUrl = `${API_ENDPOINTS.vectorEquityStrategies}?overlaySlugs=${encodeURIComponent(equityOverlaySlugs)}`;
    const load = async () => {
      try {
        const { response, data } = await fetchJsonWithTimeout(
          equityUrl,
          { headers: getBearerAuthHeaders() },
        );
        if (cancelled) return;
        if (response.ok && data.success && Array.isArray(data.strategies)) {
          setEquityStrategies(data.strategies);
        }
      } catch (_e) {
        if (!cancelled) setEquityStrategies([]);
      } finally {
        if (!cancelled) setEquityStrategiesLoaded(true);
      }
    };
    load();
    if (!liveEquityPollActive) return () => { cancelled = true; };
    const id = window.setInterval(load, LIVE_EQUITY_POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [user?.email, dataSoftRefreshKey, equityOverlaySlugs, liveEquityPollActive, registryScenarioSignature]);

  // Admin-configurable Equity Portfolio settings — drives the equity blends.
  useEffect(() => {
    if (!showEquityPortfolios) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(API_ENDPOINTS.vectorEquityPortfolioConfig, { headers: getBearerAuthHeaders() });
        const data = await resp.json().catch(() => ({}));
        if (!cancelled && resp.ok && data.success && data.configs) {
          setEquityPortfolioConfig(data.configs);
        }
      } catch (_e) {
        if (!cancelled) setEquityPortfolioConfig(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email, showEquityPortfolios, dataSoftRefreshKey]);

  const futuresCatalogNames = useMemo(
    // Stock algos (and their OG copies) are dual-stored as futures-grid snapshots;
    // keep them OUT of the Futures catalog so they only appear under Individual
    // Stock Algos in Strategy Settings.
    () => strategies.map((s) => s.strategy).filter(Boolean).filter((n) => !isVectorCapitalStockAlgo(n)),
    [strategies],
  );

  // Names claimed by registry scenarios (MW2, PrimeBeef, …). Their equity
  // strategy rows exist only as journal-derived curves for the registry pill —
  // listing them under Individual Stock Algos would duplicate the strategy
  // (with stock-algo compounding math on a fixed-notional futures curve).
  const registryClaimedNameKeys = useMemo(() => {
    const set = new Set();
    registryScenarioBySlug.forEach((row) => {
      [row?.dataSourceConfig?.strategyName, row?.displayName].forEach((n) => {
        const key = String(n || '').trim().toLowerCase();
        if (key) set.add(key);
      });
    });
    return set;
  }, [registryScenarioBySlug]);

  const individualEquityAlgos = useMemo(
    () => equityStrategies.filter(
      (s) => isIndividualEquityAlgo(s)
        && !registryClaimedNameKeys.has(String(s?.name || '').trim().toLowerCase()),
    ),
    [equityStrategies, registryClaimedNameKeys],
  );

  const equityAlgoCatalog = useMemo(
    () => individualEquityAlgos.map((s) => s.name),
    [individualEquityAlgos],
  );

  const getScenarioLabel = useCallback((scenarioId) => {
    const normalizedId = normalizeScenarioId(scenarioId);
    const registrySlug = parseRegistryScenarioId(normalizedId);
    if (registrySlug && registryScenarioBySlug.has(registrySlug)) {
      const row = registryScenarioBySlug.get(registrySlug);
      return registryScenarioLabel(row, getPresetLabel(registrySlug));
    }
    const dynamic = parseDynamicScenarioId(normalizedId);
    let defaultLabel;
    if (dynamic?.kind === 'equity_algo') {
      const name = equityAlgoNameFromKey(dynamic.key, individualEquityAlgos);
      if (name) {
        const tickerMatch = name.match(/—\s*([A-Z]{2,5})\s*\(/);
        if (tickerMatch) defaultLabel = tickerMatch[1];
        else defaultLabel = name.replace(/^Vector Capital —\s*/i, '').replace(/\s*\([^)]*\)\s*$/, '').trim() || name;
      } else {
        defaultLabel = dynamic.key.replace(/^algo:/, '').replace(/_/g, ' ');
      }
    } else if (dynamic?.kind === 'futures') {
      defaultLabel = dynamic.name;
    } else {
      defaultLabel = getPresetLabel(normalizedId);
    }
    return resolveScenarioDisplayLabel(normalizedId, basketDisplayEnabled?.labels, defaultLabel);
  }, [individualEquityAlgos, basketDisplayEnabled?.labels, registryScenarioBySlug]);

  const getScenarioColor = useCallback(
    (scenarioId) => {
      const normalizedId = normalizeScenarioId(scenarioId);
      // The Custom line color is user-chosen (persisted per browser).
      if (normalizedId === 'custom') return customLineColor;
      const registrySlug = parseRegistryScenarioId(normalizedId);
      if (registrySlug && registryScenarioBySlug.has(registrySlug)) {
        const row = registryScenarioBySlug.get(registrySlug);
        return themeColor(registryScenarioColor(row, resolveScenarioColor(normalizedId, basketDisplayEnabled?.colors)));
      }
      return resolveScenarioColor(normalizedId, basketDisplayEnabled?.colors);
    },
    [basketDisplayEnabled?.colors, registryScenarioBySlug, customLineColor],
  );

  // Registry-backed strategies (dashboard pills, visibility, labels/colors).
  useEffect(() => {
    if (!showEquityPortfolios) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(API_ENDPOINTS.vectorScenariosEnabled, {
          headers: vectorApiHeaders,
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (resp.ok && data.success && Array.isArray(data.scenarios)) {
          setEnabledRegistryScenarios(data.scenarios);
        }
      } catch (_e) {
        if (!cancelled) setEnabledRegistryScenarios(null);
      }
    })();
    return () => { cancelled = true; };
  }, [showEquityPortfolios, dataSoftRefreshKey, vectorApiHeaders]);

  // Which futures/stocks/equity algos appear as strategies (admin-managed).
  useEffect(() => {
    if (!showEquityPortfolios) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(
          API_ENDPOINTS.vectorBasketDisplay(
            futuresCatalogNames,
            equityAlgoCatalog,
          ),
          { headers: vectorApiHeaders },
        );
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (resp.ok && data.success && data.enabled) {
          setBasketDisplayEnabled(data.enabled);
        }
      } catch (_e) {
        if (!cancelled) setBasketDisplayEnabled(null);
      }
    })();
    return () => { cancelled = true; };
  }, [futuresCatalogNames, equityAlgoCatalog, showEquityPortfolios, dataSoftRefreshKey, vectorApiHeaders]);

  const loadNtSettings = useCallback(async () => {
    if (!isFullAdmin) return;
    try {
      const settingsResp = await fetch(API_ENDPOINTS.adminNt8Settings, { headers: getBearerAuthHeaders() });
      const settingsData = await settingsResp.json().catch(() => ({}));
      if (settingsResp.ok && settingsData.success) {
        setNtAccounts(settingsData.accounts || []);
        setNt8ConnectionId(settingsData.connectionId || null);
      } else {
        setNtAccounts([]);
        setNt8ConnectionId(null);
      }
    } catch (_e) {
      setNtAccounts([]);
      setNt8ConnectionId(null);
    }
  }, [isFullAdmin]);

  useEffect(() => { loadNtSettings(); }, [loadNtSettings]);

  // Equity Portfolio (base Vector Capital page only): the two equal-weight
  // 5-stock blends, surfaced both as preset pills AND as allocatable rows.
  const equityPortfolioPreset = (showEquityPortfolios && isEquityPortfolioPreset(selectedPreset))
    ? EQUITY_PORTFOLIO_PRESETS[selectedPreset]
    : null;
  const equityStrategyByName = useMemo(
    () => new Map(equityStrategies.map((s) => [s.name, s])),
    [equityStrategies],
  );
  // The five stock legs (AAPL/TSLA/NVDA/AMZN/PLTR), resolved once — same set for
  // both the 1× and 2.25× blends and for the composition explainer.
  const equityPortfolioLegs = useMemo(
    () => (showEquityPortfolios
      ? resolveEquityPortfolioLegs(
        'equity_portfolio_1x',
        equityStrategies,
        equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET.equity_portfolio_1x]?.legs,
      )
      : []),
    [showEquityPortfolios, equityStrategies, equityPortfolioConfig],
  );
  // Synthetic equity portfolio strategies — allocatable rows whose daily returns
  // encode the blended curve (leverage baked in), so a % allocation rides the
  // same curve as the matching strategy pill.
  const equityPortfolioSynthetics = useMemo(() => {
    if (!showEquityPortfolios) return [];
    return ALL_EQUITY_PORTFOLIO_PRESET_ORDER.map((id) => {
      const preset = EQUITY_PORTFOLIO_PRESETS[id];
      if (!preset) return null;
      const legs = resolveEquityPortfolioLegs(
        id,
        equityStrategies,
        equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET[id]]?.legs,
      ).filter((leg) => leg.found);
      const leverage = getEquityPortfolioEffectiveLeverage(id, equityPortfolioConfig);
      // Weighting per the selected method; compounding is applied where the curve
      // is built (buildScenarioSeries), so keep this returns-sleeve fixed-notional.
      const sleeveWeighting = resolveEquityPortfolioMethod(
        equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET[id]]?.method,
      ).weighting;
      const series = legs.length
        ? buildEquityPortfolioSeries(legs, leverage, equityStrategyByName, {}, 100000, false, sleeveWeighting)
        : [];
      return {
        name: preset.label,
        description: null,
        // Additive series → fixed-notional returns, so windowed blends match
        // the Equity Portfolio strategy column (Σ P&L ÷ base).
        returns: deriveFixedNotionalReturns(series),
        isEquityPortfolio: true,
      };
    }).filter(Boolean);
  }, [showEquityPortfolios, equityStrategies, equityPortfolioConfig, equityStrategyByName]);

  // Canonical equity list for the Stocks column + the blend. On the base page:
  // Equity Portfolio blends, Kairos curves, and admin-enabled individual stock algos.
  const equityStrategiesView = useMemo(() => {
    const allocatable = equityStrategies.filter(
      (s) => !s?.isWebhookTest && !/webhook test/i.test(String(s?.name || '')),
    );
    const rank = (name) => (/long equity/i.test(String(name)) ? 0 : 1);
    const baseSorted = [...allocatable].sort(
      (a, b) => rank(a.name) - rank(b.name) || String(a.name).localeCompare(String(b.name)),
    );
    if (!showEquityPortfolios) return baseSorted;
    const core = [];
    allocatable.forEach((s) => {
      const n = String(s?.name || '');
      // Tag the registry slug so the Custom basket can resolve this curve by slug
      // (correct data source) and label it with the scenario's configured name.
      if (n.includes('QLD')) {
        core.push({ ...s, name: 'Kairos Equity Standard', scenarioSlug: s?.scenarioSlug || 'vc_core_standard' });
        return;
      }
      if (n.includes('TQQQ')) {
        core.push({ ...s, name: 'Kairos Equity Aggressive', scenarioSlug: s?.scenarioSlug || 'vc_core_aggressive' });
        return;
      }
      // Vector Capital Journal lives in the FUTURES basket column only (as
      // "VC Futures"), journal-backed — not duplicated on the stocks side.
    });
    const enabledIndividuals = filterEquityAlgoItems(
      individualEquityAlgos,
      basketDisplayEnabled?.equityAlgos,
    );
    return [...equityPortfolioSynthetics, ...core, ...enabledIndividuals];
  }, [
    equityStrategies,
    equityPortfolioSynthetics,
    showEquityPortfolios,
    individualEquityAlgos,
    basketDisplayEnabled,
  ]);

  const equityStrategiesForBasket = useMemo(() => {
    if (!showEquityPortfolios) return equityStrategiesView;

    const syntheticsById = Object.fromEntries(
      ALL_EQUITY_PORTFOLIO_PRESET_ORDER.map((id) => {
        const label = EQUITY_PORTFOLIO_PRESETS[id]?.label;
        const syn = equityPortfolioSynthetics.find((s) => s.name === label);
        return [id, syn];
      }),
    );
    const viewByName = new Map(equityStrategiesView.map((s) => [s.name, s]));

    // "Show in custom options" gate (independent of Public/Internal/Hidden).
    // `custom` lists the visible keys admins flagged for the Custom grid; when it
    // is present we include ONLY those (and never fall back to the full view, so
    // hidden / unchecked scenarios can't leak in). Absent (older payload) → no gate.
    const customStockSet = basketDisplayEnabled?.custom?.stocks
      ? new Set(basketDisplayEnabled.custom.stocks)
      : null;
    const customAlgoSet = basketDisplayEnabled?.custom?.equityAlgos
      ? new Set(basketDisplayEnabled.custom.equityAlgos)
      : null;
    const stockAllowed = (slug) => !customStockSet || customStockSet.has(slug);
    const algoAllowed = (key) => !customAlgoSet || customAlgoSet.has(key);
    // Bind each Custom row to the scenario's configured display name so it matches
    // Strategy Settings exactly (falls back to the resolved curve name).
    const scenarioName = (slug, fallback) => (
      registryScenarioBySlug.get(slug)?.displayName || fallback
    );
    const pushNamed = (seen, out, item, name) => {
      if (!item || seen.has(name)) return;
      seen.add(name);
      out.push(name === item.name ? item : { ...item, name });
    };

    const pushStockSlug = (slug) => {
      if (!slug || !stockAllowed(slug)) return;
      const legacyPreset = legacyPresetIdFromRegistrySlug(slug) || slug;
      if (isEquityPortfolioPreset(legacyPreset)) {
        const item = syntheticsById[legacyPreset];
        if (item) pushNamed(seen, out, item, scenarioName(slug, item.name));
        return;
      }
      if (legacyPreset === 'vc_core_standard' || legacyPreset === 'vc_core_aggressive' || legacyPreset === 'vector_capital_journal') {
        const label = VECTOR_PRESET_META[legacyPreset]?.label;
        const item = label ? viewByName.get(label) : null;
        const bySlug = equityStrategiesView.find((s) => s?.scenarioSlug === slug);
        const registryRow = registryScenarioBySlug.get(slug);
        const resolved = bySlug || item
          || (registryRow ? findEquityStrategyForRegistry(registryRow, equityStrategies) : null);
        if (resolved) pushNamed(seen, out, resolved, scenarioName(slug, resolved.name));
        return;
      }
      const row = registryScenarioBySlug.get(slug);
      if (row) {
        // equityStrategiesView narrows to portfolio synthetics + Kairos cores +
        // individual algos, so registry stock strategies created in admin (e.g.
        // Kairos-webhook equity curves) resolve from the full equity list.
        const strat = findEquityStrategyForRegistry(row, equityStrategiesView)
          || findEquityStrategyForRegistry(row, equityStrategies);
        if (strat) pushNamed(seen, out, strat, scenarioName(slug, strat.name));
      }
    };

    const seen = new Set();
    const out = [];

    scenarioPillOrder.forEach((id) => {
      if (id === 'custom') return;
      const registrySlug = parseRegistryScenarioId(id);
      if (registrySlug) {
        const row = registryScenarioBySlug.get(registrySlug);
        if (row && String(row.category || '').toLowerCase() === 'stocks') {
          pushStockSlug(registrySlug);
        }
        return;
      }
      if (isEquityPortfolioPreset(id)) {
        if (!stockAllowed(id)) return;
        const item = syntheticsById[id];
        if (item) pushNamed(seen, out, item, scenarioName(id, item.name));
        return;
      }
      if (id === 'vc_core_standard' || id === 'vc_core_aggressive' || id === 'vector_capital_journal') {
        if (!stockAllowed(id)) return;
        const label = VECTOR_PRESET_META[id]?.label;
        const item = label ? viewByName.get(label) : null;
        const bySlug = equityStrategiesView.find((s) => s?.scenarioSlug === id);
        const resolved = bySlug || item;
        if (resolved) pushNamed(seen, out, resolved, scenarioName(id, resolved.name));
        return;
      }
      const dynamic = parseDynamicScenarioId(id);
      if (dynamic?.kind === 'equity_algo') {
        if (!algoAllowed(dynamic.key)) return;
        const name = equityAlgoNameFromKey(dynamic.key, individualEquityAlgos);
        const item = name
          ? (individualEquityAlgos.find((s) => s.name === name) || viewByName.get(name))
          : null;
        if (item) pushNamed(seen, out, item, item.name);
      }
    });

    // Registry stock slugs flagged Custom may not match pill-order branches above.
    if (customStockSet) {
      customStockSet.forEach((slug) => pushStockSlug(slug));
    }
    // Same for individual stock algos — resolve directly from the custom gate.
    if (customAlgoSet) {
      customAlgoSet.forEach((key) => {
        if (!algoAllowed(key)) return;
        const name = equityAlgoNameFromKey(key, individualEquityAlgos);
        const item = name
          ? (individualEquityAlgos.find((s) => s.name === name) || viewByName.get(name))
          : null;
        if (item) pushNamed(seen, out, item, item.name);
      });
    }

    // Only fall back to the full unfiltered view when there is NO custom gate
    // configured — otherwise an empty gate legitimately means "nothing in Custom".
    if (customStockSet || customAlgoSet) return out;
    return out.length ? out : equityStrategiesView;
  }, [
    showEquityPortfolios,
    scenarioPillOrder,
    equityPortfolioSynthetics,
    equityStrategiesView,
    equityStrategies,
    individualEquityAlgos,
    basketDisplayEnabled,
    registryScenarioBySlug,
  ]);

  const capitalBase = useMemo(() => {
    const cleaned = String(customCapitalRaw || '').replace(/[$,\s]/g, '').trim();
    if (cleaned !== '') {
      const n = parseFloat(cleaned);
      if (Number.isFinite(n) && n > 0) {
        return Math.min(Math.max(n, 1), 1e12);
      }
    }
    return capitalBasePreset;
  }, [customCapitalRaw, capitalBasePreset]);

  const resolveMonthlyFixedBase = useCallback((scenarioId) => (
    monthlyFixedBaseCapital(
      scenarioId,
      registryRowForScenarioId(scenarioId),
      monthlyReturnMethods,
      capitalBase,
    )
  ), [registryRowForScenarioId, monthlyReturnMethods, capitalBase]);

  /** Futures-column strategies whose Custom-blend sleeve uses the same equity
   *  curve as their registry Overview pill (trade journal / equity curve) instead
   *  of the futures grid snapshot. Grid sim is kept only when no journal exists. */
  const journalBackedFuturesByName = useMemo(() => {
    const seedEntries = new Map([
      [MASTER_ACCOUNT_STRATEGY_NAME, null],
      ['Vector Capital Journal', null],
    ]);
    registryScenarioBySlug.forEach((row) => {
      if (String(row.category || '').toLowerCase() !== 'futures') return;
      const name = String(row.dataSourceConfig?.strategyName || row.displayName || '').trim();
      if (name) seedEntries.set(name, row);
    });

    const out = {};
    seedEntries.forEach((registryRow, name) => {
      const key = name.toLowerCase();
      const fromGrid = strategies.find((s) => String(s.strategy || '').trim().toLowerCase() === key);
      const row = registryRow || findRegistryRowForFuturesDataName(registryScenarioBySlug, name);
      const displayLabel = resolveFuturesDataNameDisplayLabel(
        name,
        registryScenarioBySlug,
        basketDisplayEnabled?.labels,
      );
      const label = displayLabel.toLowerCase() !== key ? displayLabel : null;

      const equityReturns = row
        ? buildRegistryFuturesBlendEquityReturns({
          registryRow: row,
          equityStrategies,
          rawStrategyData,
          capitalBase,
        })
        : null;

      if (!equityReturns) {
        const strat = row
          ? findEquityStrategyForRegistry(row, equityStrategies)
          : findEquityStrategyByName(equityStrategies, name);
        if (strat) {
          const series = buildTradeJournalScenarioSeries(strat, {}, capitalBase);
          const derived = deriveFixedNotionalReturns(series);
          if (Array.isArray(derived) && derived.length) {
            out[name] = {
              strategy: name,
              displayLabel: label,
              statsByDay: fromGrid?.statsByDay || { Overall: {} },
              rawData: fromGrid?.rawData || [],
              success: true,
              usesEquityReturnsForBlend: true,
              equityReturns: derived,
            };
            return;
          }
          const fallbackReturns = Array.isArray(strat?.points) && strat.points.length >= 2
            ? deriveFixedNotionalReturns(strat.points, (p) => p?.equity)
            : strat?.returns;
          if (Array.isArray(fallbackReturns) && fallbackReturns.length) {
            out[name] = {
              strategy: name,
              displayLabel: label,
              statsByDay: fromGrid?.statsByDay || { Overall: {} },
              rawData: fromGrid?.rawData || [],
              success: true,
              usesEquityReturnsForBlend: true,
              equityReturns: fallbackReturns,
            };
            return;
          }
        }
      }

      if (Array.isArray(equityReturns) && equityReturns.length) {
        out[name] = {
          strategy: name,
          displayLabel: label,
          statsByDay: fromGrid?.statsByDay || { Overall: {} },
          rawData: fromGrid?.rawData || [],
          success: true,
          usesEquityReturnsForBlend: true,
          equityReturns,
        };
        return;
      }

      if (fromGrid) {
        out[name] = label ? { ...fromGrid, displayLabel: label } : fromGrid;
      }
    });
    return out;
  }, [
    strategies,
    equityStrategies,
    registryScenarioBySlug,
    rawStrategyData,
    capitalBase,
    basketDisplayEnabled?.labels,
  ]);

  const masterAccountBasketStrategy = journalBackedFuturesByName[MASTER_ACCOUNT_STRATEGY_NAME] || null;

  // Custom basket futures: every strategy flagged "Custom" in admin settings.
  const basketFuturesStrategies = useMemo(() => {
    if (!showEquityPortfolios) return strategies;
    // Individual stock algos ("Vector Capital — TICKER (1H Algo)") are dual-stored
    // as futures-grid snapshots, so they live in `strategies` too. They belong to
    // the Stocks side (equity algos) — never the Futures basket — so exclude them
    // here regardless of any stale/default futures rows that might reference them.
    const futuresStrategies = strategies.filter((s) => !isVectorCapitalStockAlgo(s?.strategy));
    const customFuturesSet = basketDisplayEnabled?.custom?.futures
      ? new Set(basketDisplayEnabled.custom.futures)
      : null;
    const includeMasterAccount = scenarioPillOrder.includes('master_multi_account')
      && registryScenarioBySlug.get('master_multi_account')?.showInCustom !== false;

    // Swap in journal-backed objects (MMA, VC Futures) and force-include every
    // journal-backed registry FUTURES strategy that allows Custom — they are
    // first-class basket entries regardless of the sheet-strategy custom gate.
    const finalizeFuturesList = (list) => {
      const upgraded = list.map((s) => journalBackedFuturesByName[s.strategy] || s);
      registryScenarioBySlug.forEach((row) => {
        if (String(row.category || '').toLowerCase() !== 'futures') return;
        if (row.showInCustom === false) return;
        const name = String(row.dataSourceConfig?.strategyName || row.displayName || '').trim();
        const obj = journalBackedFuturesByName[name];
        if (!obj) return;
        const present = upgraded.some(
          (s) => String(s.strategy || '').trim().toLowerCase() === name.toLowerCase(),
        );
        if (!present) upgraded.unshift(obj);
      });
      return upgraded;
    };

    if (customFuturesSet?.size) {
      let list = filterFuturesBasketItems(futuresStrategies, [...customFuturesSet]);
      if (includeMasterAccount && masterAccountBasketStrategy) {
        const hasMaster = list.some(
          (s) => String(s.strategy || '').trim().toLowerCase() === MASTER_ACCOUNT_STRATEGY_NAME.toLowerCase(),
        );
        if (!hasMaster) list = [masterAccountBasketStrategy, ...list];
      }
      return finalizeFuturesList(list);
    }

    const futuresAllowedInCustom = (name) => !customFuturesSet || customFuturesSet.has(name);
    const allowedFutures = new Set();
    scenarioPillOrder.forEach((id) => {
      const registrySlug = parseRegistryScenarioId(id);
      if (registrySlug) {
        const row = registryScenarioBySlug.get(registrySlug);
        if (row && String(row.category || '').toLowerCase() === 'futures' && row.showInCustom !== false) {
          const cfg = row.dataSourceConfig || {};
          const name = String(cfg.strategyName || row.displayName || '').trim();
          if (name && futuresAllowedInCustom(name)) allowedFutures.add(name);
        }
        return;
      }
      const dynamic = parseDynamicScenarioId(id);
      if (dynamic?.kind === 'futures' && futuresAllowedInCustom(dynamic.name)) {
        allowedFutures.add(dynamic.name);
      }
    });
    (enabledRegistryScenarios || []).forEach((row) => {
      if (String(row.category || '').toLowerCase() !== 'futures') return;
      if (row.showInCustom === false) return;
      const cfg = row.dataSourceConfig || {};
      const name = String(cfg.strategyName || row.displayName || '').trim();
      if (name && futuresAllowedInCustom(name)) allowedFutures.add(name);
    });
    const matchesAllowed = (strategy) => {
      const name = String(strategy?.strategy || '').trim();
      if (includeMasterAccount && name.toLowerCase() === MASTER_ACCOUNT_STRATEGY_NAME.toLowerCase()) {
        return true;
      }
      return allowedFutures.has(name);
    };
    if (allowedFutures.size || includeMasterAccount) {
      let list = futuresStrategies.filter(matchesAllowed);
      if (includeMasterAccount && masterAccountBasketStrategy) {
        const hasMaster = list.some(
          (s) => String(s.strategy || '').trim().toLowerCase() === MASTER_ACCOUNT_STRATEGY_NAME.toLowerCase(),
        );
        if (!hasMaster) list = [masterAccountBasketStrategy, ...list];
      }
      return finalizeFuturesList(list);
    }
    const futuresForBasket = basketDisplayEnabled?.futures;
    if (futuresForBasket?.length) {
      return finalizeFuturesList(filterFuturesBasketItems(futuresStrategies, futuresForBasket));
    }
    const normalize = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const allowed = new Set([
      'primebeef',
      'beefbowl',
      'grassfedprimebeefofficialsettings',
      'grassfedprimebeefelite',
      'grassfedprimebeefprecision',
      'grassfedprimebeefpro',
    ]);
    return finalizeFuturesList(futuresStrategies.filter((s) => allowed.has(normalize(s.strategy))));
  }, [
    strategies,
    showEquityPortfolios,
    scenarioPillOrder,
    basketDisplayEnabled,
    masterAccountBasketStrategy,
    journalBackedFuturesByName,
    registryScenarioBySlug,
    enabledRegistryScenarios,
  ]);

  const allocatedEquityStrategies = useMemo(
    () => equityStrategiesForBasket
      .map((s) => ({ strategy: s, weightPct: parseWeightPct(equityWeightByName[s.name]) }))
      .filter((e) => e.weightPct > 0 && Array.isArray(e.strategy?.returns) && e.strategy.returns.length),
    [equityStrategiesForBasket, equityWeightByName],
  );
  const totalEquityWeightPct = useMemo(
    () => equityStrategiesForBasket.reduce((sum, s) => sum + parseWeightPct(equityWeightByName[s.name]), 0),
    [equityStrategiesForBasket, equityWeightByName],
  );

  // Toggle a strategy in/out of the selection. At least one pill must stay on
  // so the overview never renders with zero strategies.
  const toggleScenario = useCallback((id) => {
    setSelectedScenarioIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size <= 1) return prev;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Drop selections for strategies that were disabled in Settings — but only
  // once the pill catalogs have actually loaded. On first render the basket
  // display + registry scenarios are still fetching, so the pill order only
  // contains the static presets; pruning against it then wiped the RESTORED
  // localStorage selection (e.g. a registry pill like MW2) back to the default
  // and immediately persisted the wipe — which is why the dashboard "forgot"
  // its settings on every refresh.
  const pillCatalogsLoaded = !showEquityPortfolios
    || (basketDisplayEnabled != null && enabledRegistryScenarios != null);
  useEffect(() => {
    if (!pillCatalogsLoaded || !scenarioPillOrder.length) return;
    setSelectedScenarioIds((prev) => {
      const next = normalizeScenarioSelection(prev, scenarioPillOrder, defaultScenarioId);
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [scenarioPillOrder, defaultScenarioId, pillCatalogsLoaded]);

  // Any allocation edit is an edit to the Custom strategy — make sure its curve
  // and column are shown.
  const markCustomEdit = useCallback(() => {
    setSelectedScenarioIds((prev) => (prev.has('custom') ? prev : new Set(prev).add('custom')));
  }, []);

  const updateEquityWeight = useCallback((name, raw) => {
    markCustomEdit();
    setEquityWeightByName((prev) => ({ ...prev, [name]: raw }));
  }, [markCustomEdit]);

  const normalizeEquityWeight = useCallback((name) => {
    setEquityWeightByName((prev) => {
      const cur = prev[name];
      const n = parseFloat(String(cur ?? '').trim());
      const next = cur === '' || !Number.isFinite(n) || n < 0 ? '' : String(Math.min(n, 1000));
      return { ...prev, [name]: next };
    });
  }, []);

  const clearAllAllocations = useCallback(() => {
    setEquityWeightByName({});
    setStrategyScenarioConfigByName((prev) => {
      const next = {};
      Object.entries(prev).forEach(([name, cfg]) => {
        next[name] = { ...cfg, weightPct: '' };
      });
      return next;
    });
  }, []);

  useEffect(() => {
    setSelectedBenchmarkIds((prev) => {
      if (!prev.has('GC') && !prev.has('CL')) return prev;
      const next = new Set(prev);
      next.delete('GC');
      next.delete('CL');
      if (next.size === 0) next.add('SPY');
      return next;
    });
  }, []);

  useEffect(() => {
    if (!user?.email) return undefined;
    let cancelled = false;

    const loadQuotes = async () => {
      try {
        const response = await fetch(
          API_ENDPOINTS.marketQuotes({ symbols: marketOptions.map((option) => option.id) }),
          { headers: getBearerAuthHeaders() },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to load market quotes.');
        }
        if (!cancelled) {
          setMarketQuotes(Array.isArray(data.markets) ? data.markets : []);
        }
      } catch (_error) {
        if (!cancelled) {
          setMarketQuotes([]);
        }
      }
    };

    loadQuotes();
    const id = window.setInterval(loadQuotes, QUOTE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [user?.email, marketOptions]);

  const isCustomCapitalActive = useMemo(() => {
    const cleaned = String(customCapitalRaw || '').replace(/[$,\s]/g, '').trim();
    if (cleaned === '') return false;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) && n > 0;
  }, [customCapitalRaw]);

  const availableStrategyNames = useMemo(
    () => strategies.map((strategy) => strategy.strategy),
    [strategies],
  );

  const presetPrograms = useMemo(() => {
    const base = buildPresetPrograms(availableStrategyNames, capitalBase);
    // VC+ only: every preset also runs Grass Fed Prime Beef — Official Settings
    // on Mon/Wed/Fri, using each preset's contract recipe. Resolved by pattern
    // (robust to punctuation); skipped if the strategy isn't in this account.
    const gfpbName = availableStrategyNames.find(
      (n) => /grass\s*fed\s*prime\s*beef/i.test(n) && /official/i.test(n),
    );
    if (!gfpbName) return base;
    const gfpbDefaults = getStrategyScenarioDefaults(gfpbName);
    const augmented = {};
    Object.entries(base).forEach(([presetId, sleeves]) => {
      if (sleeves.some((s) => s.strategyName === gfpbName)) {
        augmented[presetId] = sleeves;
        return;
      }
      const recipe = getVectorPresetRecipe(presetId, capitalBase);
      augmented[presetId] = [
        {
          strategyName: gfpbName,
          baseInstrument: gfpbDefaults.baseInstrument,
          executionInstrument: recipe.executionInstrument,
          activeDays: ['monday', 'wednesday', 'friday'],
          baseUnits: 1,
          sizeMultiplier: String(recipe.sizeMultiplier),
        },
        ...sleeves,
      ];
    });
    return augmented;
  }, [availableStrategyNames, capitalBase]);
  const selectedPresetMeta = getPresetMeta(selectedPreset);

  // Compact composition line for the active (primary) preset's "See more" panel.
  const presetComposition = useMemo(() => {
    if (equityPortfolioPreset) {
      return equityPortfolioLegs.map((leg) => {
        const alloc = Math.round(Number(leg.weightPct) || 0);
        const eff = Math.round((Number(leg.weightPct) || 0) * equityPortfolioPreset.leverage);
        return `${leg.ticker} · ${alloc}% · ${equityPortfolioPreset.leverage}× = ${eff}% eff${leg.found ? '' : ' (missing)'}`;
      });
    }
    if (selectedPreset === 'master_multi_account') {
      return ['Master Multi Account · combined trade journal (NQ)'];
    }
    if (selectedPreset === 'vector_capital_journal') {
      return ['Vector Capital · account trade journal (NQ/MNQ)'];
    }
    if (selectedPreset === 'vector_capital') {
      return (FUTURES_SCENARIO_SLEEVES.vector_capital || []).map((sl) => sl.strategyName);
    }
    if (selectedPreset === 'vc_core_standard' || selectedPreset === 'vc_core_aggressive') {
      return [`${getPresetMeta(selectedPreset).label} · 100% equity curve`];
    }
    if (VECTOR_PRESET_ORDER.includes(selectedPreset)) {
      const recipe = getVectorPresetRecipe(selectedPreset, capitalBase);
      return [`${recipe.displayLabel} per sleeve`, 'Grass Fed Prime Beef', 'Tuesday Special', 'Thursday Special'];
    }
    return [];
  }, [equityPortfolioPreset, equityPortfolioLegs, selectedPreset, capitalBase]);

  // Presets no longer overwrite the grid — each preset's curve is computed
  // independently in buildScenarioSeries below, so the Custom grid stays the
  // user's own allocation at all times.

  const strategyObjectByName = useMemo(() => {
    const map = Object.fromEntries(strategies.map((strategy) => [strategy.strategy, strategy]));
    Object.entries(journalBackedFuturesByName).forEach(([name, obj]) => {
      map[name] = obj;
    });
    return map;
  }, [strategies, journalBackedFuturesByName]);

  // A futures strategy is "in" purely by having a positive allocation — no
  // separate selection toggle (mirrors how stocks work).
  const selectedStrategySelections = useMemo(() => {
    return basketFuturesStrategies
      .map((s) => s.strategy)
      .filter((strategyName) => parseWeightPct(strategyScenarioConfigByName[strategyName]?.weightPct) > 0)
      .map((strategyName) => {
        const defaults = getStrategyScenarioDefaults(strategyName);
        const config = strategyScenarioConfigByName[strategyName] || {};
        const executionInstrument = defaults.executionOptions.some((option) => option.id === config.executionInstrument)
          ? config.executionInstrument
          : defaults.defaultExecutionInstrument;
        const activeDays = config.activeDays?.length
          ? config.activeDays.filter((day) => VC_DAY_SEQUENCE.includes(day))
          : defaults.defaultActiveDays;
        const baseUnits = Number.isFinite(Number(config.baseUnits)) && Number(config.baseUnits) > 0
          ? Number(config.baseUnits)
          : 1;
        const sizeMultiplier = parseProgramSizeMultiplier(config.sizeMultiplier);
        /** Allow '' while editing (e.g. user backspaced); do not coerce to '1' until blur. */
        const sizeMultiplierInput =
          config.sizeMultiplier === ''
            ? ''
            : config.sizeMultiplier != null && String(config.sizeMultiplier).trim() !== ''
              ? String(config.sizeMultiplier)
              : '1';
        const executionRatio = getExecutionRatio(defaults.baseInstrument, executionInstrument);
        const weightPct = parseWeightPct(config.weightPct);
        const weightPctInput =
          config.weightPct === ''
            ? ''
            : config.weightPct != null && String(config.weightPct).trim() !== ''
              ? String(config.weightPct)
              : '';
        return {
          strategyName,
          strategyObject: strategyObjectByName[strategyName] || null,
          baseInstrument: defaults.baseInstrument,
          executionOptions: defaults.executionOptions,
          executionInstrument,
          activeDays,
          baseUnits,
          sizeMultiplier,
          sizeMultiplierInput,
          effectiveUnits: baseUnits * sizeMultiplier,
          executionRatio,
          weightPct,
          weightPctInput,
        };
      })
      .filter((entry) => {
        const obj = entry.strategyObject;
        if (obj?.usesEquityReturnsForBlend && Array.isArray(obj.equityReturns) && obj.equityReturns.length) {
          return true;
        }
        return Boolean(obj?.rawData?.length >= 3);
      });
  }, [basketFuturesStrategies, strategyScenarioConfigByName, strategyObjectByName]);

  // Total allocated % across basket strategies + equity strategies, and the
  // capital left to allocate. This drives the running tally at the top of the
  // basket and decides whether the weighted-blend strategy is active.
  const totalStrategyWeightPct = useMemo(
    () => selectedStrategySelections.reduce((sum, s) => sum + s.weightPct, 0),
    [selectedStrategySelections],
  );
  const totalAllocatedPct = totalStrategyWeightPct + totalEquityWeightPct;
  const allocationActive = totalAllocatedPct > 0;
  const remainingPct = 100 - totalAllocatedPct;
  const remainingDollars = capitalBase * (remainingPct / 100);
  const allocatedDollars = capitalBase * (Math.min(totalAllocatedPct, 100) / 100);


  const customScenarioSeries = useMemo(() => {
    // The Custom scenario: percentage-weighted blend of the live grid (futures
    // sleeves + equity strategies). Weights are % of the total capital base; the
    // unallocated remainder stays in cash.
    if (allocationActive) {
      const { startDate, endDate } = getDateRange(dateRangeFilter, customStartDate, customEndDate);
      const startIso = resolveOverviewStartIso(dateToIsoKey(startDate), benchmarkSettings.historyStartDate);
      const endIso = dateToIsoKey(endDate);
      const inRange = (returns) => (Array.isArray(returns) ? returns : []).filter((p) => {
        if (startIso && p.date < startIso) return false;
        if (endIso && p.date > endIso) return false;
        return true;
      });

      const blend = [];
      selectedStrategySelections
        .filter((s) => s.weightPct > 0)
        .forEach((s) => {
          const obj = s.strategyObject;
          if (obj?.usesEquityReturnsForBlend && Array.isArray(obj.equityReturns) && obj.equityReturns.length) {
            blend.push({ returns: inRange(obj.equityReturns), weightPct: s.weightPct, exposure: 1, compound: false });
            return;
          }
          const rows = combineScenarioSleeveRows(
            [{ strategyName: s.strategyName, activeDays: s.activeDays, units: 1, executionRatio: 1 }],
            rawStrategyData,
          );
          const series = buildPortfolioDailySeries(rows, 1, 1, STRATEGY_AREA_NOTIONAL);
          // Effective size × execution ratio = NQ-equivalent leverage on this
          // sleeve's return path (1x NQ = 1.0, 2x MNQ = 0.2, 5x MNQ = 0.5, …).
          // This is what differentiates the Conservative/Balanced/Aggressive presets.
          const lev = (s.effectiveUnits > 0 ? s.effectiveUnits : 1) * (s.executionRatio > 0 ? s.executionRatio : 1);
          blend.push({ returns: inRange(deriveFixedNotionalReturns(series)), weightPct: s.weightPct, exposure: lev, compound: false });
        });
      allocatedEquityStrategies.forEach((e) => {
        blend.push({
          returns: inRange(blendReturnsForEquityStrategy(e.strategy)),
          weightPct: e.weightPct,
          exposure: 1,
          compound: isCompoundEquityStrategy(e.strategy),
        });
      });

      return buildMultiBucketDailySeries(
        customBlendStartBasis === 'common_start' ? clampBlendToCommonStart(blend) : blend,
        capitalBase,
        { rebalance: REBALANCE_MODE, renormalizeStarts: customBlendStartBasis === 'renormalized' },
      );
    }
    // Nothing allocated → no strategy at all. Returning an empty series clears
    // the chart AND the benchmark comparison (benchmarks only load when there's
    // a scenario), so selecting a strategy at 0% doesn't drive any numbers.
    return [];
  }, [allocationActive, selectedStrategySelections, allocatedEquityStrategies, rawStrategyData, capitalBase, dateRangeFilter, customStartDate, customEndDate, customBlendStartBasis, blendReturnsForEquityStrategy, isCompoundEquityStrategy, benchmarkSettings.historyStartDate]);

  /** Custom blend without date-range clipping — for calendar / trades / monthly panels. */
  const customScenarioSeriesFull = useMemo(() => {
    if (!allocationActive) return [];
    const blend = [];
    selectedStrategySelections
      .filter((s) => s.weightPct > 0)
      .forEach((s) => {
        const obj = s.strategyObject;
        if (obj?.usesEquityReturnsForBlend && Array.isArray(obj.equityReturns) && obj.equityReturns.length) {
          blend.push({ returns: obj.equityReturns, weightPct: s.weightPct, exposure: 1, compound: false });
          return;
        }
        const rows = combineScenarioSleeveRows(
          [{ strategyName: s.strategyName, activeDays: s.activeDays, units: 1, executionRatio: 1 }],
          rawStrategyData,
        );
        const series = buildPortfolioDailySeries(rows, 1, 1, STRATEGY_AREA_NOTIONAL);
        const lev = (s.effectiveUnits > 0 ? s.effectiveUnits : 1) * (s.executionRatio > 0 ? s.executionRatio : 1);
        blend.push({ returns: deriveFixedNotionalReturns(series), weightPct: s.weightPct, exposure: lev, compound: false });
      });
    allocatedEquityStrategies.forEach((e) => {
      blend.push({
        returns: blendReturnsForEquityStrategy(e.strategy),
        weightPct: e.weightPct,
        exposure: 1,
        compound: isCompoundEquityStrategy(e.strategy),
      });
    });
    return buildMultiBucketDailySeries(
      customBlendStartBasis === 'common_start' ? clampBlendToCommonStart(blend) : blend,
      capitalBase,
      { rebalance: REBALANCE_MODE, renormalizeStarts: customBlendStartBasis === 'renormalized' },
    );
  }, [allocationActive, selectedStrategySelections, allocatedEquityStrategies, rawStrategyData, capitalBase, customBlendStartBasis, blendReturnsForEquityStrategy, isCompoundEquityStrategy]);

  const activeDateRangeIso = useMemo(() => {
    const { startDate, endDate } = getDateRange(dateRangeFilter, customStartDate, customEndDate);
    return { startIso: dateToIsoKey(startDate), endIso: dateToIsoKey(endDate) };
  }, [dateRangeFilter, customStartDate, customEndDate]);

  const effectiveOverviewStartIso = useMemo(
    () => resolveOverviewStartIso(activeDateRangeIso.startIso, benchmarkSettings.historyStartDate),
    [activeDateRangeIso.startIso, benchmarkSettings.historyStartDate],
  );

  const filterSeriesForChart = useCallback((series) => (
    filterSeriesByDateRange(series || [], effectiveOverviewStartIso, activeDateRangeIso.endIso)
  ), [effectiveOverviewStartIso, activeDateRangeIso.endIso]);

  /** Benchmark/chart series rebased to period start (fixes all-time anchor skew). */
  const rebaseFilteredSeriesForMetrics = useCallback((series) => (
    rebaseSeriesToPeriodStart(filterSeriesForChart(series), capitalBase)
  ), [filterSeriesForChart, capitalBase]);

  // Compute the daily series for ANY strategy id, independent of the live grid.
  // Custom = the grid blend; the equity portfolios = their synthetic curve at
  // 100%; the futures presets = their recipe sleeves blended equal-weight.
  // applyDateRange=false → full history for calendar / monthly / trades (Fyntrak parity).
  const buildScenarioSeries = useCallback((id, applyDateRange = true) => {
    if (id === 'custom') return applyDateRange ? customScenarioSeries : customScenarioSeriesFull;
    let effectiveId = normalizeScenarioId(id);
    const registrySlug = parseRegistryScenarioId(effectiveId);
    const registryRow = registrySlug ? registryScenarioBySlug.get(registrySlug) : null;
    if (registryRow?.dataSourceType === 'portfolio_blend') {
      effectiveId = registryRow.dataSourceConfig?.presetId || registrySlug;
    }
    let startIso = null;
    let endIso = null;
    if (applyDateRange) {
      const { startDate, endDate } = getDateRange(dateRangeFilter, customStartDate, customEndDate);
      startIso = resolveOverviewStartIso(dateToIsoKey(startDate), benchmarkSettings.historyStartDate);
      endIso = dateToIsoKey(endDate);
    }
    // Live / Backtested: a strategy with a live date keeps only that side of it
    // (inside the date range above). Full Journey and strategies without a date: all of it.
    const liveSince = normalizeLiveSince(registryRow?.dataSourceConfig?.liveSince);
    if (liveSince && journeyView === 'live' && (!startIso || startIso < liveSince)) startIso = liveSince;
    if (liveSince && journeyView === 'backtested') {
      const lastBacktest = dayBeforeIso(liveSince);
      if (!endIso || endIso > lastBacktest) endIso = lastBacktest;
    }
    const inRange = (returns) => {
      const arr = Array.isArray(returns) ? returns : [];
      if (!startIso && !endIso) return arr;
      return arr.filter((p) => {
        if (startIso && p.date < startIso) return false;
        if (endIso && p.date > endIso) return false;
        return true;
      });
    };

    if (registryRow && registryRow.dataSourceType !== 'portfolio_blend') {
      const config = registryRow.dataSourceConfig || {};
      const strategyName = String(config.strategyName || registryRow.displayName || '').trim();
      if (registryRow.dataSourceType === 'futures_snapshot') {
        // Journal-backed strategies (e.g. MW2): render the fixed-notional
        // journal curve, exactly like their pre-registry pill. The sheet-sim
        // path below (buildPortfolioDailySeries) compounds position size with
        // account growth by design — routing a journal strategy through it
        // turned MW2's +220% journal into +600% the moment its NT8 mapping
        // provisioned a futures_snapshot scenario row.
        //
        // Only when the journal actually COVERS the sheet history, though: a
        // strategy with a full sheet grid but only a few recent journal rows
        // (e.g. GFPB Pro's webhook-era trades) must keep the sheet sim, or its
        // chart would collapse to the journal's short window.
        const journalTrades = resolveBestTradesForScenario({
          registryRow,
          strategyName,
          equityStrategies,
        });
        const gridForCoverage = rawStrategyData?.[strategyName];
        const gridFirstLabel = Array.isArray(gridForCoverage)
          ? gridForCoverage.slice(2).map((r) => r?.[0])
            .find((v) => /(\d{1,2})\.(\d{1,2})\.(\d{4})/.test(String(v || '')))
          : null;
        const gridFirstDate = gridFirstLabel ? parseDateFromRowLabel(gridFirstLabel) : null;
        const journalFirstIso = (journalTrades || []).reduce((min, t) => {
          const d = String(t?.trade_date || '').slice(0, 10);
          if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return min;
          return !min || d < min ? d : min;
        }, null);
        const journalCoversHistory = Boolean(journalTrades && journalFirstIso) && (
          !gridFirstDate
          || (() => {
            // Grid rows are labeled by week-start Monday — allow the journal's
            // first trade to land anywhere inside that first week.
            const weekEnd = new Date(gridFirstDate);
            weekEnd.setDate(weekEnd.getDate() + 6);
            return new Date(`${journalFirstIso}T12:00:00Z`) <= weekEnd;
          })()
        );
        if (journalCoversHistory) {
          return buildTradeJournalScenarioSeries(
            { trades: journalTrades },
            { startDate: startIso, endDate: endIso },
            capitalBase,
          );
        }
        const rows = combineScenarioSleeveRows(
          [{ strategyName, activeDays: VC_DAY_SEQUENCE, units: 1, executionRatio: 1 }],
          rawStrategyData,
        );
        const series = buildPortfolioDailySeries(rows, 1, 1, STRATEGY_AREA_NOTIONAL);
        return buildMultiBucketDailySeries(
          [{ returns: inRange(deriveFixedNotionalReturns(series)), weightPct: 100, exposure: 1, compound: false }],
          capitalBase,
          { rebalance: REBALANCE_MODE },
        );
      }
      if (registryRow.dataSourceType === 'equity_curve' || registryRow.dataSourceType === 'trade_journal') {
        const strat = findEquityStrategyForRegistry(registryRow, equityStrategies);
        if (registryRow.dataSourceType === 'trade_journal') {
          return buildTradeJournalScenarioSeries(
            strat,
            { startDate: startIso, endDate: endIso },
            capitalBase,
          );
        }
        if (Array.isArray(strat?.points) && strat.points.length >= 2) {
          const compoundMonthly = registryScenarioUsesCompoundEquity(registryRow);
          return buildEquityCurveWindowSeries(
            strat.points,
            { startDate: startIso, endDate: endIso },
            capitalBase,
            compoundMonthly,
          );
        }
        if (Array.isArray(strat?.returns) && strat.returns.length) {
          return buildMultiBucketDailySeries(
            [{ returns: inRange(strat.returns), weightPct: 100, exposure: 1 }],
            capitalBase,
            { rebalance: REBALANCE_MODE },
          );
        }
        return [];
      }
      if (registryRow.dataSourceType === 'webhook') {
        const parser = String(config.parser || '');
        if (parser === 'tradingview_orders' || parser === 'nt8_trade_alert') {
          // Exact matches first (slug, then configured name) — the loose
          // isNt8Test/isOrderTest fallback can grab a DIFFERENT overlay
          // strategy when several are loaded.
          const wantedName = String(strategyName || '').trim().toLowerCase();
          const strat = equityStrategies.find((s) => s?.scenarioSlug === registrySlug)
            || equityStrategies.find((s) => String(s?.name || '').trim().toLowerCase() === wantedName)
            || equityStrategies.find((s) => {
              if (parser === 'nt8_trade_alert') {
                return s?.isNt8Test || new RegExp(strategyName, 'i').test(String(s?.name || ''));
              }
              return s?.isOrderTest || new RegExp(strategyName, 'i').test(String(s?.name || ''));
            });
          // These journal/webhook curves are FIXED-NOTIONAL (constant contract
          // size — e.g. MW2's +223% journal). Prefer trade rows when present so
          // chart/metrics match the calendar even if cached equity points are stale.
          if (Array.isArray(strat?.trades) && strat.trades.length) {
            return buildTradeJournalScenarioSeries(
              strat,
              { startDate: startIso, endDate: endIso },
              capitalBase,
            );
          }
          if (Array.isArray(strat?.points) && strat.points.length >= 2) {
            return buildEquityCurveWindowSeries(
              strat.points,
              { startDate: startIso, endDate: endIso },
              capitalBase,
              false,
            );
          }
          const returns = Array.isArray(strat?.returns) ? strat.returns : [];
          if (!returns.length) return [];
          return buildMultiBucketDailySeries(
            [{ returns, weightPct: 100, exposure: 1, compound: false }],
            capitalBase,
            { rebalance: REBALANCE_MODE },
          );
        }
        if (parser === 'kairos_daily') {
          const strat = findEquityStrategyForRegistry(registryRow, equityStrategies)
            || equityStrategies.find(
              (s) => s?.isKairosWebhook
                || new RegExp(strategyName, 'i').test(String(s?.name || '')),
            );
          if (Array.isArray(strat?.points) && strat.points.length >= 2) {
            const compoundMonthly = registryScenarioUsesCompoundEquity(registryRow);
            return buildEquityCurveWindowSeries(
              strat.points,
              { startDate: startIso, endDate: endIso },
              capitalBase,
              compoundMonthly,
            );
          }
          return [];
        }
        const strat = equityStrategies.find(
          (s) => s?.scenarioSlug === registrySlug
            || s?.isWebhookTest
            || new RegExp(strategyName, 'i').test(String(s?.name || '')),
        );
        if (Array.isArray(strat?.points) && strat.points.length >= 2) {
          return buildEquityCurveWindowSeries(
            strat.points,
            { startDate: startIso, endDate: endIso },
            capitalBase,
            registryScenarioUsesCompoundEquity(registryRow),
          );
        }
        return [];
      }
      if (registryRow.dataSourceType === 'nt_connector') {
        const connectionId = String(config.connectionId || '');
        const strat = equityStrategies.find((s) => {
          if (!s?.isNtConnector) return false;
          if (s.scenarioSlug === registrySlug) return true;
          if (connectionId && String(s.connectionId) === connectionId) return true;
          const accountName = String(config.accountName || registryRow.displayName || '').trim();
          return accountName && new RegExp(accountName, 'i').test(String(s?.name || ''));
        });
        if (Array.isArray(strat?.points) && strat.points.length >= 2) {
          return buildEquityCurveWindowSeries(
            strat.points,
            { startDate: startIso, endDate: endIso },
            capitalBase,
          );
        }
        const returns = Array.isArray(strat?.returns) ? strat.returns : [];
        if (!returns.length) return [];
        return buildMultiBucketDailySeries(
          [{ returns: inRange(returns), weightPct: 100, exposure: 1 }],
          capitalBase,
          { rebalance: REBALANCE_MODE },
        );
      }
    }

    if (isEquityPortfolioPreset(effectiveId)) {
      if (effectiveId === 'og_equity_portfolio_1x' || effectiveId === 'og_equity_portfolio_225x') {
        const ogLegs = resolveEquityPortfolioLegs(
          'og_equity_portfolio_1x',
          equityStrategies,
          equityPortfolioConfig?.og_portfolio_1x?.legs,
        ).filter((leg) => leg.found);
        const ogBlended = buildOgEquityPortfolioBlendedSeries(
          FYNTRAK_OG_EQUITY_1X_CURVE,
          ogLegs,
          equityStrategyByName,
          capitalBase,
          true,
          resolveEquityPortfolioMethod(equityPortfolioConfig?.og_portfolio_1x?.method).weighting,
        );
        const ogOneX = buildEquityCurveWindowSeries(
          ogBlended.map((p) => ({ date: p.date, equity: p.normalized })),
          { startDate: startIso, endDate: endIso },
          capitalBase,
          true,
        );
        if (effectiveId === 'og_equity_portfolio_1x' || ogOneX.length < 2) return ogOneX;
        const ogLev = getEquityPortfolioEffectiveLeverage(effectiveId, equityPortfolioConfig);
        if (ogLev === 1) return ogOneX;
        const ogLevMode = resolveEquityPortfolioLeverageMode(
          equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET[effectiveId]]?.leverageMode,
        );
        return ogLevMode.id === 'compounded'
          ? applyCompoundLeverageToSeries(ogOneX, ogLev, capitalBase)
          : applyLinearLeverageToSeries(ogOneX, ogLev, ogOneX, capitalBase);
      }
      const legs = resolveEquityPortfolioLegs(
        effectiveId,
        equityStrategies,
        equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET[effectiveId]]?.legs,
      ).filter((leg) => leg.found);
      if (!legs.length) return [];
      const method = resolveEquityPortfolioMethod(
        equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET[effectiveId]]?.method,
      );
      const effLeverage = getEquityPortfolioEffectiveLeverage(effectiveId, equityPortfolioConfig);
      const leverageMode = resolveEquityPortfolioLeverageMode(
        equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET[effectiveId]]?.leverageMode,
      );
      // Fyntrak-linear leverage: build the UNLEVERED (1×) curve, then re-scale it
      // so its CAGR = leverage × the 1× CAGR (matches Fyntrak's softer 2.25×).
      // Fyntrak recomputes the 1× annualized ON THE SELECTED SLICE and scales that
      // by the leverage — so the exponent must come from the SAME windowed slice
      // being shown (not the full curve), or 1Y/YTD drift while All still matches.
      // The 1× books (effLeverage === 1) are identical either way → skip the scaling.
      if (leverageMode.id === 'linear' && effLeverage !== 1) {
        const oneXSlice = buildEquityPortfolioSeries(
          legs, 1, equityStrategyByName,
          { startDate: startIso, endDate: endIso },
          capitalBase, applyDateRange && method.compounded, method.weighting,
        );
        return applyLinearLeverageToSeries(oneXSlice, effLeverage, oneXSlice, capitalBase);
      }
      return buildEquityPortfolioSeries(
        legs,
        effLeverage,
        equityStrategyByName,
        { startDate: startIso, endDate: endIso },
        capitalBase,
        applyDateRange && method.compounded,
        method.weighting,
      );
    }

    // BTC Test = live TradingView-feed curve; show the FULL curve (no range
    // filter) so intraday points always render while testing.
    if (!registryRow && effectiveId === 'btc_test') {
      const strat = equityStrategies.find((s) => s?.isOrderTest || /btc test/i.test(String(s?.name || '')));
      const returns = Array.isArray(strat?.returns) ? strat.returns : [];
      if (!returns.length) return [];
      return buildMultiBucketDailySeries(
        [{ returns, weightPct: 100, exposure: 1 }],
        capitalBase,
        { rebalance: REBALANCE_MODE },
      );
    }

    // Jean Test = dev-only NT8 connector feed (same full-curve behavior as BTC Test).
    if (!registryRow && effectiveId === 'jean_test') {
      const strat = equityStrategies.find((s) => s?.isNt8Test || /jean test/i.test(String(s?.name || '')));
      const returns = Array.isArray(strat?.returns) ? strat.returns : [];
      if (!returns.length) return [];
      return buildMultiBucketDailySeries(
        [{ returns, weightPct: 100, exposure: 1 }],
        capitalBase,
        { rebalance: REBALANCE_MODE },
      );
    }

    // MMA Test = the live webhook-feed test curve served by the API, ridden 100%.
    if (!registryRow && effectiveId === 'mma_test') {
      const strat = equityStrategies.find((s) => s?.isWebhookTest || /webhook test/i.test(String(s?.name || '')));
      if (!Array.isArray(strat?.points) || strat.points.length < 2) return [];
      return buildEquityCurveWindowSeries(strat.points, { startDate: startIso, endDate: endIso }, capitalBase);
    }

    // Kairos Equity Standard / Aggressive = their own equity curves (the renamed
    // QLD / TQQQ equity strategies), ridden 100%.
    if (!registryRow && (effectiveId === 'vc_core_standard' || effectiveId === 'vc_core_aggressive')) {
      const ticker = effectiveId === 'vc_core_standard' ? 'qld' : 'tqqq';
      const strat = equityStrategies.find((s) => String(s?.name || '').toLowerCase().includes(ticker));
      if (!Array.isArray(strat?.points) || strat.points.length < 2) return [];
      // Kairos is a real compounding equity curve → rebase windowed views (YTD/1Y)
      // by ratio so the % move is true, not the $ gain ÷ original $100k.
      return buildEquityCurveWindowSeries(
        strat.points, { startDate: startIso, endDate: endIso }, capitalBase, scenarioUsesCompoundMonthly(effectiveId),
      );
    }

    if (!registryRow && effectiveId === 'master_multi_account') {
      const strat = equityStrategies.find((s) => String(s?.name || '').trim().toLowerCase() === 'master multi account');
      return buildTradeJournalScenarioSeries(
        strat,
        { startDate: startIso, endDate: endIso },
        capitalBase,
      );
    }

    // Vector Capital (Journal) = the Vector_Capital account's executed trade
    // journal, ingested as a $100k fixed-notional equity curve (each trade's net
    // P&L on the running balance). Same model as Master Multi Account.
    if (!registryRow && effectiveId === 'vector_capital_journal') {
      const strat = findEquityStrategyForRegistry(
        { slug: 'vector_capital_journal', dataSourceConfig: { strategyName: 'Vector Capital Journal' } },
        equityStrategies,
      );
      return buildTradeJournalScenarioSeries(
        strat,
        { startDate: startIso, endDate: endIso },
        capitalBase,
      );
    }

    const dynamicScenario = parseDynamicScenarioId(id);
    if (dynamicScenario?.kind === 'equity_algo') {
      const algoName = equityAlgoNameFromKey(dynamicScenario.key, equityStrategies);
      const strat = algoName ? equityStrategies.find((s) => s.name === algoName) : null;
      if (Array.isArray(strat?.points) && strat.points.length >= 2) {
        return buildEquityCurveWindowSeries(strat.points, { startDate: startIso, endDate: endIso }, capitalBase);
      }
      if (Array.isArray(strat?.returns) && strat.returns.length) {
        return buildMultiBucketDailySeries(
          [{ returns: inRange(strat.returns), weightPct: 100, exposure: 1 }],
          capitalBase,
          { rebalance: REBALANCE_MODE },
        );
      }
      return [];
    }
    if (dynamicScenario?.kind === 'futures') {
      const journalStrat = findEquityStrategyByName(equityStrategies, dynamicScenario.name);
      if (journalStrat) {
        const journalSeries = buildTradeJournalScenarioSeries(
          journalStrat,
          { startDate: startIso, endDate: endIso },
          capitalBase,
        );
        if (journalSeries.length >= 2) return journalSeries;
      }
      const rows = combineScenarioSleeveRows(
        [{ strategyName: dynamicScenario.name, activeDays: VC_DAY_SEQUENCE, units: 1, executionRatio: 1 }],
        rawStrategyData,
      );
      const series = buildPortfolioDailySeries(rows, 1, 1, STRATEGY_AREA_NOTIONAL);
      return buildMultiBucketDailySeries(
        [{ returns: inRange(deriveFixedNotionalReturns(series)), weightPct: 100, exposure: 1, compound: false }],
        capitalBase,
        { rebalance: REBALANCE_MODE },
      );
    }

    // Vector Capital = blend of the Vector sheet futures sleeves (live from the
    // Google-sheet sync), equal-weight at 1× NQ.
    if (id === 'vector_capital') {
      const sleeves = FUTURES_SCENARIO_SLEEVES[id] || [];
      const present = sleeves.filter(
        (sl) => Array.isArray(rawStrategyData?.[sl.strategyName]) && rawStrategyData[sl.strategyName].length,
      );
      if (!present.length) return [];
      const equalWeight = 100 / present.length;
      const blend = present.map((sl) => {
        const rows = combineScenarioSleeveRows(
          [{ strategyName: sl.strategyName, activeDays: sl.activeDays, units: 1, executionRatio: 1 }],
          rawStrategyData,
        );
        const series = buildPortfolioDailySeries(rows, 1, 1, STRATEGY_AREA_NOTIONAL);
        return { returns: inRange(deriveFixedNotionalReturns(series)), weightPct: equalWeight, exposure: 1, compound: false };
      });
      return buildMultiBucketDailySeries(blend, capitalBase, { rebalance: REBALANCE_MODE });
    }

    const program = presetPrograms[id] || [];
    if (!program.length) return [];
    const equalWeight = 100 / program.length;
    const blend = program.map((sleeve) => {
      const executionRatio = getExecutionRatio(sleeve.baseInstrument, sleeve.executionInstrument);
      const sizeMult = Number(sleeve.sizeMultiplier) || 1;
      const baseUnits = sleeve.baseUnits > 0 ? sleeve.baseUnits : 1;
      const exposure = baseUnits * sizeMult * executionRatio;
      const rows = combineScenarioSleeveRows(
        [{ strategyName: sleeve.strategyName, activeDays: sleeve.activeDays, units: 1, executionRatio: 1 }],
        rawStrategyData,
      );
      const series = buildPortfolioDailySeries(rows, 1, 1, STRATEGY_AREA_NOTIONAL);
      return { returns: inRange(deriveFixedNotionalReturns(series)), weightPct: equalWeight, exposure, compound: false };
    });
    return buildMultiBucketDailySeries(blend, capitalBase, { rebalance: REBALANCE_MODE });
  }, [customScenarioSeries, customScenarioSeriesFull, dateRangeFilter, customStartDate, customEndDate, equityPortfolioConfig, equityStrategyByName, presetPrograms, rawStrategyData, capitalBase, equityStrategies, registryScenarioBySlug, benchmarkSettings.historyStartDate, journeyView]);

  const scenarioSeriesById = useMemo(() => {
    const out = {};
    orderedSelectedIds.forEach((id) => { out[id] = buildScenarioSeries(id, true); });
    return out;
  }, [orderedSelectedIds, buildScenarioSeries]);

  const scenarioSeriesFullById = useMemo(() => {
    const out = {};
    orderedSelectedIds.forEach((id) => { out[id] = buildScenarioSeries(id, false); });
    return out;
  }, [orderedSelectedIds, buildScenarioSeries]);

  // Actual date span of the selected scenarios' (windowed) series. Benchmarks load
  // from this window start through the benchmark's latest trading day (not capped
  // at the scenario's last date).
  const scenarioUnionRange = useMemo(() => {
    let min = null;
    let max = null;
    orderedSelectedIds.forEach((id) => {
      const series = scenarioSeriesById[id] || [];
      if (!series.length) return;
      const first = series[0].date;
      const last = series[series.length - 1].date;
      if (!min || first < min) min = first;
      if (!max || last > max) max = last;
    });
    return min && max ? { startDate: min, endDate: max } : null;
  }, [orderedSelectedIds, scenarioSeriesById]);

  // Rebase a benchmark to the capital base at the START OF THE SCENARIO'S OWN
  // window — not the global filter start. Without this, a short strategy (e.g. a
  // 6-month journal) shows SPY at its multi-year level: the benchmark would be
  // rebased to $100k back in 2022 and then sliced to 2026, where it's already up
  // ~50%. Clamping to the strategy span first, then rebasing, makes the benchmark
  // start at $100k alongside the strategy. For long strategies the clamp is a no-op.
  const rebaseBenchmarkSeries = useCallback((series) => {
    let s = filterSeriesForChart(series || []);
    const rangeStart = resolveOverviewStartIso(
      scenarioUnionRange?.startDate || null,
      benchmarkSettings.historyStartDate,
    );
    if (rangeStart || scenarioUnionRange?.endDate) {
      const endDate = scenarioUnionRange?.endDate || null;
      s = s.filter((p) => p && (!rangeStart || p.date >= rangeStart) && (!endDate || p.date <= endDate));
    }
    return rebaseSeriesToPeriodStart(s, capitalBase);
  }, [filterSeriesForChart, scenarioUnionRange, capitalBase, benchmarkSettings.historyStartDate]);

  const scenarioSummaryById = useMemo(() => {
    const out = {};
    orderedSelectedIds.forEach((id) => {
      out[id] = summarizePerformance(scenarioSeriesById[id] || [], capitalBase);
    });
    return out;
  }, [orderedSelectedIds, scenarioSeriesById, capitalBase]);

  // Calendar suite: full trade/calendar history — NOT clipped by the user's
  // date-range filter, but still respects the admin history start date floor.
  const calendarSuiteScenarios = useMemo(() => {
    const tradeMeta = {
      master_multi_account: { symbol: 'NQ', positions: 1 },
      mma_test: { symbol: 'MIXED', positions: 1 },
      btc_test: { symbol: 'BTC', positions: 1 },
      jean_test: { symbol: 'TEST', positions: 1 },
      vector_capital: { symbol: 'NQ', positions: 1 },
      vector_capital_journal: { symbol: 'NQ', positions: 1 },
      vc_core_standard: { symbol: 'Kairos', positions: 1 },
      vc_core_aggressive: { symbol: 'Kairos', positions: 1 },
      equity_portfolio_1x: { symbol: 'PORTFOLIO', positions: 5 },
      equity_portfolio_225x: { symbol: 'PORTFOLIO', positions: 5 },
      og_equity_portfolio_1x: { symbol: 'PORTFOLIO', positions: 5 },
      og_equity_portfolio_225x: { symbol: 'PORTFOLIO', positions: 5 },
      conservative: { symbol: 'MNQ', positions: 1 },
      balanced: { symbol: 'MNQ', positions: 1 },
      aggressive: { symbol: 'MNQ', positions: 1 },
    };
    const customPositions = Math.max(
      1,
      selectedStrategySelections.length + allocatedEquityStrategies.length,
    );
    return orderedSelectedIds
      .map((id) => {
        // Registry pills carry prefixed ids (scenario:<slug>) — resolve back to
        // the legacy/preset id so tradeMeta and the per-scenario branches match.
        const registrySlug = parseRegistryScenarioId(normalizeScenarioId(id));
        const registryRow = registrySlug ? registryScenarioBySlug.get(registrySlug) : null;
        const effectiveId = registryRow?.dataSourceType === 'portfolio_blend'
          ? (registryRow.dataSourceConfig?.presetId || registrySlug)
          : (registrySlug || id);
        const dynamic = parseDynamicScenarioId(normalizeScenarioId(id));
        const equityAlgoName = dynamic?.kind === 'equity_algo'
          ? equityAlgoNameFromKey(dynamic.key, individualEquityAlgos)
          : null;
        let dailyStatsByDate;
        let trades;
        // Set when dailyStatsByDate is the source of truth and `trades` is only a
        // display list — see buildDailyCalendarStats in VectorCapitalCalendar.
        let dailyStatsAuthoritative = false;
        if (effectiveId === 'og_equity_portfolio_1x' || effectiveId === 'og_equity_portfolio_225x') {
          dailyStatsByDate = buildDailyStatsFromSeries(
            scenarioSeriesFullById[id] || [],
            tradeMeta[effectiveId]?.positions || 1,
          );
          const ogCalLegs = resolveEquityPortfolioLegs(
            'og_equity_portfolio_1x',
            equityStrategies,
            equityPortfolioConfig?.og_portfolio_1x?.legs,
          ).filter((leg) => leg.found && leg.enabled !== false);
          const ogLegTrades = collectEquityPortfolioLegTrades(ogCalLegs, equityStrategyByName, strategyInstrumentMap);
          if (ogLegTrades.length) trades = ogLegTrades;
          dailyStatsAuthoritative = true;
        } else if (isEquityPortfolioPreset(effectiveId)) {
          const legs = resolveEquityPortfolioLegs(
            effectiveId,
            equityStrategies,
            equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET[effectiveId]]?.legs,
          ).filter((leg) => leg.found && leg.enabled !== false);
          const leverage = getEquityPortfolioEffectiveLeverage(effectiveId, equityPortfolioConfig);
          const calMethod = resolveEquityPortfolioMethod(
            equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET[effectiveId]]?.method,
          );
          const calLeverageMode = resolveEquityPortfolioLeverageMode(
            equityPortfolioConfig?.[PORTFOLIO_KEY_BY_PRESET[effectiveId]]?.leverageMode,
          );
          // Under Fyntrak-linear leverage the levered (2.25×) curve is re-scaled
          // (not a per-day leverage multiply), so the independent fixed-notional
          // calendar can't reproduce it. Derive the calendar straight from the
          // scaled FULL series so its daily $ / % match the chart + metrics exactly.
          // 1× books (leverage 1) are unscaled → keep the rich per-trade calendar.
          if (calLeverageMode.id === 'linear' && leverage !== 1) {
            dailyStatsByDate = buildDailyStatsFromSeries(
              scenarioSeriesFullById[id] || [],
              tradeMeta[effectiveId]?.positions || 1,
            );
          } else {
            dailyStatsByDate = buildEquityPortfolioCalendarStats(
              legs,
              leverage,
              equityStrategyByName,
              100000,
              calMethod.weighting,
            );
          }
          const legTrades = collectEquityPortfolioLegTrades(legs, equityStrategyByName, strategyInstrumentMap);
          if (legTrades.length) trades = legTrades;
        } else if (effectiveId === 'master_multi_account') {
          const journalTrades = resolveJournalTradesForScenario({
            strategyName: MASTER_ACCOUNT_STRATEGY_NAME,
            equityStrategies,
          });
          if (journalTrades) {
            trades = applyInstrumentSymbolsToTrades(journalTrades, {
              instrumentMap: strategyInstrumentMap,
              strategyName: MASTER_ACCOUNT_STRATEGY_NAME,
            });
            dailyStatsByDate = buildDailyStatsFromTrades(journalTrades);
          }
        } else {
          const calendarTrades = resolveBestTradesForScenario({
            registryRow,
            strategyName: dynamic?.kind === 'futures' ? dynamic.name : null,
            equityStrategies,
          });
          if (calendarTrades) {
            const journalStrategyName = resolveInstrumentStrategyName({
              id,
              effectiveId,
              registryRow,
              equityAlgoName,
              equityStrategies,
            });
            trades = applyInstrumentSymbolsToTrades(calendarTrades, {
              instrumentMap: strategyInstrumentMap,
              strategyName: journalStrategyName,
            });
            dailyStatsByDate = buildDailyStatsFromTrades(calendarTrades);
            const fullSeries = scenarioSeriesFullById[id] || [];
            const lastTradeDate = lastTradeIso(calendarTrades);
            const lastSeriesDate = fullSeries.length
              ? String(fullSeries[fullSeries.length - 1]?.date || '').slice(0, 10)
              : '';
            const windowedSeries = calendarWindowedSeries(scenarioSeriesById, id, capitalBase);
            if (lastSeriesDate && lastTradeDate && lastSeriesDate > lastTradeDate) {
              dailyStatsByDate = buildDailyStatsFromSeries(
                windowedSeries.length > 1 ? windowedSeries : fullSeries,
                tradeMeta[effectiveId]?.positions || 1,
              );
              dailyStatsAuthoritative = true;
            } else {
              dailyStatsAuthoritative = true;
            }
          } else if (registryRow && (
            registryRow.dataSourceType === 'futures_snapshot'
            || registryRow.dataSourceType === 'equity_curve'
            || registryRow.dataSourceType === 'webhook'
          )) {
            dailyStatsByDate = buildDailyStatsFromSeries(
              calendarWindowedSeries(scenarioSeriesById, id, capitalBase),
              tradeMeta[effectiveId]?.positions || 1,
            );
            dailyStatsAuthoritative = true;
          }
        }
        const calendarFootnote = (effectiveId === 'og_equity_portfolio_1x' || effectiveId === 'og_equity_portfolio_225x')
          ? 'Calendar dollars follow Fyntrak\u2019s compounded equity curve (account reinvests profits). The Trades tab lists the same legs but on fixed notional \u2014 those rows will not sum to calendar days. See Settings \u2192 Portfolio Settings for details.'
          : (isEquityPortfolioPreset(effectiveId)
            ? 'Calendar dollars use a fixed $100k notional blend from leg trades. The growth chart and Daily Value tab compound on the running balance, so dollar amounts can differ. See Settings \u2192 Portfolio Settings for details.'
            : null);
        const windowedCalendar = calendarWindowedSeries(scenarioSeriesById, id, capitalBase);
        return {
          id,
          label: getScenarioLabel(id),
          // Calendar / List of Trades: history floor + rebase to capital base at floor.
          series: windowedCalendar,
          calendarSeries: windowedCalendar,
          seriesWindowed: windowedCalendar,
          symbol: resolveCalendarScenarioSymbol({
            id,
            effectiveId,
            registryRow,
            instrumentMap: strategyInstrumentMap,
            presetTradeMeta: tradeMeta,
            equityAlgoName,
            equityStrategies,
          }),
          positions: id === 'custom' ? customPositions : (tradeMeta[effectiveId]?.positions || 1),
          trades,
          dailyStatsByDate,
          dailyStatsAuthoritative,
          calendarFootnote,
          compoundMonthly: scenarioCompoundsMonthly(id),
        };
      })
      .filter((s) => s.series.length > 1)
      .map((scenario) => {
        const floor = benchmarkSettings.historyStartDate;
        return {
          ...scenario,
          series: clipSeriesToHistoryStart(scenario.series, floor),
          calendarSeries: clipSeriesToHistoryStart(scenario.calendarSeries, floor),
          seriesWindowed: clipSeriesToHistoryStart(scenario.seriesWindowed, floor),
          trades: scenario.trades
            ? clipTradesToHistoryStart(scenario.trades, floor)
            : scenario.trades,
          dailyStatsByDate: clipDailyStatsToHistoryStart(scenario.dailyStatsByDate, floor),
        };
      })
      .filter((s) => s.series.length > 1);
  }, [
    orderedSelectedIds,
    scenarioSeriesFullById,
    scenarioSeriesById,
    selectedStrategySelections,
    allocatedEquityStrategies,
    equityStrategies,
    equityPortfolioConfig,
    equityStrategyByName,
    getScenarioLabel,
    scenarioCompoundsMonthly,
    registryScenarioBySlug,
    strategyInstrumentMap,
    individualEquityAlgos,
    capitalBase,
    benchmarkSettings.historyStartDate,
  ]);

  /** Filtered strategies for panels that follow the chart date range (Key Insights, Growth Calculator). */
  const chartInsightScenarios = useMemo(
    () => calendarSuiteScenarios.map((s) => ({
      ...s,
      series: scenarioSeriesById[s.id] || [],
    })),
    [calendarSuiteScenarios, scenarioSeriesById],
  );

  // Selected benchmarks that have loaded stats become extra comparison columns.
  const benchmarkColIds = useMemo(
    () => sortedBenchmarkIds.filter((id) => (benchmarkDataById[id]?.series?.length || 0) > 1),
    [sortedBenchmarkIds, benchmarkDataById],
  );

  // Loaded benchmark series for the calendar-suite panels (monthly returns,
  // key insights) — same shape as calendarScenarios.
  const calendarBenchmarks = useMemo(
    () => sortedBenchmarkIds
      .filter((id) => (benchmarkDataById[id]?.series?.length || 0) > 1)
      .map((id) => ({
        id,
        label: `${marketOptions.find((m) => m.id === id)?.label || id} (${benchmarkTicker(id, marketOptions)})`,
        series: benchmarkDataById[id].series,
      })),
    [sortedBenchmarkIds, benchmarkDataById, marketOptions],
  );

  const chartInsightBenchmarks = useMemo(
    () => calendarBenchmarks.map((b) => ({
      ...b,
      series: rebaseFilteredSeriesForMetrics(b.series || []),
    })),
    [calendarBenchmarks, rebaseFilteredSeriesForMetrics],
  );

  // Sharpe + avg daily return, computed client-side with one methodology for
  // both scenarios and benchmarks (the API stats don't include them).
  const scenarioRiskStatsById = useMemo(() => {
    const out = {};
    orderedSelectedIds.forEach((id) => {
      out[id] = computeSeriesRiskStats(scenarioSeriesById[id] || []);
    });
    return out;
  }, [orderedSelectedIds, scenarioSeriesById]);

  const benchmarkRiskStatsById = useMemo(() => {
    const out = {};
    benchmarkColIds.forEach((id) => {
      out[id] = computeSeriesRiskStats(rebaseBenchmarkSeries(benchmarkDataById[id]?.series || []));
    });
    return out;
  }, [benchmarkColIds, benchmarkDataById, rebaseBenchmarkSeries]);

  const fullComparisonYearlyRows = useMemo(() => {
    const scenarioPctById = {};
    const benchmarkPctById = {};
    const yearSet = new Set();

    const floor = benchmarkSettings.historyStartDate;

    orderedSelectedIds.forEach((id) => {
      const byYear = new Map(
        computeYearlyReturnsFromSeries(clipSeriesToHistoryStart(scenarioSeriesById[id] || [], floor))
          .map((row) => [row.yearKey, row.pct]),
      );
      scenarioPctById[id] = byYear;
      byYear.forEach((_pct, yearKey) => yearSet.add(yearKey));
    });

    benchmarkColIds.forEach((id) => {
      const series = clipSeriesToHistoryStart(
        rebaseBenchmarkSeries(benchmarkDataById[id]?.series || []),
        floor,
      );
      const byYear = new Map(
        computeYearlyReturnsFromSeries(series).map((row) => [row.yearKey, row.pct]),
      );
      benchmarkPctById[id] = byYear;
      byYear.forEach((_pct, yearKey) => yearSet.add(yearKey));
    });

    return Array.from(yearSet)
      .filter((yearKey) => isYearOnOrAfterHistoryStart(yearKey, floor))
      .sort((a, b) => a.localeCompare(b))
      .map((yearKey) => ({
        yearKey,
        label: yearKey,
        scenarios: Object.fromEntries(
          orderedSelectedIds.map((id) => [id, scenarioPctById[id]?.get(yearKey) ?? null]),
        ),
        benchmarks: Object.fromEntries(
          benchmarkColIds.map((id) => [id, benchmarkPctById[id]?.get(yearKey) ?? null]),
        ),
      }));
  }, [
    orderedSelectedIds,
    scenarioSeriesById,
    benchmarkColIds,
    benchmarkDataById,
    rebaseBenchmarkSeries,
    benchmarkSettings.historyStartDate,
  ]);

  const benchmarkSummaryById = useMemo(() => {
    const out = {};
    sortedBenchmarkIds.forEach((id) => {
      out[id] = summarizePerformance(
        rebaseBenchmarkSeries(benchmarkDataById[id]?.series || []),
        capitalBase,
      );
    });
    return out;
  }, [sortedBenchmarkIds, benchmarkDataById, rebaseBenchmarkSeries, capitalBase]);

  // The primary strategy feeds the legacy single-value panels + the grid.
  const portfolioSeries = useMemo(
    () => scenarioSeriesById[primaryScenarioId] || [],
    [scenarioSeriesById, primaryScenarioId],
  );

  const portfolioSummary = useMemo(
    () => summarizePerformance(portfolioSeries, capitalBase),
    [portfolioSeries, capitalBase],
  );

  useEffect(() => {
    if (!scenarioUnionRange) {
      setBenchmarkDataById({});
      return;
    }

    const ids = Array.from(selectedBenchmarkIds).sort((a, b) => a.localeCompare(b));
    if (ids.length === 0) {
      // No benchmarks selected — that's intentional, not an error.
      setBenchmarkDataById({});
      setBenchmarkError(null);
      setBenchmarkLoading(false);
      return;
    }
    const controller = new AbortController();
    let activeController = controller;

    const loadBenchmarks = async () => {
      activeController.abort();
      activeController = new AbortController();
      const signal = activeController.signal;
      setBenchmarkLoading(true);
      setBenchmarkError(null);

      try {
        const settled = await Promise.allSettled(
          ids.map((benchmark) =>
            fetch(
              API_ENDPOINTS.benchmarkSeries({
                benchmark,
                startDate: benchmarkSettings.historyStartDate,
                baseValue: capitalBase,
              }),
              {
                signal,
                headers: getBearerAuthHeaders(),
              },
            ).then(async (response) => {
              const data = await response.json().catch(() => ({}));
              if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to load benchmark data.');
              }
              return { benchmark, data };
            }),
          ),
        );

        if (signal.aborted) return;

        const next = {};
        const failed = [];
        settled.forEach((result, index) => {
          const id = ids[index];
          if (result.status === 'fulfilled') {
            next[id] = result.value.data;
          } else {
            failed.push(id);
          }
        });

        setBenchmarkDataById(
          Object.fromEntries(
            Object.entries(next).map(([id, payload]) => [id, slimBenchmarkApiResponse(payload)]),
          ),
        );

        if (Object.keys(next).length === 0) {
          const firstReason = settled.find((r) => r.status === 'rejected');
          setBenchmarkError(
            firstReason?.status === 'rejected' ? firstReason.reason?.message || 'Failed to load benchmarks.' : 'Failed to load benchmarks.',
          );
        } else if (failed.length) {
          setBenchmarkError(`Could not load: ${failed.join(', ')}`);
        }
      } catch (nextError) {
        if (nextError.name === 'AbortError') return;
        setBenchmarkError(nextError.message || 'Failed to load benchmark data.');
      } finally {
        if (!signal.aborted) setBenchmarkLoading(false);
      }
    };

    loadBenchmarks();
    const refreshId = window.setInterval(loadBenchmarks, 15 * 60 * 1000);
    return () => {
      activeController.abort();
      window.clearInterval(refreshId);
    };
  }, [scenarioUnionRange, capitalBase, selectedBenchmarkIds, dataSoftRefreshKey, benchmarkSettings.historyStartDate]);

  const mergedChartData = useMemo(() => {
    // Union of dates across strategies + benchmarks. Strategies forward-fill after
    // their last point; benchmarks extend through their latest trading day.
    const scenarioDates = new Set();
    orderedSelectedIds.forEach((id) => {
      (scenarioSeriesById[id] || []).forEach((p) => scenarioDates.add(p.date));
    });
    if (scenarioDates.size === 0) return [];
    const sortedScenarioDates = Array.from(scenarioDates).sort((a, b) => a.localeCompare(b));
    const rangeStart = sortedScenarioDates[0];

    const allDates = new Set(scenarioDates);
    const bmTrimmed = sortedBenchmarkIds
      .map((id) => {
        const series = benchmarkDataById[id]?.series;
        if (!series?.length) return null;
        // Rebase to $100k at the strategy window start (rangeStart), then keep
        // only in-window points — so the benchmark starts level with the strategy.
        const trimmed = rebaseBenchmarkSeries(series).filter((p) => p && p.date >= rangeStart);
        return trimmed.length ? [id, trimmed] : null;
      })
      .filter(Boolean);
    bmTrimmed.forEach(([, s]) => s.forEach((p) => allDates.add(p.date)));

    const scnMaps = new Map(orderedSelectedIds.map((id) => [id, new Map((scenarioSeriesById[id] || []).map((p) => [p.date, p.normalized]))]));
    const bmMaps = new Map(bmTrimmed.map(([id, s]) => [id, new Map(s.map((p) => [p.date, p.normalized]))]));
    const lastScn = new Map(orderedSelectedIds.map((id) => [id, capitalBase]));
    const lastBm = new Map(bmTrimmed.map(([id]) => [id, capitalBase]));

    let rows = Array.from(allDates).sort((a, b) => a.localeCompare(b)).map((date) => {
      const row = { date, label: formatDateLabel(date) };
      orderedSelectedIds.forEach((id) => {
        const v = scnMaps.get(id).get(date);
        if (typeof v === 'number') lastScn.set(id, v);
        row[`scn_${id}`] = lastScn.get(id);
      });
      bmTrimmed.forEach(([id]) => {
        const v = bmMaps.get(id).get(date);
        if (typeof v === 'number') lastBm.set(id, v);
        row[`bm_${id}`] = lastBm.get(id);
      });
      return row;
    });
    if (rows.length > MAX_MERGED_CHART_ROWS) rows = rows.slice(-MAX_MERGED_CHART_ROWS);
    return rows;
  }, [orderedSelectedIds, scenarioSeriesById, benchmarkDataById, capitalBase, sortedBenchmarkIds, rebaseBenchmarkSeries]);

  const chartDisplayData = useMemo(() => {
    const loadedBm = sortedBenchmarkIds.filter((id) => benchmarkDataById[id]?.series?.length).length;
    const maxPoints = Math.max(
      320,
      Math.min(MAX_CHART_RENDER_POINTS, Math.floor(1200 / Math.max(1, loadedBm + 1))),
    );
    if (mergedChartData.length <= maxPoints) return mergedChartData;
    const step = Math.ceil(mergedChartData.length / maxPoints);
    const tailCount = 12;
    const tailStart = Math.max(0, mergedChartData.length - tailCount);
    const head = mergedChartData.slice(0, tailStart).filter((_, i) => i % step === 0);
    const tail = mergedChartData.slice(tailStart);
    const out = [...head, ...tail];
    const seen = new Set();
    return out.filter((row) => {
      if (seen.has(row.date)) return false;
      seen.add(row.date);
      return true;
    });
  }, [mergedChartData, sortedBenchmarkIds, benchmarkDataById]);

  // Full Journey: where live trading starts on the chart (the first point on or after the live date).
  const journeyMarkerLabel = useMemo(() => {
    if (!journeyLiveSince || journeyView !== 'full') return null;
    const row = chartDisplayData.find((r) => String(r?.date || '') >= journeyLiveSince);
    return row && row !== chartDisplayData[0] ? row.label : null;
  }, [journeyLiveSince, journeyView, chartDisplayData]);

  const expectsChartData = useMemo(
    () => orderedSelectedIds.some((id) => id !== 'custom') || allocationActive,
    [orderedSelectedIds, allocationActive],
  );

  const selectedScenariosNeedSheetAnalytics = useMemo(
    () => orderedSelectedIds.some((id) => scenarioNeedsSheetAnalytics(id, registryScenarioBySlug)),
    [orderedSelectedIds, registryScenarioBySlug],
  );

  const chartDataPrerequisitesMet = useMemo(() => {
    if (!equityStrategiesLoaded) return false;
    if (selectedScenariosNeedSheetAnalytics && (!analyticsLoaded || loading)) return false;
    return true;
  }, [
    equityStrategiesLoaded,
    selectedScenariosNeedSheetAnalytics,
    analyticsLoaded,
    loading,
  ]);

  const isChartInitialLoad = useMemo(
    () => expectsChartData
      && chartDisplayData.length === 0
      && !chartDataPrerequisitesMet,
    [expectsChartData, chartDisplayData.length, chartDataPrerequisitesMet],
  );

  const primaryBenchmarkId = useMemo(() => {
    const list = sortedBenchmarkIds;
    if (!list.length) return 'SPY';
    if (comparisonBenchmarkId && list.includes(comparisonBenchmarkId)) return comparisonBenchmarkId;
    return list[0];
  }, [sortedBenchmarkIds, comparisonBenchmarkId]);

  const benchmarkSummary = benchmarkSummaryById[primaryBenchmarkId] || null;
  const primaryBenchmarkSeries = useMemo(
    () => rebaseBenchmarkSeries(benchmarkDataById[primaryBenchmarkId]?.series || []),
    [benchmarkDataById, primaryBenchmarkId, rebaseBenchmarkSeries],
  );

  // Scenario driving the "Strategy vs Benchmark" panel (header dropdown).
  const compareScenarioId = benchmarkCompareScenarioId && orderedSelectedIds.includes(benchmarkCompareScenarioId)
    ? benchmarkCompareScenarioId
    : primaryScenarioId;
  const compareSeries = useMemo(
    () => scenarioSeriesById[compareScenarioId] || [],
    [scenarioSeriesById, compareScenarioId],
  );
  const compareSummary = scenarioSummaryById[compareScenarioId] || {};

  const comparePeriodReturns = useMemo(
    () => computePeriodReturns(compareSeries),
    [compareSeries],
  );
  const benchmarkPeriodReturns = useMemo(
    () => computePeriodReturns(primaryBenchmarkSeries),
    [primaryBenchmarkSeries],
  );
  const monthlyReturnBreakdownRows = useMemo(
    () => computeMonthlyReturnBreakdown(compareSeries, primaryBenchmarkSeries, {
      fixedBaseCapital: resolveMonthlyFixedBase(compareScenarioId),
    }),
    [compareSeries, primaryBenchmarkSeries, compareScenarioId, resolveMonthlyFixedBase],
  );
  const yearlyReturnBreakdownRows = useMemo(
    () => computeYearlyReturnBreakdown(compareSeries, primaryBenchmarkSeries),
    [compareSeries, primaryBenchmarkSeries],
  );

  const primaryBenchmarkFullName = useMemo(() => {
    const opt = marketOptions.find((m) => m.id === primaryBenchmarkId);
    const quote = marketQuotes.find((m) => m.id === primaryBenchmarkId);
    const raw = quote?.label || opt?.label || '';
    const cleaned = stripDisplayLabel(raw);
    return cleaned || opt?.label || benchmarkTicker(primaryBenchmarkId, marketOptions);
  }, [marketQuotes, primaryBenchmarkId, marketOptions]);

  const strategiesForBasket = useMemo(() => {
    const q = strategyBasketQuery.trim().toLowerCase();
    const filtered = basketFuturesStrategies.filter((s) => {
      const name = String(s.strategy || '').toLowerCase();
      if (q && !name.includes(q)) return false;
      const isSel = parseWeightPct(strategyScenarioConfigByName[s.strategy]?.weightPct) > 0;
      if (strategyBasketFilter === 'selected' && !isSel) return false;
      if (strategyBasketFilter === 'unselected' && isSel) return false;
      return true;
    });
    const byName = new Map(filtered.map((s) => [String(s.strategy || '').toLowerCase(), s]));
    return sortStrategies(filtered.map((s) => s.strategy))
      .map((name) => byName.get(String(name).toLowerCase()))
      .filter(Boolean);
  }, [basketFuturesStrategies, strategyBasketQuery, strategyBasketFilter, strategyScenarioConfigByName]);

  /** Short tickers only — used in headings, legend, and sidebar (avoids long API names). */
  const selectedBenchmarkTickersLabel = useMemo(() => {
    if (!sortedBenchmarkIds.length) return '—';
    return sortedBenchmarkIds.map((id) => benchmarkTicker(id, marketOptions)).join(' · ');
  }, [sortedBenchmarkIds, marketOptions]);

  const scenarioRangeLabel = buildRangeLabel(portfolioSummary.startDate, portfolioSummary.endDate);


  const updateStrategyExecutionInstrument = useCallback((strategyName, executionInstrument) => {
    markCustomEdit();
    setStrategyScenarioConfigByName((current) => ({
      ...current,
      [strategyName]: {
        ...(current[strategyName] || {}),
        executionInstrument,
      },
    }));
  }, [markCustomEdit]);

  const toggleStrategyDay = useCallback((strategyName, dayKey) => {
    markCustomEdit();
    setStrategyScenarioConfigByName((current) => {
      const defaults = getStrategyScenarioDefaults(strategyName);
      const priorDays = current[strategyName]?.activeDays?.length
        ? current[strategyName].activeDays
        : defaults.defaultActiveDays;
      const nextDays = priorDays.includes(dayKey)
        ? priorDays.filter((day) => day !== dayKey)
        : [...priorDays, dayKey];
      if (nextDays.length === 0) return current;
      return {
        ...current,
        [strategyName]: {
          ...(current[strategyName] || {}),
          executionInstrument: current[strategyName]?.executionInstrument || defaults.defaultExecutionInstrument,
          activeDays: VC_DAY_SEQUENCE.filter((day) => nextDays.includes(day)),
          baseUnits: current[strategyName]?.baseUnits || 1,
          sizeMultiplier: current[strategyName]?.sizeMultiplier ?? '1',
        },
      };
    });
  }, [markCustomEdit]);

  const updateStrategySizeMultiplier = useCallback((strategyName, raw) => {
    markCustomEdit();
    setStrategyScenarioConfigByName((current) => ({
      ...current,
      [strategyName]: {
        ...(current[strategyName] || {}),
        sizeMultiplier: raw,
      },
    }));
  }, [markCustomEdit]);

  const normalizeStrategySizeMultiplier = useCallback((strategyName) => {
    setStrategyScenarioConfigByName((current) => {
      const prev = current[strategyName]?.sizeMultiplier ?? '1';
      const n = parseFloat(prev);
      const next =
        prev === '' || !Number.isFinite(n) || n <= 0
          ? '1'
          : formatUnitMultiplier(Math.min(100, Math.max(0.1, n)));
      return {
        ...current,
        [strategyName]: {
          ...current[strategyName],
          sizeMultiplier: next,
        },
      };
    });
  }, []);

  const toggleStrategyExpanded = useCallback((strategyName) => {
    setExpandedStrategies((current) => {
      const next = new Set(current);
      if (next.has(strategyName)) next.delete(strategyName);
      else next.add(strategyName);
      return next;
    });
  }, []);

  const expandStrategy = useCallback((strategyName) => {
    setExpandedStrategies((current) => {
      if (current.has(strategyName)) return current;
      const next = new Set(current);
      next.add(strategyName);
      return next;
    });
  }, []);

  const updateStrategyWeight = useCallback((strategyName, raw) => {
    markCustomEdit();
    setStrategyScenarioConfigByName((current) => ({
      ...current,
      [strategyName]: {
        ...(current[strategyName] || {}),
        weightPct: raw,
      },
    }));
  }, [markCustomEdit]);

  const normalizeStrategyWeight = useCallback((strategyName) => {
    setStrategyScenarioConfigByName((current) => {
      const prev = current[strategyName]?.weightPct ?? '';
      const n = parseFloat(String(prev).trim());
      const next = prev === '' || !Number.isFinite(n) || n < 0 ? '' : String(Math.min(n, 1000));
      return {
        ...current,
        [strategyName]: {
          ...current[strategyName],
          weightPct: next,
        },
      };
    });
  }, []);

  const deselectAllStrategies = useCallback(() => {
    markCustomEdit();
    // Zero every allocation — basket weights AND equity weights — so
    // "Deselect all" truly resets everything back to 0%.
    setEquityWeightByName({});
    setStrategyScenarioConfigByName((prev) => {
      const next = {};
      Object.entries(prev).forEach(([name, cfg]) => {
        next[name] = { ...cfg, weightPct: '' };
      });
      return next;
    });
  }, [markCustomEdit]);

  const selectBenchmarkForComparison = useCallback((id) => {
    setSelectedBenchmarkIds((prev) => {
      const next = new Set(prev);
      if (!next.has(id)) next.add(id);
      return next;
    });
    setComparisonBenchmarkId(id);
  }, []);

  const importantDisclosuresContent = (
    <div className="vector-capital-disclosure-content">
      <div className="vector-capital-disclosure-section vector-capital-disclosure-section-statutory">
        <h4 className="vector-capital-disclosure-section-title">Hypothetical Performance Disclosure</h4>
        <p className="vector-capital-disclosure-statutory">
          HYPOTHETICAL OR SIMULATED PERFORMANCE RESULTS HAVE CERTAIN INHERENT LIMITATIONS. UNLIKE AN ACTUAL
          PERFORMANCE RECORD, SIMULATED RESULTS DO NOT REPRESENT ACTUAL TRADING. ALSO, SINCE THE TRADES HAVE
          NOT BEEN EXECUTED, THE RESULTS MAY HAVE UNDER-OR-OVER COMPENSATED FOR THE IMPACT, IF ANY, OF
          CERTAIN MARKET FACTORS, SUCH AS LACK OF LIQUIDITY. SIMULATED TRADING PROGRAMS IN GENERAL ARE ALSO
          SUBJECT TO THE FACT THAT THEY ARE DESIGNED WITH THE BENEFIT OF HINDSIGHT. NO REPRESENTATION IS
          BEING MADE THAT ANY ACCOUNT WILL OR IS LIKELY TO ACHIEVE PROFITS OR LOSSES SIMILAR TO THOSE SHOWN.
        </p>
      </div>

      <div className="vector-capital-disclosure-section">
        <h4 className="vector-capital-disclosure-section-title">About this strategy</h4>
        <ul className="vector-capital-disclosure-list">
          <li>
            The growth curve, P&amp;L, drawdown, win rate, profit factor, and other figures shown are
            <strong> hypothetical projections</strong> generated by applying historical or model-based
            strategy data to your inputs (capital base, contract sleeves, active days, contract type, and
            date range). No real account traded these results.
          </li>
          <li>
            Projections are highly sensitive to inputs. Different presets, account sizes, contract counts,
            sleeve choices, or date ranges will produce different — sometimes very different — results.
            Default selections are provided for convenience and are not representative of any particular
            outcome you should expect.
          </li>
          <li>
            Projections do not account for personal factors such as your risk tolerance, tax situation, or
            behavioral discipline, and they do not adjust for periods that were excluded by your filter
            choices.
          </li>
        </ul>
      </div>

      <div className="vector-capital-disclosure-section">
        <h4 className="vector-capital-disclosure-section-title">Strategy and benchmark data sources</h4>
        <ul className="vector-capital-disclosure-list">
          <li>
            Strategy performance data is provided by {getEntityDisplayName('the strategy provider')} and may
            include actual, backtested, forward-tested, simulated, or model-based results, or a combination
            of these. {appName} does not independently audit, verify, or certify that data, its methodology,
            sample size, or survivorship.
          </li>
          <li>
            Benchmark data may come from public daily series (indexes or spot proxies) and/or vendor
            pricing; it may differ from official indexes, contract marks, or investable returns.
          </li>
          <li>
            Results may exclude fees, taxes, slippage, commissions, financing costs, exchange and platform
            fees, and execution differences. Real-world trading costs reduce realized returns.
          </li>
        </ul>
      </div>

      <div className="vector-capital-disclosure-section">
        <h4 className="vector-capital-disclosure-section-title">Trading risk</h4>
        <ul className="vector-capital-disclosure-list">
          <li>
            <strong>Trading futures involves a substantial risk of loss and is not suitable for every
            investor.</strong> Leveraged products can result in losses that exceed your initial investment.
          </li>
          <li>
            Market conditions, liquidity, volatility, and strategy behavior change over time. Historical
            performance — actual or hypothetical — is not indicative of future results.
          </li>
          <li>
            Only trade with capital you can afford to lose. You should consult a qualified financial,
            tax, or legal professional before making any trading or investment decision.
          </li>
        </ul>
      </div>

      <div className="vector-capital-disclosure-section">
        <h4 className="vector-capital-disclosure-section-title">No advice; not a registered advisor</h4>
        <ul className="vector-capital-disclosure-list">
          <li>
            {appName} is an independent analytics and visualization tool. {appName} is <strong>not</strong> a
            Commodity Trading Advisor (CTA), Introducing Broker (IB), Futures Commission Merchant (FCM),
            Registered Investment Adviser (RIA), or broker-dealer, and does not provide personalized
            investment advice, recommendations, or suitability determinations.
          </li>
          <li>
            {getEntityDisplayName('The strategy provider')} is a third-party strategy provider. {appName} does not
            endorse, manage, or select any specific strategy or provider for you. Any classification,
            registration status, or exemption claimed by the strategy provider is that provider&apos;s
            responsibility, not {appName}&apos;s.
          </li>
          <li>
            Nothing on this page is a solicitation or offer to buy or sell any futures contract, security,
            or financial instrument, and nothing here should be relied on as a basis for any trading
            decision.
          </li>
        </ul>
      </div>

      <div className="vector-capital-disclosure-section">
        <h4 className="vector-capital-disclosure-section-title">Eligibility, jurisdiction, and use</h4>
        <ul className="vector-capital-disclosure-list">
          <li>
            This dashboard is provided from the United States and is intended for users in jurisdictions
            where its use is lawful. Some jurisdictions restrict or prohibit access to derivatives
            information or analytics, including parts of the European Union, the United Kingdom, Canada,
            and elsewhere. You are responsible for determining whether your use of {appName} complies with
            the laws of your country, state, or province.
          </li>
          <li>
            By using {appName} you accept its{' '}
            <a href="/terms-and-conditions.html" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>{' '}
            and{' '}
            <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>,
            which contain additional disclaimers and limitations of liability.
          </li>
        </ul>
      </div>
    </div>
  );

  // Tab pills shared by both comparison panels (rendered inside each card so
  // the card top-aligns with the Strategy Assumptions panel beside it).
  const comparisonTabs = (
    <div className="vector-capital-pill-row vector-capital-comparison-tabs">
      <button
        type="button"
        className={`vector-capital-pill ${comparisonTab === 'all' ? 'active' : ''}`}
        onClick={() => setComparisonTab('all')}
      >
        Full Comparison
      </button>
      <button
        type="button"
        className={`vector-capital-pill ${comparisonTab === 'benchmark' ? 'active' : ''}`}
        onClick={() => setComparisonTab('benchmark')}
      >
        Strategy vs Benchmark
      </button>
    </div>
  );

  const renderStrategyBasket = (variant) => (
    <div className={`vector-capital-panel vcp-basket vcp-basket--${variant}`}>
            <div className="vector-capital-panel-header">
              <div>
                <span className="vector-capital-panel-eyebrow">Strategy Basket</span>
                <h3>Custom Composition</h3>
              </div>
              <div className="vector-capital-basket-header-actions">
                <button
                  type="button"
                  className="vector-capital-secondary-btn vector-capital-basket-clear-btn"
                  onClick={deselectAllStrategies}
                  disabled={totalAllocatedPct === 0}
                >
                  Deselect all
                </button>
              </div>
            </div>

            <>
            <div className={`vcp-alloc-bar ${totalAllocatedPct > 100 ? 'is-over' : ''}`}>
              <div className="vcp-alloc-meter" aria-hidden="true">
                <div
                  className="vcp-alloc-meter-fill"
                  style={{ width: `${Math.min(totalAllocatedPct, 100)}%` }}
                />
              </div>
              <div className="vcp-alloc-stats">
                <span className="vcp-alloc-stat">
                  Allocated <strong>{totalAllocatedPct.toFixed(0)}%</strong> · {formatCurrency(allocatedDollars)}
                </span>
                <span className={`vcp-alloc-stat ${remainingPct < 0 ? 'is-neg' : ''}`}>
                  {remainingPct >= 0 ? 'Remaining' : 'Over by'}{' '}
                  <strong>{Math.abs(remainingPct).toFixed(0)}%</strong> · {formatCurrency(Math.abs(remainingDollars))}
                </span>
                {allocationActive && (
                  <button type="button" className="vcp-alloc-clear" onClick={clearAllAllocations}>
                    Clear allocation
                  </button>
                )}
              </div>
              <p className="vcp-alloc-hint">
                {allocationActive
                  ? (totalAllocatedPct > 100
                    ? 'Over 100% — the excess is modeled as simple margin (leveraged portfolio).'
                    : 'Set a Weight % on each strategy below until you’ve allocated 100% (the rest stays in cash).')
                  : 'Set a Weight % on a strategy below (and/or an equity strategy) to allocate a percentage of your capital. With nothing allocated, the contract-sized basket is used instead.'}
              </p>
            </div>

            <div className="vcp-groups">
            <div className="vcp-group">
              <div className="vcp-group-head vcp-group-head--static">
                <span className="vcp-group-title">
                  Futures <span className="vcp-group-count">{basketFuturesStrategies.length}</span>
                </span>
                <span className="vcp-group-meta">
                  {totalStrategyWeightPct > 0 ? `${totalStrategyWeightPct.toFixed(0)}% allocated` : ''}
                </span>
              </div>
              {(
                <>
            <div className="vector-capital-strategy-basket-toolbar">
              <label className="vector-capital-strategy-search">
                <span className="vector-capital-strategy-search-icon" aria-hidden="true">
                  <IconSearch size={16} color="rgba(148, 163, 184, 0.95)" />
                </span>
                <input
                  type="search"
                  value={strategyBasketQuery}
                  onChange={(e) => setStrategyBasketQuery(e.target.value)}
                  placeholder="Search strategies"
                  autoComplete="off"
                  aria-label="Search strategies"
                />
              </label>
              <label className="vector-capital-strategy-filter">
                <span className="vector-capital-strategy-filter-icon" aria-hidden="true">
                  <IconFilter size={16} color="rgba(148, 163, 184, 0.95)" />
                </span>
                <select
                  value={strategyBasketFilter}
                  onChange={(e) => setStrategyBasketFilter(e.target.value)}
                  aria-label="Filter strategy list"
                >
                  <option value="all">All strategies</option>
                  <option value="selected">Selected only</option>
                  <option value="unselected">Not selected</option>
                </select>
              </label>
            </div>
            <div className="vector-capital-strategy-grid" onScroll={handleStrategyBasketScroll}>
              {strategiesForBasket.length === 0 ? (
                <div className="vector-capital-strategy-grid-empty">
                  No strategies match your search or filter.
                </div>
              ) : (
                strategiesForBasket.map((strategy) => {
                  const name = strategy.strategy;
                  const cfg = strategyScenarioConfigByName[name] || {};
                  const weightInput = cfg.weightPct == null ? '' : String(cfg.weightPct);
                  const active = parseWeightPct(cfg.weightPct) > 0; // checkmark/highlight
                  const expanded = expandedStrategies.has(name);    // config visible
                  const defaults = getStrategyScenarioDefaults(name);
                  const executionInstrument = defaults.executionOptions.some((o) => o.id === cfg.executionInstrument)
                    ? cfg.executionInstrument
                    : defaults.defaultExecutionInstrument;
                  const activeDays = cfg.activeDays?.length
                    ? cfg.activeDays.filter((d) => VC_DAY_SEQUENCE.includes(d))
                    : defaults.defaultActiveDays;
                  const sizeMultiplierInput = cfg.sizeMultiplier === ''
                    ? ''
                    : (cfg.sizeMultiplier != null && String(cfg.sizeMultiplier).trim() !== '' ? String(cfg.sizeMultiplier) : '1');
                  const effectiveUnits = parseProgramSizeMultiplier(cfg.sizeMultiplier);
                  return (
                    <div
                      key={name}
                      className={`vector-capital-strategy-chip ${active ? 'active' : ''} ${expanded ? 'is-expanded' : ''}`}
                    >
                      <div className="vcp-chip-header">
                        <button
                          type="button"
                          className="vcp-chip-expand"
                          onClick={() => toggleStrategyExpanded(name)}
                          aria-expanded={expanded}
                        >
                          <span className="vector-capital-strategy-chip-check" aria-hidden="true">
                            {active ? <IconCheck size={14} /> : null}
                          </span>
                          <span className="vector-capital-strategy-chip-copy">
                            <strong>{
                              strategy?.displayLabel
                              || resolveFuturesDataNameDisplayLabel(
                                name,
                                registryScenarioBySlug,
                                basketDisplayEnabled?.labels,
                              )
                            }</strong>
                            <small>
                              {defaults.baseInstrument} source
                              {' · '}
                              PF {formatFactor(strategyOverall(strategy).pf)}
                              {' · '}
                              Win {safeNumber(strategyOverall(strategy).winRate).toFixed(1)}%
                            </small>
                          </span>
                        </button>
                        <span className="vcp-weight-input-wrap">
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            className="vcp-weight-input"
                            placeholder="0"
                            aria-label={`Allocation percent for ${name}`}
                            value={weightInput}
                            onFocus={() => expandStrategy(name)}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                updateStrategyWeight(name, v);
                              }
                            }}
                            onBlur={() => normalizeStrategyWeight(name)}
                          />
                          <span aria-hidden="true">%</span>
                        </span>
                      </div>
                      {expanded ? (
                        <div className="vector-capital-strategy-chip-settings">
                          <label className="vector-capital-strategy-setting-field">
                            <span>Execution product</span>
                            <select
                              value={executionInstrument}
                              onChange={(e) => updateStrategyExecutionInstrument(name, e.target.value)}
                            >
                              {defaults.executionOptions.map((option) => (
                                <option key={option.id} value={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <div className="vector-capital-strategy-setting-field">
                            <span>{variant === 'top' ? 'Days' : 'Active days'}</span>
                            <div className="vector-capital-day-toggle-row">
                              {VC_DAY_SEQUENCE.map((dayKey) => {
                                const dayActive = activeDays.includes(dayKey);
                                return (
                                  <button
                                    key={dayKey}
                                    type="button"
                                    className={`vector-capital-day-toggle ${dayActive ? 'active' : ''}`}
                                    onClick={() => toggleStrategyDay(name, dayKey)}
                                  >
                                    {VC_DAY_LABELS[dayKey]}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="vector-capital-effective-size-row">
                            <span className="vector-capital-effective-size-label">Effective size</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              className="vector-capital-effective-size-input"
                              aria-label={`Size multiplier for ${name}; scales relative to default contract size in ${executionInstrument}`}
                              value={sizeMultiplierInput}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                  updateStrategySizeMultiplier(name, v);
                                }
                              }}
                              onBlur={() => normalizeStrategySizeMultiplier(name)}
                            />
                            <span className="vector-capital-effective-size-x" aria-hidden="true">
                              ×
                            </span>
                            <span className="vector-capital-effective-size-product">{executionInstrument}</span>
                            <span className="vector-capital-effective-size-hint">
                              ({formatUnitMultiplier(effectiveUnits)}x default contract size)
                            </span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
            {strategiesForBasket.length > 0 ? (
              <div
                className={`vector-capital-strategy-scroll-cue ${strategyBasketScrollCueVisible ? '' : 'is-hidden'}`}
                aria-hidden="true"
              >
                <span>Scroll</span>
                <IconChevronDown size={14} color="rgba(191, 219, 254, 0.72)" />
              </div>
            ) : null}
                </>
              )}
            </div>

            {equityStrategiesForBasket.length > 0 && (
              <div className="vcp-group vcp-equity-section">
                <div className="vcp-group-head vcp-group-head--static">
                  <span className="vcp-group-title">
                    Stocks <span className="vcp-group-count">{equityStrategiesForBasket.length}</span>
                  </span>
                  <span className="vcp-group-meta">
                    {totalEquityWeightPct > 0 ? `${totalEquityWeightPct.toFixed(0)}% allocated` : ''}
                  </span>
                </div>
                {(
                <div className="vcp-equity-list">
                  {equityStrategiesForBasket.map((s) => {
                    const weightInput = equityWeightByName[s.name] ?? '';
                    const isOn = parseWeightPct(weightInput) > 0;
                    return (
                      <div className={`vcp-equity-row ${isOn ? 'is-on' : ''}`} key={s.name}>
                        <span className="vcp-equity-name">
                          <span className="vector-capital-strategy-chip-check" aria-hidden="true">
                            {isOn ? <IconCheck size={14} /> : null}
                          </span>
                          <span className="vcp-equity-name-text">
                            <strong>{s.name}</strong>
                            {s.description ? (
                              <span className="vcp-info-wrap">
                                <button
                                  type="button"
                                  className="vcp-info-btn"
                                  aria-label={`File used: ${s.description}`}
                                >
                                  <IconInfo size={13} color="rgba(148, 163, 184, 0.95)" />
                                </button>
                                <span className="vcp-info-tip" role="tooltip">
                                  <span className="vcp-info-tip-label">File used</span>
                                  {s.description}
                                </span>
                              </span>
                            ) : null}
                          </span>
                        </span>
                        <span className="vcp-weight-input-wrap">
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            className="vcp-weight-input"
                            placeholder="0"
                            aria-label={`Allocation percent for ${s.name}`}
                            value={weightInput}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                updateEquityWeight(s.name, v);
                              }
                            }}
                            onBlur={() => normalizeEquityWeight(s.name)}
                          />
                          <span aria-hidden="true">%</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
                )}
                <div className="vector-capital-strategy-scroll-cue" aria-hidden="true">
                  <span>Scroll</span>
                  <IconChevronDown size={14} color="rgba(191, 219, 254, 0.72)" />
                </div>
              </div>
            )}
            </div>
            </>
          </div>
  );

  const renderTopbar = () => (
    <section className="vector-capital-topbar">
        <div className="vector-capital-topbar-title">
          <span className="vector-capital-eyebrow">{getEntityDisplayName()}</span>
          <h2>Multi-Asset Strategy Dashboard</h2>
        </div>
        <div className="vector-capital-topbar-stats">
          <div className="vector-capital-topstat">
            <span>Capital Base</span>
            <strong>{formatCurrency(capitalBase)}</strong>
          </div>
          <div className="vector-capital-topstat vector-capital-topstat--range">
            <span>Range</span>
            <strong>{scenarioRangeLabel}</strong>
          </div>
        </div>
      </section>
  );

  const renderControls = () => (
    <section className="vector-capital-controls-card vector-capital-controls-card--compact">
        <div className="vector-capital-control-row">
          <span
            className="vector-capital-control-label"
            title="Select one or more — each draws its own curve and adds a comparison column."
          >
            Strategy
          </span>
          <div className="vector-capital-pill-row">
            {scenarioPillOrder.map((value) => (
              value === 'custom' ? (
                <span key={value} className="vector-capital-custom-pill-wrap">
                  <button
                    type="button"
                    className={`vector-capital-pill vector-capital-pill--custom ${selectedScenarioIds.has(value) ? 'active' : ''}`}
                    onClick={() => toggleScenario(value)}
                  >
                    {getScenarioLabel(value)}
                  </button>
                  <button
                    type="button"
                    className={`vector-capital-custom-edit-btn ${selectedScenarioIds.has(value) ? 'active' : ''}`}
                    onClick={() => {
                      markCustomEdit();
                      setBasketModalOpen(true);
                    }}
                    aria-label="Edit custom composition"
                    title="Edit custom composition"
                  >
                    <IconPencil size={13} color="currentColor" />
                  </button>
                </span>
              ) : (
                <button
                  key={value}
                  type="button"
                  className={`vector-capital-pill ${selectedScenarioIds.has(value) ? 'active' : ''}${isInternalScenario(value) ? ' vector-capital-pill--internal' : ''}`}
                  onClick={() => toggleScenario(value)}
                  title={isInternalScenario(value) ? 'Internal — visible to admins only, not shown to clients' : undefined}
                >
                  {getScenarioLabel(value)}
                </button>
              )
            ))}
            {/* Admin-only Settings — sits inline next to Custom; opens the settings modal. */}
            {isAdmin && (
              <VectorEquityPortfolioSettings
                userEmail={user?.email}
                customBlendStartBasis={customBlendStartBasis}
                onCustomBlendStartBasisChange={setCustomBlendStartBasis}
                configs={equityPortfolioConfig}
                onSaved={(configs) => setEquityPortfolioConfig(configs)}
                futuresCatalogNames={futuresCatalogNames}
                equityAlgoCatalog={equityAlgoCatalog}
                scenarioPillOrder={scenarioPillOrder}
                getScenarioLabel={getScenarioLabel}
                monthlyReturnMethods={monthlyReturnMethods}
                onMonthlyReturnMethodsSaved={(methods) => {
                  setBasketDisplayEnabled((prev) => (
                    prev ? { ...prev, monthlyReturnMethods: methods } : { monthlyReturnMethods: methods }
                  ));
                }}
                benchmarkSettings={benchmarkSettings}
                onBenchmarkSettingsSaved={(settings) => {
                  setBasketDisplayEnabled((prev) => (
                    prev ? { ...prev, benchmarkSettings: settings } : { benchmarkSettings: settings }
                  ));
                  setDataSoftRefreshKey((k) => k + 1);
                }}
                onScenarioDisplaySaved={(enabled) => {
                  if (enabled) setBasketDisplayEnabled(enabled);
                  setDataSoftRefreshKey((k) => k + 1);
                }}
                nt8ConnectionId={nt8ConnectionId}
                ntAccounts={ntAccounts}
                onNt8Saved={(connectionId) => {
                  setNt8ConnectionId(connectionId);
                  loadNtSettings();
                }}
                onRefreshAccounts={loadNtSettings}
                ntConnectorDownload={API_ENDPOINTS.ntConnectorDownload}
                isFullAdmin={isFullAdmin}
              />
            )}
          </div>
          {selectedPreset !== 'custom' && (
            <div className="vector-capital-preset-inline-info">
              <button
                type="button"
                className="vector-capital-preset-inline-info-trigger"
                onClick={() => setPresetInfoExpanded((v) => !v)}
              >
                <span>
                  <strong>{selectedPresetMeta.label}</strong>
                  {' · '}
                  {selectedPresetMeta.riskLabel}
                </span>
                <span className="vector-capital-preset-inline-info-toggle">
                  {presetInfoExpanded ? 'See less ▲' : 'See more ▼'}
                </span>
              </button>
              {presetInfoExpanded && (
                <div className="vector-capital-preset-inline-info-body">
                  <div className="vector-capital-preset-inline-info-desc">{selectedPresetMeta.description}</div>
                  <div className="vector-capital-preset-inline-info-composition">
                    {presetComposition.map((item, i) => (
                      <span key={i}>
                        {item}{i < presetComposition.length - 1 ? ' ·' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="vector-capital-control-row vector-capital-control-row--split">
          <div className="vector-capital-control-cluster">
            <span className="vector-capital-control-label">Capital Base</span>
            <div className="vector-capital-capital-row">
              <div className="vector-capital-pill-row">
                {CAPITAL_BASE_OPTIONS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`vector-capital-pill ${!isCustomCapitalActive && capitalBasePreset === value ? 'active' : ''}`}
                    onClick={() => {
                      setCapitalBasePreset(value);
                      setCustomCapitalRaw('');
                    }}
                  >
                    {formatCurrency(value)}
                  </button>
                ))}
              </div>
              <label className="vector-capital-custom-capital-inline">
                <span title="Overrides preset when valid">Custom</span>
                <div className="vector-capital-prefix-input">
                  <span className="vector-capital-prefix-input-symbol" aria-hidden="true">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder=""
                    aria-label="Custom capital base in dollars"
                    value={customCapitalRaw}
                    onChange={(e) => setCustomCapitalRaw(e.target.value)}
                  />
                </div>
              </label>
            </div>
          </div>

          <div className="vector-capital-control-cluster">
            <span className="vector-capital-control-label">Date Range</span>
            <div className="vector-capital-pill-row">
              {DATE_RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`vector-capital-pill ${dateRangeFilter === option.value ? 'active' : ''}`}
                  onClick={() => setDateRangeFilter(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {dateRangeFilter === 'custom' && (
              <div className="vector-capital-inline-controls">
                <label className="vector-capital-field">
                  <span>Start Date</span>
                  <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                </label>
                <label className="vector-capital-field">
                  <span>End Date</span>
                  <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                </label>
              </div>
            )}
          </div>
        </div>
      </section>
  );

  const renderChart = () => (
    <section className="vector-capital-chart-section">
          <div className="vector-capital-panel vector-capital-chart-panel">
            <div className="vector-capital-panel-header vector-capital-chart-panel-header">
              <div>
                <span className="vector-capital-panel-eyebrow">Illustrative Growth Curve</span>
                <h3 className="vector-capital-chart-title">Growth of {formatCurrency(capitalBase)}</h3>
                <p className="vector-capital-chart-subtitle">
                  {sortedBenchmarkIds.length ? `Strategy vs ${selectedBenchmarkTickersLabel}` : 'Hypothetical strategy growth'}
                </p>
                <JourneyTabs liveSince={journeyLiveSince} view={journeyView} onChange={setJourneyView} className="vector-capital-journey" />
              </div>
              <div className="vector-capital-chart-bm">
                <span
                  className="vector-capital-control-label"
                  title="Toggle benchmarks on the chart. Click again to remove."
                >
                  Benchmarks
                </span>
                <div className="vector-capital-pill-row vector-capital-chart-bm-row">
                  {marketOptions.map((option) => {
                    const quote = marketQuotes.find((market) => market.id === option.id);
                    const active = selectedBenchmarkIds.has(option.id);
                    const isPositive = safeNumber(quote?.percentChange) >= 0;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={`vector-capital-pill vector-capital-bm-pill ${active ? 'active' : ''}`}
                        onClick={() => toggleBenchmarkId(option.id)}
                      >
                        <span className="vector-capital-bm-pill-name">
                          <span className="vector-capital-bm-pill-name-full">
                            {option.label} ({option.shortLabel})
                          </span>
                          <span className="vector-capital-bm-pill-name-short">{option.shortLabel}</span>
                        </span>
                        <span className="vector-capital-bm-pill-quote">
                          {quote?.lastPrice != null ? (
                            <>
                              <strong>{formatQuoteNumber(quote.lastPrice)}</strong>
                              <span className={`vector-capital-bm-pill-chg ${isPositive ? 'positive' : 'negative'}`}>
                                {quote?.netChange != null
                                  ? `${safeNumber(quote.netChange) >= 0 ? '+' : ''}${formatQuoteNumber(quote.netChange)}`
                                  : '--'}
                                {' · '}
                                {quote?.percentChange != null
                                  ? `${safeNumber(quote.percentChange) >= 0 ? '+' : ''}${safeNumber(quote.percentChange).toFixed(2)}%`
                                  : '--'}
                              </span>
                              {quote?.source === 'fred' && (
                                <span
                                  className="vector-capital-bm-pill-eod"
                                  title="End-of-day index level; one update per business day after the close."
                                >
                                  EOD
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="vector-capital-bm-pill-chg">…</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="vector-capital-panel-status" aria-live="polite">
                  {benchmarkLoading ? 'Loading benchmarks…' : ''}
                </div>
              </div>
            </div>

            {benchmarkError && (
              <div className="vector-capital-inline-error">
                <IconAlert size={16} color="currentColor" />
                <span>{benchmarkError}</span>
              </div>
            )}

            <div className="vector-capital-chart-canvas-wrap">
              <div className="vector-capital-chart-info-anchor">
                <span className="vector-capital-metric-info-wrap vector-capital-chart-info-wrap">
                  <button
                    type="button"
                    className="vector-capital-metric-info-btn"
                    aria-label="How this growth chart is calculated"
                  >
                    <IconInfo size={16} color="rgba(148, 163, 184, 0.95)" />
                  </button>
                  <div className="vector-capital-metric-info-tooltip vector-capital-chart-info-tooltip" role="tooltip">
                    {CHART_GROWTH_CURVE_INFO}
                  </div>
                </span>
              </div>
              {isChartInitialLoad ? (
                <VectorCapitalChartLoader />
              ) : chartDisplayData.length === 0 ? (
                <div className="vector-capital-chart-empty">
                  {allocationActive
                    ? 'No data for the allocated strategies in the selected date range — they don’t go back that far. Try a wider range like “All Time.”'
                    : expectsChartData
                      ? 'No equity curve data for the selected strategy. If you just uploaded a CSV in Strategy Data, wait a few seconds and refresh — or confirm the strategy’s strategy name matches the equity strategy you edited.'
                      : 'Allocate a percentage to a strategy (Futures or Stocks) to render a strategy comparison.'}
                </div>
              ) : (
                <div className="vector-capital-chart-shell vector-capital-chart-shell--ready">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartDisplayData} margin={{ top: 8, right: 20, left: 10, bottom: 8 }}>
                      <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: chartColors.tick }}
                        minTickGap={24}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: chartColors.tick }}
                        tickFormatter={chartTickFormatter}
                        // Fit tightly to the data (small pad) so the curve fills the
                        // plot height instead of floating with dead headroom — makes
                        // the same slope read steeper without touching the data.
                        domain={[
                          (dataMin) => dataMin - Math.abs(dataMin) * 0.01,
                          (dataMax) => dataMax + Math.abs(dataMax) * 0.02,
                        ]}
                      />
                      <Tooltip content={<ComparisonTooltip />} />
                      <Legend />
                      {journeyMarkerLabel ? (
                        <ReferenceLine
                          x={journeyMarkerLabel}
                          stroke="#22c55e"
                          strokeDasharray="4 4"
                          label={{ value: 'Live →', position: 'insideTopRight', fill: '#22c55e', fontSize: 11 }}
                        />
                      ) : null}
                      {orderedSelectedIds.map((id) => (
                        <Line
                          key={id}
                          type="monotone"
                          dataKey={`scn_${id}`}
                          name={getScenarioLabel(id)}
                          stroke={getScenarioColor(id)}
                          strokeWidth={id === primaryScenarioId ? 3 : 2}
                          dot={false}
                          isAnimationActive={false}
                          activeDot={{ r: 4 }}
                        />
                      ))}
                      {sortedBenchmarkIds
                        .filter((id) => benchmarkDataById[id]?.series?.length)
                        .map((id) => {
                          return (
                            <Line
                              key={id}
                              type="monotone"
                              dataKey={`bm_${id}`}
                              name={benchmarkTicker(id, marketOptions)}
                              stroke={resolveBenchmarkLineColor(id)}
                              strokeWidth={2}
                              dot={false}
                              isAnimationActive={false}
                              activeDot={{ r: 4 }}
                              connectNulls
                            />
                          );
                        })}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="vector-capital-chart-footnote">
              Benchmark lines use public daily index or spot proxies where configured (vendor or St. Louis Fed series), not
              necessarily futures contract marks. Strategy line is hypothetical from your selected assumptions and
              historical strategy rows in {appName}.
            </div>
          </div>
      </section>
  );

  const renderComparison = () => (
    <section className="vector-capital-layout">
        <div className="vector-capital-main">
          {comparisonTab === 'all' && (
          <div className="vector-capital-panel">
            <div className="vector-capital-panel-header">
              <div>
                <span className="vector-capital-panel-eyebrow">Comparison</span>
                <h3>Strategies{benchmarkColIds.length ? ' vs Benchmarks' : ''}</h3>
              </div>
              {comparisonTabs}
            </div>
            <div className="vector-capital-scenario-compare" style={{ overflowX: 'auto' }}>
              <div
                className="vc-scenario-compare-grid"
                style={{
                  gridTemplateColumns: `minmax(150px, 1.3fr) repeat(${Math.max(orderedSelectedIds.length + benchmarkColIds.length, 1)}, minmax(120px, 1fr))`,
                }}
              >
                <div className="vc-scenario-compare-head-metric">
                  Metric
                </div>
                {orderedSelectedIds.map((id) => (
                  <div
                    key={`h-${id}`}
                    className="vc-scenario-compare-head-col"
                  >
                    <span className="vc-scenario-compare-swatch vc-scenario-compare-swatch--square" style={{ background: getScenarioColor(id) }} />
                    {getScenarioLabel(id)}{id === primaryScenarioId ? ' ·' : ''}
                  </div>
                ))}
                {benchmarkColIds.map((id) => (
                  <div
                    key={`hb-${id}`}
                    className="vc-scenario-compare-head-bm"
                  >
                    <span className="vc-scenario-compare-swatch vc-scenario-compare-swatch--round" style={{ background: resolveBenchmarkLineColor(id) }} />
                    {benchmarkFullName(id, marketOptions)}
                  </div>
                ))}
                {[
                  // Same metrics, order, and explanations as Fyntrak's
                  // "Performance & Risk Statistics" table.
                  { label: 'Total Return', kind: 'pct', scn: (s) => s.totalReturnPct, bm: (b) => b.totalReturnPct },
                  { label: 'Annualized Return', kind: 'pct', scn: (s) => s.annualizedReturnPct, bm: (b) => b.annualizedReturnPct },
                  { label: 'Max Drawdown', kind: 'dd', scn: (s) => s.maxDrawdownPct, bm: (b) => b.maxDrawdownPct },
                  { label: 'Sharpe Ratio', kind: 'num', scn: (s, r) => r.sharpeRatio, bm: (b, r) => r.sharpeRatio },
                  { label: 'Standard Deviation', kind: 'vol', scn: (s, r) => r.annualizedStdDevPct, bm: (b, r) => r.annualizedStdDevPct },
                  { label: 'Profit Factor', kind: 'pf', scn: (s, r) => r.profitFactor, bm: (b, r) => r.profitFactor },
                  { label: 'Win Rate', kind: 'pct', scn: (s, r) => r.winRatePct, bm: (b, r) => r.winRatePct },
                  { label: 'Avg. Trade Return', kind: 'pct', scn: (s, r) => r.avgDailyReturnPct, bm: (b, r) => r.avgDailyReturnPct },
                ].map((m, rowIdx) => {
                  const stripe = rowIdx % 2 === 1;
                  return (
                    <React.Fragment key={m.label}>
                      <div className={`vc-scenario-compare-label${stripe ? ' vc-scenario-compare-label--stripe' : ''}`}>
                        {m.label}
                        <MetricInfo
                          label={m.label}
                          text={COMPARISON_METRIC_INFO[m.label]}
                        />
                      </div>
                      {orderedSelectedIds.map((id) => {
                        const value = m.scn(scenarioSummaryById[id] || {}, scenarioRiskStatsById[id] || {});
                        return (
                          <div
                            key={`${m.label}-${id}`}
                            className={`vc-scenario-compare-cell${stripe ? ' vc-scenario-compare-cell--stripe' : ''}`}
                            style={{ color: comparisonTone(m.kind, value) || undefined }}
                          >
                            {formatComparisonValue(m.kind, value)}
                          </div>
                        );
                      })}
                      {benchmarkColIds.map((id) => {
                        const value = m.bm(benchmarkSummaryById[id] || {}, benchmarkRiskStatsById[id] || {});
                        return (
                          <div
                            key={`${m.label}-bm-${id}`}
                            className={`vc-scenario-compare-cell${stripe ? ' vc-scenario-compare-cell--stripe' : ''}`}
                            style={{ color: comparisonTone(m.kind, value) || undefined }}
                          >
                            {formatComparisonValue(m.kind, value)}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {fullComparisonYearlyRows.length > 0 && (() => {
                  const summaryStripe = 8 % 2 === 1;
                  const latestYearRow = fullComparisonYearlyRows[fullComparisonYearlyRows.length - 1];
                  return (
                    <>
                      <div className={`vc-scenario-compare-label vc-scenario-compare-label--expandable${summaryStripe ? ' vc-scenario-compare-label--stripe' : ''}`}>
                        <span className="vc-scenario-compare-label-main">
                          Year by Year
                          <MetricInfo
                            label="Year by Year"
                            text={COMPARISON_METRIC_INFO['Year by Year']}
                          />
                        </span>
                        <button
                          type="button"
                          className="vector-capital-monthly-expand-btn vc-scenario-compare-expand-btn"
                          onClick={() => setFullComparisonYearlyExpanded((v) => !v)}
                          aria-expanded={fullComparisonYearlyExpanded}
                          aria-controls="vector-capital-full-comparison-yearly-breakdown"
                          title={fullComparisonYearlyExpanded ? 'Hide calendar-year breakdown' : 'Show calendar-year breakdown'}
                        >
                          <IconChevronDown
                            size={16}
                            color="rgba(191, 219, 254, 0.9)"
                            className={fullComparisonYearlyExpanded ? 'vector-capital-monthly-chevron is-open' : 'vector-capital-monthly-chevron'}
                          />
                        </button>
                      </div>
                      {orderedSelectedIds.map((id) => {
                        const value = latestYearRow?.scenarios[id];
                        return (
                          <div
                            key={`yby-summary-${id}`}
                            className={`vc-scenario-compare-cell${summaryStripe ? ' vc-scenario-compare-cell--stripe' : ''}`}
                            style={{ color: comparisonTone('pct', value) || undefined }}
                          >
                            {formatComparisonValue('pct', value)}
                          </div>
                        );
                      })}
                      {benchmarkColIds.map((id) => {
                        const value = latestYearRow?.benchmarks[id];
                        return (
                          <div
                            key={`yby-summary-bm-${id}`}
                            className={`vc-scenario-compare-cell${summaryStripe ? ' vc-scenario-compare-cell--stripe' : ''}`}
                            style={{ color: comparisonTone('pct', value) || undefined }}
                          >
                            {formatComparisonValue('pct', value)}
                          </div>
                        );
                      })}
                      {fullComparisonYearlyExpanded && fullComparisonYearlyRows.map((row, rowIdx) => {
                        const stripe = rowIdx % 2 === 1;
                        return (
                          <React.Fragment key={row.yearKey}>
                            <div className={`vc-scenario-compare-label vc-scenario-compare-label--nested${stripe ? ' vc-scenario-compare-label--stripe' : ''}`}>
                              {row.label}
                            </div>
                            {orderedSelectedIds.map((id) => {
                              const value = row.scenarios[id];
                              return (
                                <div
                                  key={`${row.yearKey}-${id}`}
                                  className={`vc-scenario-compare-cell vc-scenario-compare-cell--nested${stripe ? ' vc-scenario-compare-cell--stripe' : ''}`}
                                  style={{ color: comparisonTone('pct', value) || undefined }}
                                >
                                  {formatComparisonValue('pct', value)}
                                </div>
                              );
                            })}
                            {benchmarkColIds.map((id) => {
                              const value = row.benchmarks[id];
                              return (
                                <div
                                  key={`${row.yearKey}-bm-${id}`}
                                  className={`vc-scenario-compare-cell vc-scenario-compare-cell--nested${stripe ? ' vc-scenario-compare-cell--stripe' : ''}`}
                                  style={{ color: comparisonTone('pct', value) || undefined }}
                                >
                                  {formatComparisonValue('pct', value)}
                                </div>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          )}
          {/* "Strategy vs Benchmark" tab — full single-scenario vs benchmark
              comparison with differences + monthly/yearly breakdowns. */}
          {comparisonTab === 'benchmark' && (
          <div className="vector-capital-panel vector-capital-benchmark-compare-panel">
            <div className="vector-capital-panel-header vector-capital-benchmark-compare-header">
              <div className="vector-capital-benchmark-compare-title-block">
                <span className="vector-capital-panel-eyebrow">Benchmark Comparison</span>
                <div className="vector-capital-benchmark-compare-title-row">
                  <h3>{getScenarioLabel(compareScenarioId)} vs {primaryBenchmarkFullName}</h3>
                  {orderedSelectedIds.length > 1 && (
                    <select
                      className="vector-capital-compare-scenario-select"
                      value={compareScenarioId}
                      onChange={(e) => setBenchmarkCompareScenarioId(e.target.value)}
                      aria-label="Strategy to compare against the benchmark"
                    >
                      {orderedSelectedIds.map((id) => (
                        <option key={id} value={id}>{getScenarioLabel(id)}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="vector-capital-benchmark-compare-pick" role="tablist" aria-label="Benchmark to compare">
                  {marketOptions.map((option) => {
                    const id = option.id;
                    const active = primaryBenchmarkId === id;
                    const onChart = selectedBenchmarkIds.has(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        title={onChart ? undefined : `Add ${benchmarkTicker(id, marketOptions)} to chart and compare`}
                        className={`vector-capital-benchmark-tile ${active ? 'active' : ''} ${onChart && !active ? 'is-on-chart' : ''}`}
                        onClick={() => selectBenchmarkForComparison(id)}
                      >
                        {benchmarkTicker(id, marketOptions)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="vector-capital-comparison-controls">
                {comparisonTabs}
              </div>
            </div>

            <div className="vector-capital-metrics-table">
              <div className="vector-capital-metric-row header">
                <div>Metric</div>
                <div>{getScenarioLabel(compareScenarioId)}</div>
                <div>{benchmarkFullName(primaryBenchmarkId, marketOptions)}</div>
                <div>Difference</div>
              </div>
              <ComparisonMetricRow
                label="Total return"
                metricInfo={METRIC_TOOLTIPS.totalReturn}
                displayMode="percent"
                rowKind="return"
                portfolioPct={compareSummary.totalReturnPct}
                benchmarkPct={benchmarkSummary?.totalReturnPct}
              />
              <div className="vector-capital-monthly-return-block">
                <ComparisonMetricRow
                  label="Monthly return"
                  metricInfoModal={{
                    title: MONTHLY_RETURNS_INFO_TITLE,
                    paragraphs: buildMonthlyReturnsInfoParagraphs(capitalBase),
                  }}
                  displayMode="percent"
                  rowKind="return"
                  portfolioPct={comparePeriodReturns.monthPct}
                  benchmarkPct={benchmarkPeriodReturns.monthPct}
                  diffSuffix={(
                    <button
                      type="button"
                      className="vector-capital-monthly-expand-btn vector-capital-monthly-expand-btn--diff"
                      onClick={() => setMonthlyBreakdownExpanded((v) => !v)}
                      aria-expanded={monthlyBreakdownExpanded}
                      aria-controls="vector-capital-monthly-breakdown"
                      title={monthlyBreakdownExpanded ? 'Hide monthly breakdown' : 'Show monthly breakdown'}
                    >
                      <IconChevronDown
                        size={16}
                        color="rgba(191, 219, 254, 0.9)"
                        className={monthlyBreakdownExpanded ? 'vector-capital-monthly-chevron is-open' : 'vector-capital-monthly-chevron'}
                      />
                    </button>
                  )}
                />
                {monthlyBreakdownExpanded && (
                  <div
                    id="vector-capital-monthly-breakdown"
                    className="vector-capital-monthly-breakdown-panel"
                  >
                    <PeriodReturnBreakdownTable
                      rows={monthlyReturnBreakdownRows}
                      displayMode="percent"
                      benchmarkShortLabel={benchmarkFullName(primaryBenchmarkId, marketOptions)}
                      firstColumnHeader="Month"
                      rowKey="monthKey"
                      emptyMessage="No monthly periods in the selected range."
                    />
                  </div>
                )}
              </div>
              <div className="vector-capital-yearly-return-block">
                <ComparisonMetricRow
                  label="Yearly return"
                  metricInfo={METRIC_TOOLTIPS.yearlyReturn}
                  displayMode="percent"
                  rowKind="return"
                  portfolioPct={comparePeriodReturns.yearPct}
                  benchmarkPct={benchmarkPeriodReturns.yearPct}
                  diffSuffix={(
                    <button
                      type="button"
                      className="vector-capital-monthly-expand-btn vector-capital-monthly-expand-btn--diff"
                      onClick={() => setYearlyBreakdownExpanded((v) => !v)}
                      aria-expanded={yearlyBreakdownExpanded}
                      aria-controls="vector-capital-yearly-breakdown"
                      title={yearlyBreakdownExpanded ? 'Hide calendar-year breakdown' : 'Show calendar-year breakdown'}
                    >
                      <IconChevronDown
                        size={16}
                        color="rgba(191, 219, 254, 0.9)"
                        className={yearlyBreakdownExpanded ? 'vector-capital-monthly-chevron is-open' : 'vector-capital-monthly-chevron'}
                      />
                    </button>
                  )}
                />
                {yearlyBreakdownExpanded && (
                  <div
                    id="vector-capital-yearly-breakdown"
                    className="vector-capital-monthly-breakdown-panel"
                  >
                    <PeriodReturnBreakdownTable
                      rows={yearlyReturnBreakdownRows}
                      displayMode="percent"
                      benchmarkShortLabel={benchmarkFullName(primaryBenchmarkId, marketOptions)}
                      firstColumnHeader="Year"
                      rowKey="yearKey"
                      emptyMessage="No calendar years in the selected range."
                    />
                  </div>
                )}
              </div>
              <ComparisonMetricRow
                label="Annualized return"
                metricInfo={METRIC_TOOLTIPS.annualizedReturn}
                displayMode="percent"
                rowKind="return"
                portfolioPct={compareSummary.annualizedReturnPct}
                benchmarkPct={benchmarkSummary?.annualizedReturnPct}
              />
              <ComparisonMetricRow
                label="Max drawdown"
                metricInfo={METRIC_TOOLTIPS.maxDrawdown}
                labelTone="risk"
                displayMode="percent"
                rowKind="drawdown"
                portfolioPct={compareSummary.maxDrawdownPct}
                benchmarkPct={benchmarkSummary?.maxDrawdownPct}
              />
              <ComparisonMetricRow
                label="Standard deviation (annualized)"
                metricInfo={METRIC_TOOLTIPS.stdDev}
                labelTone="risk"
                displayMode="percent"
                rowKind="winrate"
                invert
                portfolioPct={scenarioRiskStatsById[compareScenarioId]?.annualizedStdDevPct}
                benchmarkPct={benchmarkRiskStatsById[primaryBenchmarkId]?.annualizedStdDevPct}
              />
              <ComparisonMetricRow
                label="Win rate"
                metricInfo={METRIC_TOOLTIPS.winRate}
                labelTone="positive"
                displayMode="percent"
                rowKind="winrate"
                portfolioPct={scenarioRiskStatsById[compareScenarioId]?.winRatePct}
                benchmarkPct={benchmarkRiskStatsById[primaryBenchmarkId]?.winRatePct}
              />
              <ComparisonMetricRow
                label="Profit factor"
                metricInfo={METRIC_TOOLTIPS.profitFactor}
                displayMode="percent"
                rowKind="factor"
                portfolioPct={scenarioRiskStatsById[compareScenarioId]?.profitFactor}
                benchmarkPct={benchmarkRiskStatsById[primaryBenchmarkId]?.profitFactor}
              />
            </div>
          </div>
          )}

        </div>
      </section>
  );

  const renderCalendar = () => (
    calendarSuiteScenarios.length > 0 ? (
      <div className="vector-capital-panel vector-capital-calendar-panel">
        <VectorCapitalCalendar scenarios={calendarSuiteScenarios} />
        <p className="vector-capital-chart-footnote">
          Hypothetical daily P&amp;L from the selected scenario&apos;s trade journal or simulated equity curve
          on your capital base — not actual trading results. Constant-size P&amp;L matching {getResultsNavLabel()}.
        </p>
      </div>
    ) : null
  );

  const renderMonthlyReturns = () => (
    chartInsightScenarios.length > 0 ? (
      <VectorCapitalMonthlyReturns
        scenarios={chartInsightScenarios}
        benchmarks={chartInsightBenchmarks}
        capitalBase={capitalBase}
        resolveMonthlyFixedBase={resolveMonthlyFixedBase}
        monthlyReturnMethods={monthlyReturnMethods}
        registryRowForScenarioId={registryRowForScenarioId}
      />
    ) : null
  );

  const renderTradesList = () => (
    calendarSuiteScenarios.length > 0 ? (
      <VectorCapitalTradesList scenarios={calendarSuiteScenarios} />
    ) : null
  );

  const renderKeyInsights = () => (
    calendarSuiteScenarios.length > 0 ? (
      <VectorCapitalKeyInsights
        scenarios={chartInsightScenarios}
        benchmarks={chartInsightBenchmarks}
        capitalBase={capitalBase}
        resolveMonthlyFixedBase={resolveMonthlyFixedBase}
      />
    ) : null
  );

  const renderGrowthCalculator = () => (
    calendarSuiteScenarios.length > 0 ? (
      <VectorCapitalGrowthCalculator scenarios={chartInsightScenarios} defaultCapital={capitalBase} />
    ) : null
  );

  const renderDisclosures = () => (
    <div className="vector-capital-panel vector-capital-disclosure-panel">
      <div className="vector-capital-panel-header">
        <div>
          <h3>Important Disclosures</h3>
        </div>
      </div>
      {importantDisclosuresContent}
    </div>
  );

  const renderBasketModal = () => (
    basketModalOpen ? (
      <div
        className="vcp-basket-modal-overlay"
        role="presentation"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) setBasketModalOpen(false);
        }}
      >
        <div className="vcp-basket-modal" role="dialog" aria-modal="true" aria-label="Edit custom composition">
          <button
            type="button"
            className="vcp-basket-modal-close"
            onClick={() => setBasketModalOpen(false)}
            aria-label="Close composition editor"
          >
            <IconX size={18} color="currentColor" />
          </button>
          <div className="vcp-basket-modal-body">
            {renderStrategyBasket('top')}
          </div>
          <div className="vcp-basket-modal-foot">
            <button
              type="button"
              className="vcp-basket-modal-done"
              onClick={() => setBasketModalOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    ) : null
  );

  return {
    loading,
    error,
    fetchAnalytics,
    renderTopbar,
    renderControls,
    renderChart,
    renderComparison,
    renderCalendar,
    renderMonthlyReturns,
    renderTradesList,
    renderKeyInsights,
    renderGrowthCalculator,
    renderDisclosures,
    renderBasketModal,
    basketModalOpen,
  };
}
